import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import { beginLoad } from './loading';
import { kindSearch } from './driverKind';
import { getOwnerPick } from './ownerPick';

/**
 * 머리줄 「담당자」(`lib/ownerPick.ts`)를 실어 보낼 호출 — 목록 조회 전부 + **보이는 목록을 통째로 처리하는 일괄 쓰기**.
 * 🔴 일괄 쓰기(월정기 전체등록·취소, 통근 달 줄 만들기·복사)가 고르기를 안 보면 화면엔 한 사람 것만 보이는데
 *    범위 전체가 등록된다(10-30 전체등록 2,244줄 사고와 같은 자리). 상세 열기·한 건 쓰기는 고르기와 무관하다(서버도 안 본다).
 */
const PICK_WRITES = new Set(['applyResultMo', 'cancelResultMo', 'createCommuteMonth', 'copyCommuteMonth']);
function withOwnerPick<T>(name: string, data: T): T {
  const pick = getOwnerPick();
  if (pick === null || !(/^list/.test(name) || PICK_WRITES.has(name))) return data;
  if (data !== undefined && (data === null || typeof data !== 'object' || Array.isArray(data))) return data;
  return { ...(data ?? {}), ownerPick: pick } as T;
}

/**
 * Cloud Functions callable 래퍼.
 * businweb 은 Firestore 를 쓰지 않는다 — 브라우저가 DB 를 직접 읽는 경로는 **없다.**
 * 데이터는 전부 이 함수들을 통과한다(권한도 거기서 강제된다).
 */
function call<Req, Res>(name: string) {
  // 🔴 SDK 기본 대기는 70초 — 서버 한도(최대 300초, 예: commitMisuSync)보다 짧으면 서버는 끝까지 반영하는데
  //    화면만 `deadline-exceeded` 를 띄운다(2026-09-29 밀린 반영 28건: 서버 72초 성공 · 화면 70초 실패).
  const fn = httpsCallable<Req, Res>(functions, name, { timeout: 310_000 });
  const read = isRead(name);
  return async (data?: Req): Promise<Res> => {
    // ⚠️ `silentDepth` 는 **이 줄에서** 읽어야 한다(첫 await 전 = 호출한 그 순간). await 뒤에 읽으면
    //    워밍의 `silently()` 가 이미 끝나 0 이 되어 있다.
    const done = read && silentDepth === 0 ? beginLoad() : null;
    try {
      const args = withOwnerPick(name, (read ? kindSearchArgs(data) : data) as Req);
      let res: Res;
      try {
        res = (await fn(args)).data;
      } catch (e) {
        // 조회만 한 번 다시 — 서버 인스턴스가 막 뜨다 실패한 경우가 대부분이다(10-23 장애).
        if (!read || !isServerGlitch(e)) throw friendly(e);
        await new Promise((r) => setTimeout(r, 1500));
        try {
          res = (await fn(args)).data;
        } catch (e2) {
          throw friendly(e2);
        }
      }
      // 쓰기가 성공하면 조회 캐시를 버린다 — 새 달의 배차를 넣었는데 월 목록이 5분간 옛것이면 안 된다.
      if (!read && !isQuiet(name)) dropReadCaches();
      return res;
    } finally {
      done?.();
    }
  };
}

/**
 * 서버가 사유 없이 실패한 경우(인스턴스 기동 실패·일시 장애) — 화면엔 「internal」 대신 안내 문구.
 * 서버가 문구를 담아 던진 오류(권한·검증)는 그대로 둔다.
 */
const GLITCH_CODES = new Set(['functions/internal', 'functions/unavailable', 'functions/deadline-exceeded']);
function isServerGlitch(e: unknown): boolean {
  const code = (e as { code?: string })?.code ?? '';
  const msg = String((e as { message?: string })?.message ?? '').trim().toLowerCase();
  return GLITCH_CODES.has(code) && (msg === '' || msg === 'internal' || msg === 'unavailable' || msg === 'deadline-exceeded');
}
function friendly(e: unknown): unknown {
  if (!isServerGlitch(e)) return e;
  return new Error('서버 응답이 잠시 불안정합니다. 잠시 뒤 다시 시도해 주세요. 계속되면 관리자에게 알려 주세요.');
}

/** 조회 인자의 글자(한 단계 안 객체 값까지)에서 「관리기사A/B」 → 원문 — 검색·칼럼 거르기 공용. */
function kindSearchArgs<T>(data: T): T {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const fix = (v: unknown): unknown =>
    typeof v === 'string' && /관리기사/.test(v) ? kindSearch(v) : v;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    out[k] = v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([kk, vv]) => [kk, fix(vv)]))
      : fix(v);
  }
  return out as T;
}

/**
 * 워밍 전용 호출 — `{ __warm: true }` 만 보낸다. 서버 `requireUser` 가 로그인만 확인하고
 * **DB 커넥션을 열기 전에** 거절한다(컨테이너 부팅·커넥터 준비만 남는다, `shared.ts`).
 * 로딩 카드·캐시를 거치지 않는다 — 사람이 기다리는 요청이 아니다.
 */
export function warmPing(name: string): Promise<unknown> {
  return httpsCallable(functions, name)({ __warm: true });
}

/**
 * 조회로 볼 이름. 로딩 카드는 **조회에만** 뜬다 — 저장은 폼의 버튼이 이미 `저장 중…` 을 보여 주고,
 * 저장에까지 화면 가운데 카드를 띄우면 방금 누른 버튼이 가려진다.
 */
function isRead(name: string): boolean {
  return /^(list|get|preview)/.test(name) || name === 'dbHealth' || name === 'opsTransitXref';
}

/**
 * 이름이 list/get/preview 가 아니어도 **아무것도 안 바꾸는** 호출 — 성공해도 조회 캐시를 버리지 않는다.
 * 🔴 2026-09-29 속도 개선: 메뉴 배지(`countImprovements`)가 메뉴를 옮길 때마다 불리는데 «쓰기»로 잘못 분류돼
 *    그때마다 정산년월 캐시를 버렸다 → 정산 계열 화면마다 그 목록(prod 0.3~0.9초)을 새로 받았다.
 */
function isQuiet(name: string): boolean {
  return /^(count|lookup|search|export)/.test(name) || /(Check|Meta|Pulse)$/.test(name);
}

/** 워밍처럼 **사람이 기다리지 않는** 호출은 로딩 카드에 세지 않는다(`lib/loading.ts`). */
let silentDepth = 0;
export function silently(run: () => void): void {
  silentDepth += 1;
  try {
    run();
  } finally {
    silentDepth -= 1;
  }
}

/**
 * 정산년월 목록 캐시 (2026-09-17 실측으로 넣었다).
 *
 * 정산 계열 **네 화면이 들어올 때마다** 같은 목록을 다시 부르고 있었다(웜 상태에서도 **0.4~1.4초** —
 * 그 화면의 본 목록보다 느렸다). 값은 "배차완료가 있는 달" 이라 한 달에 한 번 바뀐다.
 * → 5분 캐시 + **쓰기가 성공하면 버린다**(`dropReadCaches`). 실패한 요청은 캐시에 남기지 않는다.
 */
const DRIVE_MON_TTL_MS = 5 * 60_000;
/** `ahead` = 재무추가에만 있는, 최신 달 뒤의 정산월(#511 · 재무추가 화면만 쓴다). 옛 서버면 없다. */
type DriveMonths = { rows: string[]; ahead?: string[] };
let driveMonCache: { at: number; p: Promise<DriveMonths> } | null = null;

export function dropReadCaches() {
  driveMonCache = null;
}

const rawListDriveMonths = call<void, DriveMonths>('listDriveMonths');
function listDriveMonthsCached(): Promise<DriveMonths> {
  if (driveMonCache && Date.now() - driveMonCache.at < DRIVE_MON_TTL_MS) return driveMonCache.p;
  const p = rawListDriveMonths();
  const entry = { at: Date.now(), p };
  driveMonCache = entry;
  p.catch(() => {
    if (driveMonCache === entry) driveMonCache = null;
  });
  return p;
}

/** 운행차량편성 아래 내역 탭 종류(#300 · 서버 allocActivity.ts 와 같은 값). */
export type AllocActivityKind = 'dispatch' | 'daily' | 'mo' | 'req' | 'drive' | 'inst';

export interface ListArgs {
  q?: string;
  limit?: number;
  offset?: number;
  /** 'active' 의 의미는 탭마다 다르다 — 차량=운용중 · 기사=재직중 · 편성=정산 진행 중. */
  scope?: 'active' | 'all';
  /** 편성 전용 — 운행중만(종료일이 비었거나 오늘 이후). 정산(`scope`)과 별개 축이다. */
  running?: boolean;
  belong?: string;
  /** 편성 전용 — 그 날(YYYY-MM-DD) 운행하는 편성만(배정 후보 창). */
  onDate?: string;
  /** 편성 전용 — 적용일이 된 편성만(배차·정산 편성 고르기 · 10-21). */
  started?: boolean;
  /** 편성 전용 — `started` 와 함께: 이 날짜(YYYY-MM-DD)까지 시작하는 편성도 보이기(#465 · 정산월 있는 재무추가 = 그 달 말일). */
  startedBy?: string;
  /**
   * 정렬·칼럼별 검색. **서버가 한다** — 목록은 한 페이지(최대 200행)뿐이라 화면에서 정렬하면
   * "전체를 이름순으로"가 아니라 "이 페이지만"이 되고, 화면은 그 차이를 안 보여준다.
   */
  sort?: string;
  dir?: 'asc' | 'desc';
  /** 칼럼 → 검색어(부분일치). 서버 화이트리스트 밖 키는 무시된다. */
  filters?: Record<string, string>;
}

/**
 * 서버가 낸 합계 한 줄. `count` 는 **검색·필터에 걸린 전체 행 수**이고 나머지 키는
 * 그 컬럼의 합이다(금액은 문자열 — numeric 을 숫자로 왕복시키면 소수 4자리가 깎인다).
 *
 * ⚠️ 화면에 온 행을 더해 만들지 말 것. 목록은 한 페이지(최대 200행)뿐이라 그 합은
 *    전체가 아니고, 화면에서는 그 차이가 안 보인다.
 */
/** 월정기 좌우 화면의 한 줄 — 왼쪽이면 정기항목(그 달에 넘어갔으면 result_mo_id), 오른쪽이면 그 달 월정산. */
export interface MoLine {
  result_mo_def_id: number | null; result_mo_id: number | null;
  account_name: string | null; amt: string | null; def_amt: string | null; is_plus: boolean | null;
  comment: string | null; owner_id: number | null; driver_id: number | null; car_id: number | null;
  grp_id: number | null; car_alloc_id: number | null;
  owner_name: string | null; driver_name: string | null; car_no: string | null; grp_name: string | null;
  created_at: string | null;
  /** 버스인입력담당자 — 서버가 이름으로 풀어 준 값(11-03). */
  created_name?: string | null;
}
export interface MoGroup {
  owner_id: number | null; owner_name: string | null; n: number; plus: string; minus: string; rows: MoLine[];
}

export interface Totals extends Record<string, unknown> {
  count: number;
}

export interface ListResult<Row> {
  rows: Row[];
  limit: number;
  offset: number;
  /** 총계 — 목록과 **같은 WHERE** 로 서버가 낸다. 목록을 안 내려주는 응답에는 없다. */
  total?: Totals | null;
  /** 소계 — `groupBy` 를 켰을 때만. `key` 가 그룹을 가른다. */
  subtotals?: (Totals & { key: string })[];
}

export interface DbHealth {
  caller: string;
  instance: string;
  expected: { database: string; user: string };
  actual: { db: string; usr: string; ver: string; now_kst: string } | null;
  counts: { table_name: string; n: string }[];
}

export interface Grp {
  grp_id: number;
  grp_name: string | null;
  grp_kind: string | null;
  biz_status: string | null;
  manager: string | null;
  phone: string | null;
  addr: string | null;
  corp_name: string | null;
  biz_number: string | null;
  master?: string | null;
  /** 숨김(사용 안 함 · #389) — 목록 「사용중만」·신규 접수 배차 소속에서 빠진다 */
  is_hidden?: boolean;
}

export interface Garage {
  garage_id: number;
  garage_name: string | null;
  addr: string | null;
  lat: string | null;
  lng: string | null;
}

export interface Cust {
  cust_id: number;
  cust_name: string | null;
  cust_code: string | null;
  cust_kind: string | null;
  biz_number: string | null;
  phone: string | null;
  fax: string | null;
  manager: string | null;
  manager_phone: string | null;
  manager_email: string | null;
  address: string | null;
  /** 등록된 담당자 이름들(일반 먼저 · 쉼표) — 거래처 고르기에서 같은 이름 거래처를 가린다(#256). */
  contact_names?: string | null;
}

export interface Car {
  car_id: number;
  car_no: string | null;
  car_name: string | null;
  car_type_gubun: string | null;
  full_cnt: number | null;
  car_year: number | null;
  grp_name: string | null;
  owner_name: string | null;
  garage_name: string | null;
  ins_expire: string | null;
  test_expire: string | null;
  is_use: boolean | null;
  un_use_kind: string | null;
  grp_id: number | null;
  owner_id: number | null;
  garage_id: number | null;
  /** 자동차등록증 항목. ⚠️ 폼이 목록 행에서 초기값을 읽으므로 **목록에도 실려 있어야** 한다. */
  ident_no: string | null;
  car_type: string | null;
  car_code: string | null;
  /** ⚠️ 날짜지만 **문자열**이다 — 옛 자료에 `3/29/2019` 형식이 섞여 있다(비ISO 3,267·1,080건). */
  car_date: string | null;
  car_expire: string | null;
  car_number_area: string | null;
  paint: string | null;
  car_cost: string | null;
  /** 번호판 권리 — 차주 목록 ID(0 은 서버가 null 로 준다)와 그 차주의 지금 이름. */
  number_owner_id: number | null;
  number_owner_name: string | null;
  number_price: string | null;
  ins_valid: string | null;
  un_use_date: string | null;
  un_use_comment: string | null;
  history: string | null;
  /** 관제 계열 — **읽기 전용**이다. buslink 가 쓰는 값이라 여기서 고치면 두 시스템이 갈린다. */
  gps: string | null;
  device_no: string | null;
  beacon_major: string | null;
  gps_control: number | null;
  /** #126·#130 — 차주 휴대폰 · 등록/수정 기록(등록자·수정자 = 레거시 로그인 ID 또는 웹 메일). */
  owner_mobile: string | null;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface Owner {
  owner_id: number;
  owner_name: string | null;
  belong: string | null;
  mobile_phone: string | null;
  phone: string | null;
  biz_number: string | null;
  bank_name: string | null;
  email: string | null;
}

export interface Driver {
  driver_id: number;
  driver_name: string | null;
  driver_kind: string | null;
  phone: string | null;
  lic_kind: string | null;
  date_drive_lic: string | null;
  date_bus_lic: string | null;
  date_bosu_edu: string | null;
  join_date: string | null;
  resign_date: string | null;
  is_resign: boolean | null;
  /** 가린 주민번호(900101-1******) — 전체는 revealDriverRrn 으로만. */
  res_no_hint?: string | null;
  has_res_no?: boolean;
}

export interface CarAlloc {
  car_alloc_id: number;
  car_id: number | null;
  driver_id: number | null;
  owner_id: number | null;
  grp_id: number | null;
  car_no: string | null;
  car_name: string | null;
  owner_name: string | null;
  grp_name: string | null;
  driver_name: string | null;
  driver_kind: string | null;
  /** 기사 연락처(#237·#242 — 서버가 이미 내려 주던 칸). */
  driver_phone?: string | null;
  startdt: string | null;
  enddt: string | null;
  is_use: boolean | null;
  fuel_corp: string | null;
  fuel_num: string | null;
  hipass_corp: string | null;
  hipass_num: string | null;
  driver_missing: boolean | null;
  driver_deleted: boolean | null;
  car_deleted: boolean | null;
}

/** 편성 변경 기록의 그때 편성(이름까지 굳힌 것). 서버 `allocChangeLog.AllocSnap`. */
export interface AllocSnap {
  car_alloc_id: number;
  car_id: number | null; car_no: string | null;
  owner_id: number | null; owner_name: string | null;
  grp_id: number | null; grp_name: string | null;
  driver_id: number | null; driver_name: string | null;
  startdt: string | null; enddt: string | null; is_use: boolean | null;
}
export type AllocChangeKind = '신규' | '변경' | '종료' | '수정' | '삭제';
export interface AllocFile { id: number; file_name: string; content_type: string; byte_size: string; created_by: string | null; created_at: string }

/** 마감 후 수정 기안 한 건(팀 승인 · 10-28) — 서버 `postCloseReview.ts shape`. */
export interface PostCloseItem {
  draftId: string;
  draftNo: string | null;
  title: string | null;
  drafterName: string | null;
  drafterEmail: string | null;
  mon: string | null;
  type: string | null;
  reason: string | null;
  reasonCode: string | null;
  prevent: string | null;
  teams: string[];
  /** review 승인 대기 · applying 반영 중 · applied · held 멈춤 · failed · rejected 팀 반려 · cancelled DYOPS 에서 거둠 */
  status: 'review' | 'applying' | 'applied' | 'held' | 'failed' | 'rejected' | 'cancelled';
  message: string | null;
  createdAt: string;
  updatedAt: string;
  lines: string[];
  reviews: { team: string; status: 'pending' | 'approved' | 'rejected'; byName: string | null; at: string | null; reason: string | null }[];
  /** 내가 지금 승인할 수 있는 팀 */
  canDecide: string[];
  result: { status: string; message: string; todo?: string[]; salary?: { target: string; ok: boolean; note?: string }[] } | null;
}

export interface AllocChangeRow {
  alloc_change_id: number;
  /** 등록번호 — 등록 연도 + 그 해 일련번호(예: 2026-0012) */
  change_no: string;
  kind: AllocChangeKind;
  reason: string;
  car_id?: number | null;
  old_alloc_id?: number | null;
  new_alloc_id?: number | null;
  apply_date: string;
  settle_mon: string;
  old_snap: AllocSnap | null;
  new_snap: AllocSnap | null;
  carry?: { moDefs?: number; moRows?: number; inst?: number; daily?: number; from?: string; denied?: string[]; cardsMoved?: boolean } | null;
  comment: string | null;
  /** 첨부(#189) — 목록·상세·팝업·이력에 같이 온다. */
  files?: AllocFile[];
  source: 'web' | 'form' | 'legacy' | 'gform';
  /** 그 달 정산 줄을 묶은 편성(옛/새 편성 번호) · null = 실제 날짜대로 나눔 */
  settle_alloc_id?: number | null;
  created_by?: string;
  created_at: string;
  created_name: string | null;
  ack_cnt?: number;
  /** 내가 확인했거나 최종 확인이 끝남(10-21 — 최종 확인이면 미확인으로 안 남김) */
  acked?: boolean;
  /** 내가 실제로 「확인」을 누름 */
  my_acked?: boolean;
  /** 최종 확인(관리자만 · 10-15 way) */
  final_by?: string | null;
  final_at?: string | null;
  final_name?: string | null;
  /** 관리자 최종 확인이 필요한 예외(종료·삭제·소급·재배차·통근 줄 확인 — 서버 REVIEW_SQL · 11-04). false = 자동 처리 */
  need_review?: boolean;
  /** 외부 편성인데 기사·차량 이름이 양식(개인_/관광사_ + 차주 이름)이 아님 — 지금 이름 기준(10-08) */
  ext_form_bad?: boolean;
  is_ext?: boolean;
}
export interface AllocSchRow {
  dispt_car_sch_id: number; recv_id: number | null; dispt_id: number | null; dispt_name: string | null;
  cust_name: string | null; start_time: string | null; end_time: string | null;
}
/** 편성 변경 등록이 정리한 배정(10-08) — skipped = 이어받기를 건너뛴 걸친 배정과 사유. */
export interface AllocSchResult {
  trimmed: number; moved: number; created: number; dailyMoved: number; dailyDeleted: number;
  skipped: { disptCarSchId: number; disptId: number | null; reason: string }[];
}
export interface AllocChangePreview {
  alloc: Record<string, unknown> | null;
  endDate: string;
  settleMon: string;
  monthClosed: boolean;
  closedMon: string;
  kind: '변경' | '종료';
  /** 종료일에 걸쳐 그날까지로 자를 배정 수(10-08). */
  trimSch: number;
  /** 변경 — 종료일 뒤에만 있어 새 편성으로 옮길 배정 수. */
  moveSch: number;
  /** 종료 — 종료일 뒤에만 있어 막는 배정(걸친 배정은 자르면 되므로 여기 없다). */
  blockedSch: AllocSchRow[];
  blockedSchCount: number;
  /** 영향 받는 배정 30건 — act: trim 자름 · move 옮김 · block 막힘. */
  schSample: (AllocSchRow & { act: 'trim' | 'move' | 'block' })[];
  /** 종료일 뒤 달 통근 월정산 줄 — 종료 = 전부 · 변경 = 마감 달 것만(막힘). */
  blockedSettled: number;
  /** 변경 — 새 편성으로 옮길 통근 월정산 줄(열린 달, 10-12). 옛 서버면 없다. */
  moveSettled?: number;
  /** 변경 — 그중 이어받을 배정이 없어 배정 없이 새 편성으로 옮길 줄(#469). 옛 서버면 없다. */
  forceSettled?: number;
  forceSettledSample?: { driveMon: string; disptId: number | null; disptName: string | null }[];
  carryFrom: string;
  left: { moDefs: number; moRows: number; inst: number; daily: number; months: string[] };
  canCarry: CarryKind[];
  cards: { fuel: string; hipass: string };
}
/** 외부 → 내부 이전 때 끝나는 같은 연락처의 외부 편성(10-07). new_enddt = 실제로 넣은 종료일(저장 결과에만). */
export interface ExternalDup {
  car_alloc_id: number; driver_id: number; driver_name: string | null; car_no: string | null; grp_name: string | null;
  startdt: string | null; enddt: string | null; is_use: boolean | null; new_enddt?: string;
}
/** 확인 창 문구 — 미리보기 결과로. */
export function externalDupConfirm(p: { endDate: string; rows: ExternalDup[] }): string {
  return [`같은 연락처로 등록된 외부 편성 ${p.rows.length}건을 ${p.endDate}로 종료하고 정산 대상에서 뺍니다.`, '',
    ...p.rows.map((r) => `· NO ${r.car_alloc_id} ${r.car_no ?? ''} · ${r.driver_name ?? ''} (${r.grp_name ?? '외부'})`), '',
    '계속할까요?'].join('\n');
}
export const externalDupDone = (rows: ExternalDup[]) =>
  `외부 편성 ${rows.length}건 종료(연락처 중복): ${rows.map((r) => `NO ${r.car_alloc_id} ${r.car_no ?? ''} → ${r.new_enddt ?? ''}`).join(', ')}`;

/** 외부 편성 한 번에 등록(10-08) — 미리보기·등록 공용 결과. reuse = 있는 것을 다시 씀. */
export interface ExternalPick { id: number | null; name: string; reuse: boolean; fixPrivate?: boolean }
export interface ExternalAllocPlan {
  preview: boolean; owner: ExternalPick; driver: ExternalPick; car: ExternalPick; grp: { id: number; name: string };
  existing: { carAllocId: number; enddt: string } | null; carAllocId?: number;
  /** 개인 — 다시 쓰는 기사에 주민번호가 이미 있음 / 입력이 필요함(10-29). */
  hasRrn?: boolean; needRrn?: boolean;
  /** 휴대전화를 이름이 다른 여러 분이 같이 씀(대표·임시 번호 · #437) — 연락처 중복으로 막지 않는다. */
  sharedPhone?: boolean;
}

export interface AllocChangeInput {
  kind: '변경' | '종료' | '신규';
  reason: string;
  comment?: string;
  oldAllocId?: number;
  date: string;
  next?: { carId: number | null; ownerId: number | null; grpId: number | null; driverId: number | null };
  moveCards?: boolean;
  carry?: CarryKind[];
  settlePick?: 'old' | 'new';
  /** 먼저 올려 둔 첨부(uploadAllocChangeFile) — 등록하면서 이 기록에 붙는다. */
  fileIds?: number[];
}

export interface DispatchListArgs extends ListArgs {
  status?: string;
  /** '일반' | '통근'. 비우면 전체 — 접수는 구분 없이 하나로 다룬다. */
  recvType?: string;
  disptId?: number;
  pendingOnly?: boolean;
  /** 노선 조회를 한 접수로 좁힌다. 접수 화면이 그 접수의 노선을 펼쳐 보는 데 쓴다. */
  recvId?: number;
  /** 대수가 안 찬 노선만 — 배차 담당의 작업 대기열. 노선 탭의 기본값이다. */
  unfilledOnly?: boolean;
  /** 출발일 기간(#197) — YYYY-MM-DD · 끝 날짜 포함. 주면 출발 이른 순. */
  dateFrom?: string;
  dateTo?: string;
  /** 머리글 정렬(#207 · 노선 목록만) — 서버 `DISPT_SORT` 키. */
  sort?: string;
  dir?: 'asc' | 'desc';
}

export interface Bid {
  dispt_car_sch_wait_id: number;
  dispt_id: number | null;
  recv_id: number | null;
  applied_at: string | null;
  /** 삭제가 아니라 "배차로 소비됨". 실데이터는 전 행 true. */
  is_consumed: boolean | null;
  recv_status: string | null;
  bidding_type: string | null;
  close_date: string | null;
  cust_name: string | null;
  dispt_name: string | null;
  start_time: string | null;
  start_loc: string | null;
  end_loc: string | null;
  car_cnt: number | null;
  car_no: string | null;
  driver_name: string | null;
  owner_name: string | null;
  grp_name: string | null;
}

export interface Recv {
  recv_id: number;
  recv_type: string | null;
  cust_name: string | null;
  recv_date: string | null;
  recv_startdt: string | null;
  recv_enddt: string | null;
  status: string | null;
  recv_user: string | null;
  recv_request: string | null;
  is_bidding: boolean | null;
  bidding_type: string | null;
  close_date: string | null;
  dispt_grp: string | null;
  car_area: string | null;
  fin_manager: string | null;
  fin_req_method: string | null;
  dispt_cnt: number | null;
  /** 접수담당자(웹 계정). null = 미지정이고, 그 접수는 **전원에게 보인다**. */
  owner_app_user_id: number | null;
  owner_user_name: string | null;
  owner_user_email: string | null;
  owner_user_team: string | null;
  /** 함께 담당 이름(「, 」 이음 · 이름순 · 10-19). 없으면 null. */
  co_owner_names: string | null;
}

export interface Dispt {
  dispt_id: number;
  recv_id: number | null;
  recv_type: string | null;
  cust_name: string | null;
  dispt_name: string | null;
  car_kind: string | null;
  /** 차량 연식 제한 = 차령 N년(#214). 0·NULL = 제한 없음. */
  car_year: number | null;
  trip_type: string | null;
  car_cnt: number | null;
  assigned_cnt: number | null;
  start_loc: string | null;
  start_time: string | null;
  end_loc: string | null;
  end_time: string | null;
  /** numeric → pg 가 문자열로 준다. 계산에 쓰지 말 것. 청구 = **노선 총액**, 운행료 = **1대당 단가**(issues 179). */
  cost_cust: string | null;
  cost_driver: string | null;
  /** 청구 합계 = 노선 총액 그대로 · 운행료 합계 = 단가 × 대수. 서버가 계산해 준다 — 화면이 곱하면 그 곱이 어디서 났는지 안 보인다. */
  cust_amt: string | null;
  driver_amt: string | null;
  drive_mon: string | null;
  dispt_status: string | null;
  recv_status: string | null;
  is_bidding: boolean | null;
  bidding_type: string | null;
  close_date: string | null;
}

export interface DisptCarSch {
  dispt_car_sch_id: number;
  dispt_id: number | null;
  car_alloc_id: number | null;
  car_no: string | null;
  car_name: string | null;
  driver_name: string | null;
  driver_kind: string | null;
  owner_name: string | null;
  grp_name: string | null;
  start_time: string | null;
  end_time: string | null;
  change_cost: string | null;
}

export type Role = 'admin' | 'dispatch' | 'settle' | 'accounting' | 'view';

/**
 * 접수·배차 목록에서 보는 범위.
 * ⚠️ 정산 화면에는 걸지 않는다 — 회계는 전량을 봐야 하고, 접수담당자로 자르면
 *    같은 달 총계가 사람마다 달라 보인다(2026-07-31 사용자 확인).
 */
export type ViewScope = 'own' | 'team' | 'all';

/** 담당 넘기기 대상(10-12) — 웹 계정(비활성 포함) 또는 웹 계정 없는 버스인 담당. */
export interface HandoverSource {
  kind: 'web' | 'legacy';
  app_user_id: number | null;
  users_id: number | null;
  name: string | null;
  team: string | null;
  is_active: boolean | null;
  cnt: number;
  last_recv: string | null;
}

export interface Me {
  email: string;
  name: string | null;
  role: Role;
  team: string | null;
  viewScope: ViewScope;
  /** 기사 주민번호 「전체 보기」 가능(#185 — 인차팀·회계·관리자). 옛 서버면 없음. */
  rrnReveal?: boolean;
  /** 왜 이만큼만 보이는지 한 줄. null 이면 전량을 보고 있다는 뜻이다. */
  scopeNote: string | null;
  /** 정산 범위 안내(스위치가 켜져 있고 전체가 아닐 때만). null 이면 정산 전량을 본다. */
  settleScopeNote?: string | null;
  /** 화면별 최종 권한(서버 계산). 없으면(옛 서버) 화면이 아무것도 허락하지 않는다. */
  screens?: Partial<Record<'master' | 'dispatch' | 'settle' | 'result-daily' | 'result-mo' | 'result-req' | 'fin-inst' | 'accounting' | 'notices' | 'profit', 'none' | 'read' | 'write'>>;
  /** 기준정보 수정 소속(09-28) — all 전체 · internal 내부만 · external 외부만. 강제는 서버. */
  masterGrpScope?: 'all' | 'internal' | 'external';
  /** 계정에 기억된 화면 조건(#37) — 키 → 값. 없으면(옛 서버·표 없음) 빈 객체. `lib/sticky.ts` 가 쓴다. */
  prefs?: Record<string, unknown>;
  /** 머리줄 「담당자」 고르기 목록(11-02) — null = 본인 범위뿐이라 끔. 옛 서버면 없음. */
  ownerPick?: { rows: { id: number; name: string; team: string | null; active: boolean }[] } | null;
}

export interface AppUser {
  app_user_id: number;
  email: string;
  name: string | null;
  role: Role;
  is_active: boolean;
  team: string | null;
  view_scope: ViewScope;
  /** 팀 범위에서 함께 볼 팀(10-28). 옛 서버면 없음. */
  view_teams?: string[] | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** 담당자 선택기용 — 이름·팀만. 역할·활성여부는 여기 없다(관리자 화면에만 있다). */
export interface Staff {
  app_user_id: number;
  name: string | null;
  email: string;
  team: string | null;
}

export interface Via {
  via_id: number;
  via_loc: string | null;
  start_hm: string | null;
  via_sort: number | null;
}

export interface DisptInput {
  disptId?: number;
  recvId?: number;
  disptName?: string;
  carKind?: string;
  carYear?: number;
  carCnt?: number;
  startLoc?: string;
  startTime?: string;
  endLoc?: string;
  endTime?: string;
  isShuttle?: boolean;
  endDeptTime?: string;
  startArrvTime?: string;
  /** 금액은 문자열로 주고받는다 — 숫자로 왕복시키면 소수 4자리가 깎인다. */
  costCust?: string;
  costDriver?: string;
  driveMon?: string;
  isMeal?: boolean;
  isLodge?: boolean;
  /** 청구액이 부가세포함가인가(#87). 켜면 서버가 공급가를 ⌈포함가 ÷ 1.1⌉ 로 둔다. */
  vatIncl?: boolean;
  /** 노선 참고사항(#275 · 입찰·노선 조회 때 기사·관리자가 본다). 안 보내면 서버가 그대로 둔다. */
  request?: string;
}

export interface RecvInput {
  recvId?: number;
  /** 청구법인(#138) = recv.fin_grp_id. 비우면 이 거래처 지난 접수 법인. */
  finGrpId?: number;
  recvType?: string;
  custId?: number;
  recvStartdt?: string;
  recvEnddt?: string;
  /** 거래처 담당 이름(#177 — 칸은 레거시 recv_user). */
  recvUser?: string;
  /** 거래처 담당 이메일(#177). */
  recvUserEmail?: string;
  /** 거래처 담당 전화(#388). 새 접수는 이름·이메일·전화 필수. */
  recvUserPhone?: string;
  recvRequest?: string;
  finManager?: string;
  finPhone?: string;
  finReqMethod?: string;
  disptGrp?: string;
  /** 배차 소속 고르기(#179) — 빈 배열 = 전체. 주면 서버가 disptGrp 를 소속 이름으로 다시 만든다. */
  disptGrpIds?: number[];
  carArea?: string;
  isBidding?: boolean;
  biddingType?: string;
  closeDate?: string;
  isMeal?: boolean;
  isLodge?: boolean;
  /** 접수담당자. 안 주면 **등록한 사람**이 담당이 된다(수정에서 안 주면 바뀌지 않는다). */
  ownerAppUserId?: number;
  /** 함께 담당(10-19) — 우리 쪽 계정 id 여럿. 안 주면 바뀌지 않는다 · 빈 배열 = 전부 뺀다. */
  coOwnerIds?: number[];
  /** 새 접수에서 거래처를 고르지 않고 친 이름·사업자등록번호(#498) — custId 가 없을 때만. 같은 번호 거래처가 있으면 그 거래처로. */
  newCust?: { custName?: string; bizNumber?: string };
}

/** 접수 + 노선 여러 줄을 한 번에. 서버가 한 트랜잭션으로 넣는다 — 전부 서거나 전부 없다. */
export interface RecvIntakeInput extends RecvInput {
  routes: DisptInput[];
}

export interface SettleListArgs extends ListArgs {
  recvId?: number;
  driveMon?: string;
  /** 거래처별 정산 접수구분(#117) — 비우면 전체. */
  recvType?: '일반' | '통근';
  /** 거래처별 정산 청구법인 = recv.fin_grp_id(#117) — 비우면 전체 · 0 = 법인 미지정. */
  finGrpId?: number;
  driverId?: number;
  /** 소계 기준. 켜면 목록 **정렬도 그 키로 바뀐다**(안 바꾸면 소계가 엉뚱한 자리에 선다). */
  groupBy?: 'cust' | 'grp';
  /** 거래처별 정산 머리글 정렬(#452)·칸별 검색(#440) — 서버가 한다. */
  sort?: string;
  dir?: 'asc' | 'desc';
  filters?: Record<string, string>;
  /** 거래처별 정산 「통합 청구 묶음으로 보기」(#489) — 켜면 거래처 소계는 서버가 끈다. */
  billGroup?: boolean;
}

export interface FinGrpOption { id: number; label: string }
/** 통합 청구 묶음 한 줄(#489) — 그 달 묶음 멤버 중 **내가 보는 줄만** 서버가 더한 값(금액 문자열). `key` = 줄의 `bill_group_key`. */
export interface BillGroupSum extends Record<string, unknown> {
  key: string; group_id: number; name: string; drive_mon: string | null; count: number;
}

/** 금액은 전부 문자열이다 — numeric 을 숫자로 왕복시키면 소수 4자리가 깎인다. */
export interface ResultCust {
  recv_id: number;
  drive_mon: string | null;
  cust_id: number | null;
  cust_name: string | null;
  fin_req_method: string | null;
  fin_manager: string | null;
  /** 버스인 접수담당자 이름(#497 · 거래처별 정산 목록만). */
  owner_name?: string | null;
  dispt_cnt: number | null;
  cost_cust: string | null;
  cost_cust_add_tax: string | null;
  cost_driver: string | null;
  cust_plus: string | null;
  cust_minus: string | null;
  driver_plus: string | null;
  driver_minus: string | null;
  cost_cust_total: string | null;
  cost_driver_total: string | null;
  profit: string | null;
  /** 관리기사A 차량비용 배분(직영 수익 시스템 식) · 그 달 급여명세 전이면 null */
  direct_cost?: string | null;
  /** 실질수익 = profit − direct_cost */
  real_profit?: string | null;
  has_misu: boolean | null;
}

export interface SalarySummary {
  drive_mon: string | null;
  kind: string | null;
  owner_id: number | null;
  owner_name: string | null;
  driver_id: number | null;
  driver_name: string | null;
  driver_kind: string | null;
  grp_id: number | null;
  grp_name: string | null;
  line_cnt: number | null;
  plus_amt: string | null;
  minus_amt: string | null;
  result_amt: string | null;
}

/** 전월누적분잔액(#262) — 차주 이월잔액. snap = 명세 생성 때 굳힌 값 · live = 스냅샷이 없어 지금 합계. */
export interface SalaryRemain {
  owner_id: number;
  owner_name: string | null;
  amt: string;
  src: 'snap' | 'live';
}

export interface SalaryLine {
  sort: number | null;
  kind: string | null;
  io: string | null;
  category: string | null;
  account_name: string | null;
  amt: string | null;
  comment: string | null;
  /** 그 줄을 만든 사람의 로그인 아이디. ⚠️ `created_by` 는 전 행이 'busman'(생성기)이라 못 쓴다. */
  job_user: string | null;
  jobdt: string | null;
  /** 위 아이디를 `users.login_id` 로 이어 붙인 이름(실측 매칭률 99.9%). 못 찾으면 아이디 그대로. */
  job_user_name: string | null;
  /** 그 줄 접수의 정산담당(#285 · recv.fin_manager). 노선에 안 매인 줄은 null. */
  fin_manager?: string | null;
}

/**
 * 명세 생성 미리보기. **읽기 전용** — 무엇이 지워지고 무엇이 생기는지를 먼저 숫자로 본다.
 * `current.migrated` 가 0 이 아니면 그 달 원장은 레거시에서 이관된 과거 명세다.
 */
export interface SalaryGenPreview {
  driveMon: string;
  excludePreDeposit: boolean;
  excludeCash: boolean;
  isFinished: boolean;
  targetOwners: number;
  targetDrivers: number;
  /** '정산'이 꺼져 있어 대상에서 빠진 정산현황 행 수. */
  excludedNotSalary: number;
  current: { rows: number; plus: string; minus: string; migrated: number };
  generated: { rows: number; plus: string; minus: string; ownerRows: number; driverRows: number };
}

/** 재무추가 카드 대량등록 — 유류/하이패스 두 갈래. 레거시 실측상 이 둘이 전부다. */
export type CardKind = '유류' | '하이패스';

export interface CardUploadArgs {
  driveMon: string;
  cardKind: CardKind;
  /** 지정하면 후보 편성을 그 카드사로 좁힌다 — 뒤 4자리 충돌을 푸는 수단이다. */
  cardCorp?: string;
  /** 비고 앞머리. 꼬리(`유류사용분` 등)는 서버가 계정별로 붙인다. */
  commentPrefix?: string;
  rows: { cardNo: string; amount: string; subsidy?: string }[];
}

export interface CardUploadMatchedRow {
  index: number;
  cardNoRaw: string;
  matchedBy: 'full' | 'last4' | null;
  carAllocId: number | null;
  ownerName: string | null; driverName: string | null; carNo: string | null; grpName: string | null;
  cardCorp: string | null;
  amount: string | null;
  subsidy: string | null;
  errors: string[];
}

export interface CardUploadPreview {
  driveMon: string;
  cardKind: CardKind;
  cardCorp: string | null;
  commentPrefix: string;
  total: number;
  okCount: number;
  errorCount: number;
  /** 실제로 만들어질 result_daily 행 수 — 유류는 보조금이 있으면 한 카드에서 2행이 나온다. */
  willCreate: number;
  /** 이 달·이 카드종류의 후보 편성 수(카드번호가 채워진 것만). */
  candidateCount: number;
  /** 뒤 4자리로 붙은 행 수 — 0 이 아니면 사용자가 눈으로 확인해야 한다. */
  last4Count: number;
  duplicateAllocs: { carAllocId: number; count: number }[];
  isFinished: boolean;
  errors: { row: number; cardNo: string; errors: string[] }[];
  rows: CardUploadMatchedRow[];
  sumUse: string;
  sumSubsidy: string;
}

/** 재무추가 유가보조금 사이트 자료 대량등록 — 차량번호·주유일로 그날 편성을 찾는다. */
export type FuelSubsidyMode = 'both' | 'company' | 'site';
export interface FuelSiteArgs {
  driveMon: string;
  from?: string;
  to?: string;
  subsidyMode: FuelSubsidyMode;
  commentUse?: string;
  commentSub?: string;
  commentCo?: string;
  /** 시트 그대로(머리글 위 조건 줄 포함) — 서버가 라벨로 칸을 찾는다. */
  files: { name: string; rows: string[][] }[];
  fixes?: Record<string, number>;
  skipErrors?: boolean;
  allowDup?: boolean;
  /** 'dyops' = files 대신 DYOPS 유류 거래에 올린 사이트 자료. */
  source?: 'dyops';
}
export interface FuelSiteLine {
  key: string; siteCarNo: string; count: number; pay: number; siteSubsidy: number; subsidy: number; coSubsidy: number;
  companies: string[]; card4: string[]; firstDate: string | null; lastDate: string | null;
  carMatch: 'exact' | 'embedded' | 'fixed' | null; pickedBy: 'acct' | 'driver' | 'latest' | null;
  outside: number; split: number; existing: number;
  carAllocId: number | null; carNo: string | null; driverName: string | null; driverKind: string | null;
  ownerName: string | null; grpName: string | null;
  suggestions: { carId: number; carNo: string }[]; errors: string[];
}
export interface FuelSitePreview {
  driveMon: string; from: string; to: string; subsidyMode: FuelSubsidyMode; rate: string;
  commentUse: string; commentSub: string; commentCo: string;
  files: { name: string; approved: number; cancelled: number; outOfRange: number; unreadable: number; minDate: string | null; maxDate: string | null }[];
  unreadable: { file: string; row: number; why: string }[]; unreadableCount: number;
  isFinished: boolean;
  cars: number; okLines: number; errorCars: number; willCreate: number;
  sumPay: number; sumSiteSubsidy: number; okPay: number; okSubsidy: number; okSiteSubsidy: number; okCoSubsidy: number; errPay: number; errSiteSubsidy: number;
  latestPicked: number; embeddedCars: number; fixedCars: number; splitCars: number; outsideLines: number; dupLines: number;
  lines: FuelSiteLine[];
  /** DYOPS 원천일 때만 — 몇 줄·어느 파일·누가 언제. */
  dyops?: { rows: number; files: string[]; uploadedBy: string[]; lastUploadedAt: string | null } | null;
}

export interface SalaryGenResult {
  driveMon: string;
  targets: number;
  owners: number;
  drivers: number;
  cleared: number;
  created: number;
  plus: number;
  minus: number;
  overwroteMigrated: number;
  failed: number;
  failures: { ownerId: number | null; driverId: number | null; error?: string }[];
}

export type MasterKind = 'grp' | 'cust' | 'owner' | 'driver' | 'car' | 'garage' | 'car_alloc';

export type CarryKind = 'mo' | 'inst' | 'daily';
export type CarryPreview =
  | { ended: false }
  | {
      ended: true; enddt: string; from: string; moDefs: number; moRows: number; inst: number; daily: number; months: string[];
      targets: { car_alloc_id: number; driver_name: string | null; owner_name: string | null; grp_name: string | null; startdt: string | null; enddt: string | null }[];
    };

export interface OpsTransitItem { docId: string; dyNo: string | null; cash: string; req: string; cashError: string | null; reqError: string | null }
export interface OpsTransitRun { fromYm: string; docs: number; dryRun: boolean; counts: Record<string, number>; items: OpsTransitItem[] }
export interface OpsTransitConfig { enabled: boolean; from_ym: string; updated_at: string | null; updated_by: string | null }
export interface OpsTransitRow {
  doc_id: string; dy_no: string | null; kind: string | null; burden: string | null; settle_ym: string | null; corp_label: string | null;
  client: string | null; payee: string | null; amount: string | null; paid_date: string | null;
  cash_mode: string | null; cash_book_id: string | null; cash_pay_date: string | null; cash_amount: string | null; cash_error: string | null;
  req_mode: string | null; result_req_id: number | null; req_mon: string | null; req_amount: string | null; req_account: string | null;
  req_error: string | null; warn: string | null; doc_gone: boolean; synced_at: string | null;
}

/** OPS 대조 보기 한 DY(`opsTransitXref`) — 출납·영업정산 줄을 같은 DY 번호로 전부 모은다. */
export interface OpsXrefCash { id: string; date: string; source: string; amount: string; corp: string | null; vendor: string | null; summary: string | null; deduct: boolean; linked: boolean; deducted: string | null }
export interface OpsXrefReq { id: number; mon: string | null; recv: number | null; dispt: number | null; account: string | null; amount: string | null; driverMinus: boolean; linked: boolean; by: string | null; held?: boolean; misuState?: string | null }
export interface OpsXrefRow {
  digits: string; dy_no: string; doc_id: string | null; kind: string | null; burden: string | null; settle_ym: string | null;
  payee: string | null; client: string | null; amount: string | null; paid_date: string | null;
  cash_mode: string | null; cash_book_id: string | null; cash_error: string | null;
  req_mode: string | null; result_req_id: number | null; req_error: string | null; warn: string | null; doc_gone: boolean | null;
  cash_lines: OpsXrefCash[]; req_lines: OpsXrefReq[];
}
/** 정산현황 줄 보기 한 줄(`listResultSalaryLines`). grp = 목록 칸(drive·req_plus…·none=합계 밖). */
export interface SalaryStatusLine {
  grp: string; kind: string; io: string; account_name: string | null; amt: string | null; comment: string | null; section: number; src_id: number | null;
  recv_id: number | null; dispt_id: number | null; car_id: number | null; driver_id: number | null; owner_id: number | null; drive_mon: string;
  car_no: string | null; driver_name: string | null; link_recv_id: number | null; link_dispt_id: number | null;
  ops_dy_no: string | null; ops_cash_book_id: string | null; ops_cash_date: string | null; grp_sum: string; grp_n: number;
  /** 영업정산 줄만 — 그 접수·그 달 청구상태(#251). */
  misu_state: string | null;
}

export interface DriverCustomerRow {
  driver_customer_id: string; cust_name: string; biz_number: string | null; email: string | null;
  created_by: string | null; created_at: string; updated_by: string | null; updated_at: string | null; invoice_n: number;
}

/** 회계 › 카드 점검(`functions/src/cardCheck.ts`). */
export interface CardLiveRow {
  car_alloc_id: number; car_no: string | null; driver_name: string | null; driver_kind: string | null; owner_name: string | null;
  grp_name: string | null; grp_kind: string | null; startdt: string | null; enddt: string | null;
  fuel_corp: string | null; fuel_num: string | null; hipass_corp: string | null; hipass_num: string | null;
}
export interface CardHeldRow {
  car_alloc_id: number; kind: 'fuel' | 'hipass'; num: string; corp: string | null; startdt: string | null; enddt: string;
  car_no: string | null; driver_name: string | null; owner_name: string | null; grp_name: string | null;
  live_cnt: number; move_to: number | null; move_to_driver: string | null;
}
export interface CardUsageRow {
  result_daily_id: number; account_name: string; amt: string; car_no: string | null; driver_name: string | null;
  owner_name: string | null; comment: string | null; st: string;
}
export interface CardCheckResult {
  today: string; live: CardLiveRow[]; held: CardHeldRow[]; mon: string | null; months: string[]; usage: CardUsageRow[]; usageTotal: number;
}

/** 출금 ↔ 출납일보 대조(회계 › 출금 대조 · 서버 `cashOutMatch.ts`). 금액은 원 단위 문자열. */
export type CashOutDayStatus = 'ok' | 'missing' | 'nofile';
export interface CashOutDay {
  corp: number; date: string; status: CashOutDayStatus; bal_out: string; bank_out: string; cash_out: string; matched: string;
  only_bank: string; only_bank_n: number; transfer: string; only_cash: string; only_cash_n: number; unknown_cash: string;
}
export interface CashOutBank { id: number; corp: number; date: string; amount: string; summary: string | null; memo: string | null; bank: string; account_no: string; alias: string | null }
export interface CashOutCash { id: number; corp: number; date: string; amount: string; method: string | null; cash_account: string | null; vendor: string | null; summary: string | null; corp_name: string }
export type CashOutHow = 'same' | 'fee' | 'near' | 'bundle' | 'many' | 'daysum';
export interface CashOutMatchResult {
  mon: string; from: string; to: string; corps: { corp_id: number; short_name: string }[]; days: CashOutDay[];
  bank: CashOutBank[]; cash: CashOutCash[]; groups: { how: CashOutHow; bank: number[]; cash: number[] }[];
  onlyBank: number[]; transfer: number[]; onlyCash: number[]; cashUnknown: number[];
}

/** 관계법인 정산(회계 › 관계법인 정산 · 서버 `affilSettle.ts`). 방향 = 운행 소속(from) → 청구법인(to). 금액은 원 단위 문자열. */
export interface AffilLine {
  from_grp: number; to_grp: number; cust_id: number | null; cust_name: string; dispt_name: string; driver_name: string; car_no: string;
  start_time: string; req_status: string; bank_status: string; recv_id: number | null; dispt_id: number | null;
  base: string; rate: string | null; amt: string; excluded: boolean;
}
export type AffilRuleKind = 'markup' | 'exclude' | 'fixed';
export interface AffilRule {
  id: number; kind: AffilRuleKind; from_grp_id: number; to_grp_id: number | null; cust_id: number | null; cust_name: string | null;
  rate: string | null; amount: string | null; memo: string | null; from_mon: string; to_mon: string | null;
  created_by: string; created_at: string; updated_by: string | null; updated_at: string; active: boolean;
}
export interface AffilSettleResult {
  mon: string; corps: { grp_id: number; name: string; label: string }[]; lines: AffilLine[];
  pairs: { from_grp: number; to_grp: number; amount: string; count: number }[];
  nets: { payer: number; payee: number; amount: string }[]; rules: AffilRule[];
}

/** 지급만(청구 없음) 예외 한 줄 — state: 열림(그 달 청구 0 → 명세에 운행료) · 청구 있음(보통 경로) · 기간 밖. */
export type PayOnlyRow = {
  id: number; recv_id: number; recv_type: string; cust_name: string | null; from_mon: string; to_mon: string | null; reason: string;
  created_by: string; created_at: string; updated_by: string | null; updated_at: string;
  cost_cust_total: string; cost_driver_total: string; state: '열림' | '청구 있음' | '기간 밖';
};

/** 통합 청구 묶음 한 줄 = 묶음 × 접수(멤버 없는 묶음은 member_id null) · 11-02 미팅 #48. */
export type BillGroupRow = {
  group_id: number; group_name: string; note: string | null; updated_by: string | null; updated_at: string;
  member_id: number | null; recv_id: number | null; from_mon: string | null; recv_type: string | null; cust_name: string | null;
  cost_cust_total: string; cost_driver_total: string; profit: string;
  req_status: string | null; bank_status: string | null;
  state: '청구 있음' | '묶음 청구로 열림' | '닫힘' | '시작 전' | null;
};

export interface CashBulkDeductRow {
  cash_book_id: string; pay_date: string; corp_name: string; vendor: string | null; summary: string | null; cash_account: string | null;
  amount: string; deduct: boolean; vat_separate: boolean; deduct_amount: string | null; plan_n: number; deduct_done: boolean; linked: number;
  car_alloc_id: number | null; alloc_label: string | null; deduct_account: string | null; deduct_mon: string | null; plate: string | null;
  sug_alloc_id: number | null; sug_alloc_label: string | null; sug_account: string | null; default_mon: string;
}

export const api = {
  listCardCheck: call<{ mon?: string | null }, CardCheckResult>('listCardCheck'),
  getCashOutMatch: call<{ mon: string; corpId?: number | null }, CashOutMatchResult>('getCashOutMatch'),
  getAffilSettle: call<{ mon: string }, AffilSettleResult>('getAffilSettle'),
  saveAffilRule: call<{
    id?: number; off?: boolean; kind?: AffilRuleKind; fromGrpId?: number; toGrpId?: number | null; custId?: number | null;
    rate?: string | null; amount?: string | null; memo?: string | null; fromMon?: string; toMon?: string | null;
  }, { id?: number; ok?: boolean }>('saveAffilRule'),
  releaseAllocCard: call<{ carAllocId: number; kind: 'fuel' | 'hipass'; moveTo?: number | null }, { carAllocId: number; moveTo: number | null; num: string }>('releaseAllocCard'),
  registerExternalAlloc: call<Record<string, unknown>, ExternalAllocPlan>('registerExternalAlloc'),
  saveCarAlloc: call<Record<string, unknown>, { carAllocId: number; endedExternal?: ExternalDup[] }>('saveCarAlloc'),
  saveCarAllocCards: call<{ carAllocId: number; fuelCorp: string; fuelNum: string; hipassCorp: string; hipassNum: string }, { carAllocId: number }>('saveCarAllocCards'),
  /** 관리자 「기사앱으로 보기」(읽기 전용 · 10-21) — custom token. */
  adminViewAsDriver: call<{ driverId: number }, { token: string; until: number; driverName: string | null }>('adminViewAsDriver'),
  saveDriverBank: call<{ driverId: number; bankName: string; bankOwner: string; bankNumber: string }, { driverId: number }>('saveDriverBank'),
  previewExternalDups: call<{ driverId: number; grpId: number; startdt?: string; exceptAllocId?: number }, { endDate: string; rows: ExternalDup[] }>('previewExternalDups'),
  deleteCarAlloc: call<{ carAllocId: number }, { carAllocId: number }>('deleteCarAlloc'),
  /** 편성 이어받기(#65) — 끝낸 편성에 남은 종료 뒤 항목. 종료일 없으면 `ended:false`. */
  previewCarAllocCarry: call<{ carAllocId: number }, CarryPreview>('previewCarAllocCarry'),
  carryCarAlloc: call<{ fromId: number; toId: number; kinds: CarryKind[] },
    { moDefs: number; moRows: number; inst: number; daily: number; from: string; denied: string[] }>('carryCarAlloc'),
  saveGrp: call<Record<string, unknown>, { grpId: number }>('saveGrp'),
  saveCust: call<Record<string, unknown>, { custId: number }>('saveCust'),
  /** 거래처 담당자·정산담당자 여러 명(레거시 cust_manager) — 종류별 첫 줄 = 기본 */
  listCustContacts: call<{ custId: number }, { rows: CustContact[] }>('listCustContacts'),
  listCustStartLocs: call<{ custId: number }, { locs: string[] }>('listCustStartLocs'),
  /** 담당자만 저장 — 기준정보 또는 배차 입력 권한(#235). */
  saveCustContacts: call<{ custId: number; contacts: CustContact[] }, { custId: number; rows: CustContact[] }>('saveCustContacts'),
  saveCustPhone: call<{ custId: number; phone: string }, { custId: number; phone: string }>('saveCustPhone'),
  /** 거래처 입력 중 사업자번호(숫자만)·이름이 이미 있는지 — 안내용(저장도 같은 판정으로 막는다). 10-17 */
  checkCustDup: call<{ custId?: number | null; bizNumber?: string; custName?: string }, { byBiz: CustDupHit[]; byName: CustDupHit[] }>('checkCustDup'),
  /** 거래처 중복 정리 2단계 — 사업자번호 같고 이름 다른 묶음(10-17 way). */
  listCustDupGroups: call<{ q?: string }, { groups: CustDupGroup[]; recent: CustMergeLog[] }>('listCustDupGroups'),
  mergeCusts: call<{ toCustId: number; from: { custId: number; dept?: string }[] }, { toCustId: number; merged: number; counts: Record<string, number>; contactsMoved: number; contactsAdded: number }>('mergeCusts'),
  undoCustMerge: call<{ logId: number }, { custId: number }>('undoCustMerge'),
  /** 외부 차주 중복 정리 — 관광사 사업자번호 · 개인 계좌가 같은 묶음(10-17 way). */
  listOwnerDupGroups: call<{ q?: string }, { groups: OwnerDupGroup[]; recent: OwnerMergeLog[] }>('listOwnerDupGroups'),
  mergeOwners: call<{ toOwnerId: number; fromOwnerIds: number[] }, { toOwnerId: number; merged: number; counts: Record<string, number>; kept: Record<string, number> }>('mergeOwners'),
  undoOwnerMerge: call<{ logId: number }, { ownerId: number }>('undoOwnerMerge'),
  saveOwner: call<Record<string, unknown>, { ownerId: number; loginId?: string }>('saveOwner'),
  saveDriver: call<Record<string, unknown>, { driverId: number; loginId?: string }>('saveDriver'),
  /** 주민번호 전체 보기 — 인차팀·회계·관리자만, 볼 때마다 서버에 기록된다. */
  revealDriverRrn: call<{ driverId: number }, { resNo: string }>('revealDriverRrn'),
  revealOwnerRrn: call<{ ownerId: number }, { resNo: string }>('revealOwnerRrn'),
  saveCar: call<Record<string, unknown>, { carId: number }>('saveCar'),
  setCarTypeGubunBulk: call<{ carIds: number[]; value: string; overwrite: boolean }, { changed: number; skipped: number }>('setCarTypeGubunBulk'),
  saveGarage: call<Record<string, unknown>, { garageId: number; warnings?: string[] }>('saveGarage'),
  deleteMaster: call<{ kind: MasterKind; id: number }, { kind: string; id: number }>('deleteMaster'),
  dbHealth: call<void, DbHealth>('dbHealth'),
  getMe: call<void, Me>('getMe'),
  /** 내 화면 조건 하나 기억(#37). value null = 지우기. */
  setMyPref: call<{ key: string; value: unknown }, { ok: true }>('setMyPref'),
  listAppUsers: call<ListArgs, ListResult<AppUser>>('listAppUsers'),
  /** 팀은 3-상태다: 안 주면 그대로 · `''` 이면 지움 · 값이면 그 값으로. */
  setAppUserRole: call<
    { appUserId: number; role?: Role; isActive?: boolean; team?: string; viewScope?: ViewScope; viewTeams?: string[] },
    { appUserId: number }
  >('setAppUserRole'),
  /** 담당자 선택기 — 관리자 전용이 아니다(배차 담당도 사람 목록이 필요하다). */
  listStaff: call<void, { rows: Staff[]; teams: string[] }>('listStaff'),
  // 버스인 담당자 ↔ 웹 계정 연결(관리자 · 09-27) — 담당자별 보기 범위의 기준
  listLegacyManagers: call<void, { rows: Record<string, unknown>[] }>('listLegacyManagers'),
  setLegacyManagerMap: call<{ links: { usersId: number; appUserId: number | null }[] }, { changed: number }>('setLegacyManagerMap'),
  // 담당 넘기기(10-12) — 퇴사·인수인계. 마감된 달 담당은 굳어 있어 안 따라간다.
  listHandoverSources: call<void, { rows: HandoverSource[] }>('listHandoverSources'),
  previewOwnerHandover: call<{ appUserId?: number; usersId?: number },
    { webCnt: number; legacyCnt: number; legacyIds: number[]; custs: { cust_name: string | null; recvs: number; last_recv: string | null }[]; frozenMonths: number }>('previewOwnerHandover'),
  commitOwnerHandover: call<{ appUserId?: number; usersId?: number; toAppUserId: number; note?: string },
    { fromName: string; toName: string; webMoved: number; legacyMoved: number }>('commitOwnerHandover'),
  listOwnerHandovers: call<void, { rows: { owner_handover_id: number; from_name: string; to_name: string; web_moved: number; legacy_recv_cnt: number; note: string | null; created_by: string; created_at: string }[] }>('listOwnerHandovers'),
  previewSettleScope: call<void, SettleScopePreview>('previewSettleScope'),
  setSettleScope: call<{ on: boolean }, { on: boolean }>('setSettleScope'),
  listScreenPerms: call<void, ScreenPermTable>('listScreenPerms'),
  setScreenPerms: call<{ changes: ScreenPermChange[] }, { changed: number }>('setScreenPerms'),
  /** DYOPS users.team → app_user.team (`dyopsTeamSync.ts`). dryRun 이면 바뀔 목록만. */
  syncDyopsTeams: call<{ dryRun?: boolean }, {
    changes: { appUserId: number; email: string; name: string | null; from: string | null; to: string }[];
    renames: { from: string; to: string; members: number }[];
    matched: number; dyopsWithTeam: number; users: number; applied: number; dryRun: boolean;
  }>('syncDyopsTeams'),
  createDispt: call<DisptInput, { disptId: number }>('createDispt'),
  updateDispt: call<DisptInput, { disptId: number }>('updateDispt'),
  getDispt: call<{ disptId: number }, Record<string, unknown>>('getDispt'),
  /** 노선 삭제(#109). 배정·정산·입찰이 남았거나 마감된 달이면 서버가 거부한다. */
  deleteDispt: call<{ disptId: number }, { disptId: number }>('deleteDispt'),
  /** 시간이 겹치면 서버가 details.overlap 으로 거부 → 확인 뒤 allowOverlap 으로 다시(#299·#311). */
  assignCar: call<
    { disptId: number; carAllocId: number; allowOverlap?: boolean },
    { disptCarSchId: number; carNo: string | null; driverName: string | null; assigned: number; carCnt: number | null }
  >('assignCar'),
  unassignCar: call<{ disptCarSchId: number }, { disptCarSchId: number }>('unassignCar'),
  cancelAssignment: call<
    { disptCarSchId: number; isDriverCancel: boolean; comment?: string },
    { cancelId: number; cancelFee: string | null; rate: number }
  >('cancelAssignment'),
  listVias: call<{ disptId: number }, { rows: Via[] }>('listVias'),
  saveVias: call<{ disptId: number; items: { viaLoc: string; startHm?: string }[] }, { count: number }>(
    'saveVias',
  ),
  /**
   * 접수·노선·배차 복사.
   * `targetRecvId` 를 주면 **그 접수에 노선을 더한다**(헤더는 그대로) · 없으면 접수째 새로 만든다.
   * `withAssignments` 를 켜면 배정도 복사하는데, 화면 배정과 **같은 가드**를 태워
   * 겹치는 건만 `skipped` 로 돌아온다(전부 거부하지 않는다).
   */
  copyRecv: call<
    {
      recvId: number; disptIds?: number[]; shiftMonths?: number; shiftDays?: number;
      targetRecvId?: number; withAssignments?: boolean;
    },
    {
      recvId: number; recvType: string | null; custName: string | null;
      createdRecv: boolean; disptCount: number;
      assignedCopied: number; assignedSkipped: number;
      skipped: { disptId: number; carNo: string | null; driverName: string | null; reason: string }[];
    }
  >('copyRecv'),
  listRecvDispts: call<{ recvId: number }, { rows: Record<string, unknown>[] }>('listRecvDispts'),
  createRecv: call<RecvInput, { recvId: number; custName: string | null; recvDate: string }>('createRecv'),
  createRecvWithRoutes: call<
    RecvIntakeInput,
    { recvId: number; custName: string | null; recvDate: string; disptIds: number[] }
  >('createRecvWithRoutes'),
  updateRecv: call<RecvInput, { recvId: number }>('updateRecv'),
  deleteRecv: call<{ recvId: number }, { recvId: number }>('deleteRecv'),
  getRecv: call<{ recvId: number }, Record<string, unknown>>('getRecv'),
  /** 청구법인 고르기(#138) — 회계 법인에 연결된 소속만. */
  listRecvFinGrps: call<Record<string, never>, { rows: { grp_id: number; grp_name: string }[] }>('listRecvFinGrps'),
  // 차종 설정(#171) — 접수·노선 차종 고르기 목록 · 고치기는 배차 입력 권한
  listCarKinds: call<Record<string, never>, { rows: CarKind[] }>('listCarKinds'),
  /** 형식코드 조회(#289) — 구 버스인 형식코드 → 차량명. */
  listCarCodes: call<Record<string, never>, { rows: { code: string; car_name: string }[] }>('listCarCodes'),
  saveCarKinds: call<{ rows: { id?: number | null; name: string; seats: number[]; isUse: boolean }[]; removed: number[] }, { rows: CarKind[] }>('saveCarKinds'),
  listGrps: call<ListArgs, ListResult<Grp>>('listGrps'),
  listGarages: call<ListArgs, ListResult<Garage>>('listGarages'),
  listCusts: call<ListArgs, ListResult<Cust>>('listCusts'),
  listCars: call<ListArgs, ListResult<Car>>('listCars'),
  listOwners: call<ListArgs, ListResult<Owner>>('listOwners'),
  listDrivers: call<ListArgs, ListResult<Driver>>('listDrivers'),
  listCarAllocs: call<ListArgs, ListResult<CarAlloc>>('listCarAllocs'),
  /** `finGrps` = 청구법인 고르기 목록(id = grp_id · label = 회계 법인 약칭, 없으면 소속명). */
  listResultCust: call<SettleListArgs, ListResult<ResultCust> & { finGrps?: FinGrpOption[]; billGroups?: BillGroupSum[] }>('listResultCust'),
  /**
   * 미수금 관리 목록.
   * ⚠️ 기간은 **운행시작일**(recv_startdt) 기준이다 — 청구 목록의 정산월과 앵커가 다르다
   *    (실측 62.7%에서 서로 다른 달을 가리킨다).
   * `reqStatus` 를 비우면 **미수 3종**(청구대상·청구완료 · 옛 청구임시)만 나온다.
   */
  /**
   * 미수금 목록. 축이 **둘**이다 — `deposit`(수금)과 `reqStatus`(청구 진행 상태).
   * ⚠️ `deposit` 빈값 = **미수만**(입금일 없음)이 기본. `'입금'` 이면 받은 건, `'전체'` 면 안 거른다.
   */
  listResultMisu: call<
    ListArgs & {
      from?: string; to?: string; deposit?: string; reqStatus?: string;
      driveMon?: string; groupByMon?: boolean;
    },
    ListResult<Record<string, unknown>> & {
      from: string | null; to: string | null; deposit: string; reqStatus: string;
      driveMon: string; groupByMon: boolean;
      subtotals: Record<string, unknown>[];
      /** 목록에 못 실리는 미수(접수 삭제·부재). 숨기지 않고 화면이 수를 알린다. */
      orphan: { count: number; no_recv: number; deleted_recv: number } | null;
    }
  >('listResultMisu'),
  listResultFinish: call<{ driveMon?: string }, { rows: Record<string, unknown>[] }>('listResultFinish'),
  setResultFinish: call<
    { driveMon: string; resultType: string; isFinish: boolean },
    { isFinish: boolean; lockedDispts: number; lockedRecvs: number }
  >('setResultFinish'),
  /** 월 마감 예정일(매월 N일 · 전월 자동 마감) — `resultFinishAuto.ts`. due = 그 정산월의 자동 마감 날짜(대상 밖이면 null). */
  listResultFinishAuto: call<
    { driveMon?: string },
    { autoFrom: string; rows: { type: string; day: number | null; due: string | null }[] }
  >('listResultFinishAuto'),
  setResultFinishDay: call<{ resultType: string; day: number | null }, { resultType: string; day: number | null }>('setResultFinishDay'),
  /**
   * 미청구 — 정산월 한 달의 「거래처별 정산엔 있는데 청구가 안 걸린」 접수(원장 줄 없음 · 청구상태 빈값/청구대상).
   * 기본은 0원·움직임 없는 줄을 뺀다(`hiddenZero` 로 수를 준다). `held_cnt`·`ops_cnt` = 게이트에 막힌 영업정산·OPS 줄.
   */
  listResultUnbilled: call<
    ListArgs & { driveMon: string; includeZero?: boolean },
    ListResult<Record<string, unknown>> & { driveMon: string; includeZero: boolean; hiddenZero: number }
  >('listResultUnbilled'),
  // 정산 총괄(10-28 way · 구 버스인 소속별 정산현황·기사정산) — 편성 단위 줄 · 회계 조회. 서버 `settleSummary.ts`.
  getSettleSummary: call<{ driveMon: string }, { driveMon: string; rows: Record<string, unknown>[]; ms: number }>('getSettleSummary'),
  // 지급만(청구 없음) 예외 접수(10-28 way) — 관리자만 넣고 끈다. 서버 `recvPayOnly.ts`.
  listRecvPayOnly: call<{ mon: string }, { rows: PayOnlyRow[] }>('listRecvPayOnly'),
  listRecvBillGroups: call<{ mon: string }, { rows: BillGroupRow[] }>('listRecvBillGroups'),
  // 팀별 정산 마감 기한(11-04) — 기한 다음 날부터 관리자·회계 외 쓰기 막힘. 서버 closeDeadline.ts.
  listCloseDeadlines: call<{ mon?: string }, { rows: { driveMon: string; kind: '영업' | '월정기' | '회계'; deadline: string; note: string | null; closed: boolean }[] }>('listCloseDeadlines'),
  saveCloseDeadline: call<{ mon: string; kind: '영업' | '월정기' | '회계'; deadline: string | null }, { ok: boolean }>('saveCloseDeadline'),
  saveRecvBillGroup: call<{ op: 'group' | 'drop' | 'add' | 'remove'; id?: number; name?: string; note?: string; recvId?: number; fromMon?: string; memberId?: number },
    { ok?: boolean; id?: number; memberId?: number }>('saveRecvBillGroup'),
  saveRecvPayOnly: call<{ id?: number; recvId?: number; fromMon?: string; toMon?: string | null; reason?: string; off?: boolean },
    { ok?: boolean; id?: number; updated?: boolean }>('saveRecvPayOnly'),
  getResultMisu: call<{ recvId: number; driveMon: string }, { row: Record<string, unknown> | null; cancel: Record<string, unknown> | null; invoiceDate?: string | null }>('getResultMisu'),
  // 청구 취소·수정 요청(10-02 · 10-19 일원화) — 영업 요청 → 회계 › 발행 요청 화면에서 승인·반려. 서버 `misuCancel.ts`.
  requestMisuCancel: call<{ recvId: number; driveMon: string; reason: string; kind: MisuCancelKind; fixAmount?: string; fixVatIncluded?: boolean; fixNote?: string; writtenDate: string },
    { requestId: number }>('requestMisuCancel'),
  withdrawMisuCancel: call<{ requestId: number }, { requestId: number }>('withdrawMisuCancel'),
  listMisuCancelRequests: call<{ status?: '대기' | '끝남' | '전체'; kind?: MisuCancelKind | null; corpId?: number | null; q?: string },
    { rows: MisuCancelRow[]; pending: number; pendingCancel: number; pendingFix: number }>('listMisuCancelRequests'),
  seeMisuCancel: call<{ requestId: number }, { row: MisuCancelRow | null }>('seeMisuCancel'),
  decideMisuCancel: call<{ requestId: number; approve: boolean; note?: string },
    { requestId: number; status: string; amendIds: number[]; reopenRequestId: number | null }>('decideMisuCancel'),
  ackMisuCancelResults: call<{ ids: number[] }, { ok: boolean }>('ackMisuCancelResults'),
  getBillTimeline: call<{ recvId: number; driveMon: string }, BillTimeline>('getBillTimeline'),
  // 운행편성 변경 관리(10-02 way) — 서버 `allocChange.ts`. 보드·이력·회계 팝업·정산 강조.
  previewAllocChange: call<{ oldAllocId: number; endDate: string; kind: '변경' | '종료' }, AllocChangePreview>('previewAllocChange'),
  applyAllocChange: call<AllocChangeInput, { changeId: number; files?: number; oldAllocId: number | null; newAllocId: number | null; endDate: string;
    carry: { moDefs: number; moRows: number; inst: number; daily: number; from: string } | null; denied: string[]; settleAllocId: number | null; sch?: AllocSchResult | null; commuteForced?: { driveMon: string; disptId: number | null; disptName: string | null }[]; endedExternal?: ExternalDup[] }>('applyAllocChange'),
  listAllocChanges: call<{ mon?: string; day?: string; kind?: string; q?: string; belong?: string; notifyOnly?: boolean; limit?: number; offset?: number; sort?: string; dir?: 'asc' | 'desc' },
    { rows: AllocChangeRow[]; total: number; limit: number; offset: number }>('listAllocChanges'),
  listAllocHistory: call<{ carAllocId: number }, { changes: (AllocChangeRow & { old_alloc_id: number | null; new_alloc_id: number | null })[];
    log: { car_alloc_log_id: number; car_no: string | null; owner_name: string | null; grp_name: string | null; driver_name: string | null;
      startdt: string | null; enddt: string | null; is_use: boolean | null; created_name: string | null; created_at: string | null }[] }>('listAllocHistory'),
  // 운행차량편성 아래 내역 탭(#300) — 권한 없는 탭은 hidden(던지지 않는다).
  listAllocActivity: call<{ carAllocId: number; kind: AllocActivityKind; driveMon?: string }, {
    hidden?: true; reason?: 'screen' | 'scope';
    rows?: Record<string, unknown>[]; total?: Record<string, unknown> | null;
    amts?: Record<string, unknown>[]; months?: string[]; mon?: string;
  }>('listAllocActivity'),
  listCarChanges: call<{ carId: number }, { rows: { at: string; who: string | null; action: 'create' | 'update' | 'delete' | 'file';
    changes: { label: string; old: string | null; new: string | null }[] }[] }>('listCarChanges'),
  listCustChanges: call<{ custId: number }, { rows: { at: string; who: string | null; action: 'create' | 'update' | 'delete' | 'file';
    changes: { label: string; old: string | null; new: string | null }[] }[] }>('listCustChanges'),
  getAllocChange: call<{ id: number }, { row: AllocChangeRow; acks: { name: string; team: string | null; acked_at: string }[];
    carHistory: AllocChangeRow[]; allocLog: Record<string, unknown>[]; settle: { pickable: boolean; monthClosed: boolean } }>('getAllocChange'),
  setAllocChangeApplyDate: call<{ id: number; date: string }, { changed: boolean; startMoved?: boolean; endMoved?: boolean }>('setAllocChangeApplyDate'),
  setAllocChangeSettle: call<{ id: number; pick: 'old' | 'new' | 'split' }, { settleAllocId: number | null; changed: boolean }>('setAllocChangeSettle'),
  ackAllocChanges: call<{ ids: number[] }, { acked: number }>('ackAllocChanges'),
  finalAllocChanges: call<{ ids: number[]; undo?: boolean }, { done: number }>('finalAllocChanges'),
  deleteAllocChangeFile: call<{ fileId: number }, { ok: boolean }>('deleteAllocChangeFile'),
  allocChangePulse: call<void, { modal: boolean; audience: 'accounting' | 'sales' | 'settle' | null; count: number; items: AllocChangeRow[] }>('allocChangePulse'),
  // 마감 후 수정 팀 승인(10-28 way) — 서버 `postCloseReview.ts`
  listPostCloseDrafts: call<{ view?: 'pending' | 'all'; id?: string }, { items: PostCloseItem[] }>('listPostCloseDrafts'),
  postClosePulse: call<void, { count: number; items: PostCloseItem[] }>('postClosePulse'),
  decidePostClose: call<{ draftId: string; team: string; approve: boolean; reason?: string }, { status: string; message?: string | null }>('decidePostClose'),
  saveResultMisu: call<Record<string, unknown>, { resultMisuId: number }>('saveResultMisu'),
  listResultMisuLog: call<{ recvId: number; driveMon?: string }, { rows: Record<string, unknown>[] }>('listResultMisuLog'),
  /**
   * 청구 상세. `rows` 는 **배정(차량) 단위**, `routes` 는 **노선 단위 집계**다.
   * ⚠️ 청구액은 `routes` 에서만 읽는다 — 배정 행의 `cost_cust` 는 노선당 한 줄에만 실리고
   *    **어느 줄인지가 보장되지 않는다**(스냅샷 경로는 정렬 tiebreak 이 없다).
   */
  listResultCustDetail: call<
    SettleListArgs,
    ListResult<Record<string, unknown>> & {
      routes: Record<string, unknown>[];
      /** 같은 모수를 **기사 축**으로 다시 센 것. 운행료 합은 `total` 과 같다. */
      drivers: Record<string, unknown>[];
      /** 관리기사A 차량별 직영비용(실질수익 · 10-21). 옛 서버면 없다. */
      directCars?: { car_id: number; car_no: string | null; direct_cost: string }[];
    }
  >('listResultCustDetail'),
  listSalaryMonths: call<SettleListArgs, ListResult<SalarySummary> & { driveMon: string | null }>(
    'listSalaryMonths',
  ),
  /** 명세서 한 장. `subtotals` 는 지급·공제 두 덩어리의 소계다(명세서는 그렇게 읽는 문서다). */
  listSalaryLines: call<
    SettleListArgs,
    ListResult<SalaryLine> & { subtotals: Record<string, unknown>[]; remain?: SalaryRemain[] }
  >('listSalaryLines'),
  /**
   * 노선의 **금액 두 칸만** 고친다(2026-09-17) — 청구액 **노선 총액** · 운행료 **1대당 단가**.
   * ⚠️ `updateDispt` 와 다르다. 그건 노선 전 칸을 덮어써서 금액만 보내면 나머지가 날아간다.
   * ⚠️ 금액은 **문자열로** 보낸다(number 로 왕복시키면 소수가 깎인다). 마감이면 서버가 막는다.
   */
  setDisptCost: call<
    { disptId: number; costCust?: string | null; costDriver?: string | null; vatIncl?: boolean },
    { disptId: number; costCust: string | null; costDriver: string | null }
  >('setDisptCost'),
  /** 노선 필요 대수만(#293 노선 상세 −/+) — 마감 달·배정 수 아래는 서버가 막는다. */
  setDisptCarCnt: call<{ disptId: number; carCnt: number }, { disptId: number; carCnt: number }>('setDisptCarCnt'),
  setDisptBidding: call<
    { disptId: number; isBidding: boolean; biddingType?: string; closeDate?: string; carYear?: number; bidSeats?: number[]; bidRoutes?: number[] },
    { disptId: number; isBidding: boolean; biddingType: string | null; closeDate: string | null; carYear: number | null; bidSeats: number[] | null; bidOff: boolean; dispt_status: string | null }
  >('setDisptBidding'),
  /** 노선 수정 창 청구법인(#488) — 접수 칸(recv.fin_grp_id)을 고친다 · 마감 달 노선이 있으면 서버가 막는다. */
  setDisptFinGrp: call<{ disptId: number; finGrpId: number }, { recvId: number; changed: boolean }>('setDisptFinGrp'),
  /**
   * 기사 명세서 **공개 예약**(2026-09-16). 마감(`setResultFinish`)과 **다른 축**이다 —
   * 마감은 담당자 쪽 쓰기 잠금, 이쪽은 기사 쪽 읽기 개방.
   */
  listSalaryPublish: call<
    { limit?: number },
    { rows: Record<string, unknown>[]; now: string | null }
  >('listSalaryPublish'),
  setSalaryPublish: call<
    { driveMon: string; mode: 'lock' | 'open' | 'schedule'; openAt?: string; note?: string },
    { driveMon: string; mode: string; published: boolean; openAt: string | null }
  >('setSalaryPublish'),
  /** 그 달 명세 기사가 기사앱으로 볼 수 있는지(10-20 팀장회의 #39) — 로그인·번호 겹침·알림. */
  listSalaryAppReach: call<{ driveMon: string }, { driveMon: string; rows: Record<string, unknown>[] }>('listSalaryAppReach'),
  listResultSalaryStatus: call<
    ListArgs & { driveMon: string; axis?: 'owner' | 'direct'; onlySalary?: boolean; grpId?: number | null },
    ListResult<Record<string, unknown>> & { driveMon: string; axis: string; grps?: { grp_id: number; grp_name: string; grp_kind: string }[] }
  >('listResultSalaryStatus'),
  listResultReqHeld: call<{ driveMon: string; axis?: 'owner' | 'direct'; targetId?: number },{ rows: Record<string, unknown>[]; driveMon: string }>('listResultReqHeld'),
  listResultSalaryLines: call<{ driveMon: string; axis: 'owner' | 'direct'; targetId: number }, { rows: SalaryStatusLine[] }>('listResultSalaryLines'),
  setResultSalary: call<
    { driveMon: string; ownerId?: number; driverId?: number; isSalary: boolean },
    { resultSalaryId: number; isSalary: boolean; created: boolean }
  >('setResultSalary'),
  listResultDaily: call<
    ListArgs & { driveMon: string; io?: 'plus' | 'minus'; corpId?: number | null; ownerId?: number | null },
    ListResult<Record<string, unknown>> & {
      driveMon: string; total: number; plus: string; minus: string; isFinished: boolean;
      corps?: { corp_id: number; short_name: string }[];
    }
  >('listResultDaily'),
  /** 차주 잔액 목록(#261) — 차주별 이월잔액. 기본은 0 이 아닌 차주만, 이름을 치면 0원도. */
  listOwnerRemain: call<
    { q?: string },
    { rows: { owner_id: number; owner_name: string | null; amt: string | null; n: number }[] }
  >('listOwnerRemain'),
  listAccountNames: call<{ q?: string; limit?: number }, { rows: Record<string, unknown>[] }>('listAccountNames'),
  saveResultDaily: call<
    {
      resultDailyId?: number; driveMon: string; accountName: string; isPlus: boolean;
      accountAmt: string; exDate?: string; comment?: string;
      ownerId?: number; driverId?: number; carId?: number; grpId?: number; carAllocId?: number;
    },
    { resultDailyId: number; created: boolean }
  >('saveResultDaily'),
  deleteResultDaily: call<{ resultDailyId: number }, { resultDailyId: number }>('deleteResultDaily'),
  listFinInst: call<
    Omit<ListArgs, 'scope'> & { scope?: 'active' | 'repay' | 'done' | 'all' | 'paydiff'; corpId?: number | null; mon?: string | null; past?: boolean },
    ListResult<Record<string, unknown>> & { corps?: { corp_id: number; short_name: string }[]; showPay?: boolean }
  >('listFinInst'),
  /** `pays` = 연결된 회사 실납부(회계·관리자만, 아니면 null) · 회차 `pay_amt` = 정산월 + 1 의 납부액. */
  getFinInst: call<
    { finInstId: number },
    {
      head: Record<string, unknown>; amts: Record<string, unknown>[]; logs: Record<string, unknown>[]; pays: Record<string, unknown>[] | null;
      /** 상환스케줄표 첨부(#230). */
      files: FinInstFile[];
    }
  >('getFinInst'),
  deleteFinInstFile: call<{ fileId: number }, { ok: boolean }>('deleteFinInstFile'),
  // 할부 회사 실납부(10-01 미팅 #30) — 회계팀 할부금 시트를 옮긴 표. 공제 정산월 = 납부월 − 1.
  listFinInstPay: call<
    { state?: 'active' | 'diff' | 'unlinked' | 'all'; corp?: string | null; q?: string; limit?: number; offset?: number; mon?: string },
    ListResult<Record<string, unknown>> & { corps: { corp_label: string; n: number }[] }
  >('listFinInstPay'),
  finInstPayCandidates: call<
    { finInstPayId?: number; carNo?: string; startDate?: string },
    { rows: Record<string, unknown>[] }
  >('finInstPayCandidates'),
  saveFinInstPay: call<Record<string, unknown>, { finInstPayId: number }>('saveFinInstPay'),
  deleteFinInstPay: call<{ finInstPayId: number }, { finInstPayId: number }>('deleteFinInstPay'),
  saveFinInst: call<
    Record<string, unknown>,
    {
      finInstId: number; created: number; months: number; isNew: boolean; files: number;
      /** #339 수정 — 새 금액으로 맞춘 회차 · 중도상환으로 내린 회차 · 날짜를 지워 되살린 회차. */
      amtSynced: number; repayCut: number; repayRestored: number;
    }
  >('saveFinInst'),
  /** #494 대량 등록 — 고른 편성마다 계약 하나 · 회차 금액 = 붙여 넣은 상환스케줄 그대로. 전부 아니면 전무. */
  bulkCreateFinInst: call<
    Record<string, unknown>,
    { contracts: Array<{ finInstId: number; carNo: string; created: number }>; months: number; from: string; to: string }
  >('bulkCreateFinInst'),
  /** ⚠️ 전량 덮어쓰기다 — `amt` 를 빼면 0 이 된다. `sort`(회차 번호)는 **보냈을 때만** 바뀐다. */
  updateFinInstAmt: call<
    { finInstAmtId: number; amt: string; comment?: string; sort?: number | string },
    { finInstAmtId: number; sort: number | null }
  >('updateFinInstAmt'),
  deleteFinInst: call<{ finInstId: number }, { finInstId: number; cancelledAmts: number; keptClosedMonths: string[] }>('deleteFinInst'),
  // 회차 일괄 조작 — 레거시 FinInstAmtChange* 3화면. 선택한 회차들만 바꾼다(계약은 그대로).
  /**
   * 정산년월 일괄 밀기. 고른 회차의 **첫 달**을 `toDriveMon` 으로 옮기고 나머지를 같은 간격만큼 민다.
   * ⚠️ 옮기기 전·후 달을 둘 다 검사하고, 같은 계약 안에서 달이 겹치면 거부한다.
   */
  shiftFinInstAmtMonths: call<
    { finInstAmtIds: number[]; toDriveMon: string },
    { changed: number; shift: number; from: string; to: string }
  >('shiftFinInstAmtMonths'),
  bulkUpdateFinInstAmt: call<
    { finInstAmtIds: number[]; amt: string },
    { changed: number; amt: string }>('bulkUpdateFinInstAmt'),
  /** 고른 회차 비고 한 번에(#510). */
  bulkUpdateFinInstComment: call<
    { finInstAmtIds: number[]; comment: string },
    { changed: number }>('bulkUpdateFinInstComment'),
  /** comment 를 보내면 비고도 같이(#510) · 안 보내면 그대로. */
  changeFinInstAmtCarAlloc: call<
    { finInstAmtIds: number[]; carAllocId: number; comment?: string },
    { changed: number; carAllocId: number }>('changeFinInstAmtCarAlloc'),
  /** ⚠️ 레거시에 마감 게이트가 없다 — 서버가 막지 않고 `closedMonths` 로 알려준다. */
  changeFinInstAmtOwner: call<
    { finInstAmtIds: number[]; ownerId: number; comment?: string },
    { changed: number; ownerId: number; closedMonths: string[] }>('changeFinInstAmtOwner'),
  listResultMo: call<
    ListArgs & { driveMon: string; appliedOnly?: boolean },
    ListResult<Record<string, unknown>> & {
      driveMon: string; applied: number; templates: number; plus: string; minus: string;
      /** 금액 0 = 등록 대상에서 빠지는 휴면 템플릿 · pending = 아직 등록 안 된 유효 템플릿. */
      dormant: number; pending: number;
      manualCount: number; isFinished: boolean; ended?: number;
    }
  >('listResultMo'),
  /** `all: true` 면 서버가 그 달 미등록 템플릿 **전량**을 처리한다(목록 페이지 크기에 안 묶인다). */
  applyResultMo: call<
    { driveMon: string; defIds?: number[]; all?: boolean },
    {
      driveMon: string; all: boolean; requested: number; created: number; createdIds: number[];
      skippedAlready: number; skippedZero: number; skippedEnded?: number;
    }
  >('applyResultMo'),
  /** 좌우 한 화면 — 왼쪽(def) 정기항목 · 오른쪽(month) 그 달 월정산을 **차주 묶음 단위**로. */
  listResultMoBoard: call<
    { driveMon: string; side: 'def' | 'month'; q?: string; pendingOnly?: boolean; ended?: boolean; limit?: number; offset?: number },
    {
      driveMon: string; side: 'def' | 'month'; limit: number; offset: number; ended?: boolean; endedCount?: number;
      owners: number; n: number; plus: string; minus: string; isFinished: boolean;
      groups: MoGroup[];
    }
  >('listResultMoBoard'),
  /** `all: true` = 그 달 정기항목에서 온 줄 전부(담당 범위 안) — 수동 등록분은 남긴다. */
  cancelResultMo: call<
    { driveMon: string; resultMoIds?: number[]; all?: boolean },
    { driveMon: string; cancelled: number }
  >('cancelResultMo'),
  saveResultMo: call<
    { resultMoId: number; driveMon: string; accountName: string; amt: string; comment?: string },
    { resultMoId: number }
  >('saveResultMo'),
  /** 편성변경 — 정기항목(다음 달부터) + 보고 있는 달에 등록된 줄을 함께 옮긴다. 마감 달이면 정기항목만. */
  reassignResultMo: call<
    { driveMon: string; defIds: number[]; ownerId: number; driverId: number; carId: number; grpId: number },
    {
      driveMon: string; requested: number; defsUpdated: number; monthUpdated: number;
      monthSkippedFinished: boolean; logged: number;
    }
  >('reassignResultMo'),

  // ── 월정기 정기항목 템플릿(result_mo_def) 관리 ──────────────────────────────
  listResultMoDef: call<
    ListArgs & {
      isPlus?: boolean; grpId?: number; ownerId?: number; driverId?: number;
      amtFilter?: 'dormant' | 'active'; driveMon?: string;
    },
    ListResult<Record<string, unknown>> & {
      driveMon: string | null;
      total: {
        n: string; plus_n: string; minus_n: string; dormant_n: string;
        plus_amt: string; minus_amt: string;
      };
    }
  >('listResultMoDef'),
  listMoAccountNames: call<
    { isPlus: boolean; q?: string },
    { isPlus: boolean; rows: { account_name: string; sort: number | null }[] }
  >('listMoAccountNames'),
  /** 대상은 편성으로 고른다 — 네 칸을 따로 고르면 실재하지 않는 조합이 만들어진다. */
  listMoDefTargets: call<
    ListArgs & { activeOnly?: boolean },
    ListResult<{
      car_alloc_id: number; driver_id: number; car_id: number; owner_id: number; grp_id: number;
      car_no: string | null; driver_name: string | null; owner_name: string | null; grp_name: string | null;
      startdt: string | null; enddt: string | null; is_use: boolean | null; def_count: string;
    }>
  >('listMoDefTargets'),
  listResultMoDefLog: call<
    { resultMoDefId: number },
    { resultMoDefId: number; rows: Record<string, unknown>[] }
  >('listResultMoDefLog'),
  /**
   * 신규는 같은 대상·계정의 활성 행이 있으면 **저장하지 않고** `needsConfirm` 으로 돌아온다.
   * 실측상 그런 중복 223행 대부분이 월 중 소속변경에 따른 정당한 일할 분할이라 막지 않는다.
   */
  saveResultMoDef: call<
    {
      resultMoDefId?: number; ownerId: number; driverId: number; carId: number; grpId: number;
      accountName: string; amt: string; isPlus: boolean; comment?: string; confirmDuplicate?: boolean;
    },
    {
      resultMoDefId?: number; created: boolean; needsConfirm: boolean;
      duplicates?: { result_mo_def_id: number; amt: string }[]; message?: string;
    }
  >('saveResultMoDef'),
  /** 적용 이력이 있어도 막지 않는다 — 미마감 달에 남는 항목을 `openMonthsStillApplied` 로 알려준다. */
  deleteResultMoDef: call<
    { resultMoDefId: number } | { resultMoDefIds: number[] },
    {
      resultMoDefId: number; deleted: boolean; accountName: string | null; openMonthsStillApplied: string[];
      deletedCount: number; skipped: number;
    }
  >('deleteResultMoDef'),
  bulkUpdateResultMoDef: call<
    {
      defIds: number[]; kind: 'assign' | 'account' | 'amt';
      ownerId?: number; driverId?: number; carId?: number; grpId?: number;
      accountName?: string; amt?: string;
    },
    { kind: string; requested: number; updated: number; skipped: number; logged: number }
  >('bulkUpdateResultMoDef'),
  /** `dryRun` 을 먼저 부른다 — 한 번에 수백 건이 생기는 동작이다. */
  createAllResultMoDef: call<
    { accountName: string; amt: string; isPlus: boolean; comment?: string; grpId?: number; dryRun?: boolean },
    { dryRun: boolean; targets?: number; toCreate?: number; skipped?: number; created?: number }
  >('createAllResultMoDef'),
  getResultDailyTemplate: call<
    void,
    {
      allocs: {
        car_alloc_id: number; owner_name: string | null; driver_name: string | null;
        car_no: string | null; grp_name: string | null;
      }[];
      accounts: { account_name: string; is_plus: boolean | null; sort: number | null }[];
      columns: string[];
    }
  >('getResultDailyTemplate'),
  previewResultDailyUpload: call<
    { driveMon: string; rows: Record<string, string>[] },
    {
      driveMon: string; total: number; okCount: number; errorCount: number; isFinished: boolean;
      errors: { row: number; errors: string[] }[]; rows: Record<string, unknown>[];
      sumPlus: string; sumMinus: string;
    }
  >('previewResultDailyUpload'),
  commitResultDailyUpload: call<
    { driveMon: string; rows: Record<string, string>[] },
    { driveMon: string; created: number; plus: string; minus: string }
  >('commitResultDailyUpload'),
  /** 카드 대량등록 — 매칭 키가 **카드번호**라 엑셀(편성번호) 경로와 별개다. */
  getCardUploadTemplate: call<
    { driveMon: string; cardKind: CardKind },
    {
      driveMon: string; cardKind: CardKind; corps: string[];
      accounts: { use: string; usePlus: boolean; sub: string | null; subPlus: boolean };
      columns: string[];
      allocs: {
        car_alloc_id: number; card_corp: string | null; card_num: string | null;
        grp_name: string | null; owner_name: string | null; driver_name: string | null; car_no: string | null;
      }[];
    }
  >('getCardUploadTemplate'),
  previewResultDailyCardUpload: call<CardUploadArgs, CardUploadPreview>('previewResultDailyCardUpload'),
  commitResultDailyCardUpload: call<
    CardUploadArgs,
    {
      driveMon: string; cardKind: CardKind; cardCorp: string | null;
      matchedRows: number; created: number; plus: string; minus: string;
    }
  >('commitResultDailyCardUpload'),
  previewResultDailyFuelSite: call<FuelSiteArgs, FuelSitePreview>('previewResultDailyFuelSite'),
  commitResultDailyFuelSite: call<
    FuelSiteArgs,
    {
      driveMon: string; created: number; plus: number; minus: number; lines: number;
      skipped: { siteCarNo: string; pay: number; siteSubsidy: number; companies: string[]; errors: string[] }[];
    }
  >('commitResultDailyFuelSite'),
  previewSalaryGen: call<{ driveMon: string }, SalaryGenPreview>('previewSalaryGen'),
  generateSalary: call<
    { driveMon: string; ownerId?: number; driverId?: number; overwriteMigrated?: boolean },
    SalaryGenResult
  >('generateSalary'),
  listDriveMonths: listDriveMonthsCached,
  /** 캐시를 건너뛰는 원본 — 워밍 전용(캐시가 살아 있으면 컨테이너를 못 깨운다). */
  listDriveMonthsFresh: rawListDriveMonths,
  listRecvs: call<DispatchListArgs, ListResult<Recv>>('listRecvs'),
  listDispts: call<DispatchListArgs, ListResult<Dispt>>('listDispts'),
  listBids: call<DispatchListArgs, ListResult<Bid>>('listBids'),
  /** #391 기사 입찰 신청 취소 — 기사가 전화로 승인받은 뒤 담당자가 누른다(기사앱엔 취소 버튼이 없다). */
  withdrawBid: call<{ disptId: number }, { withdrawn: number }>('withdrawBid'),
  listDisptCarSchs: call<DispatchListArgs, ListResult<DisptCarSch>>('listDisptCarSchs'),
  listFileAssets: call<
    { ownerType: FileOwnerType; ownerId: number },
    { ownerType: FileOwnerType; ownerId: number; items: FileAssetItem[] }
  >('listFileAssets'),

  // 개선요청 게시판 — 담당자가 올리는 요청. 상태·코멘트·첨부는 전부 서버 게이트를 지난다.
  // 공지 관리(09-27) — 쓰기·기사 폰 알림. 발송 전에 반드시 preview 로 인원을 확인받는다.
  listNoticesAdmin: call<{ q?: string; includeExpired?: boolean }, { rows: NoticeAdminRow[] }>('listNoticesAdmin'),
  getNoticeAdmin: call<{ noticeId: number }, { row: NoticeAdminDetail }>('getNoticeAdmin'),
  saveNotice: call<NoticeSaveArgs, { noticeId: number }>('saveNotice'),
  deleteNotice: call<{ noticeId: number }, { ok: true }>('deleteNotice'),
  previewNoticePush: call<{ noticeId: number }, NoticePushPreview>('previewNoticePush'),
  sendNoticePush: call<{ noticeId: number; expectTokens: number; again?: boolean }, { tokenCnt: number; success: number; fail: number; removed: number }>('sendNoticePush'),
  listImprovements: call<ImproveListArgs, ImproveListResult>('listImprovements'),
  importImprovementSeen: call<{ seen: Record<string, string> }, { imported: number }>('importImprovementSeen'),
  getImprovement: call<{ id: number }, ImproveDetail>('getImprovement'),
  countImprovements: call<void, { open: number; mineReplied: number }>('countImprovements'),
  createImprovement: call<
    { title: string; content: string; menu?: string | null; isPrivate?: boolean },
    { improvementId: number }
  >('createImprovement'),
  patchImprovement: call<{ id: number; title: string; content: string; menu?: string | null }, { ok: true }>(
    'patchImprovement',
  ),
  /** 비밀글 켜기/끄기 — 작성자·관리자, 상태 무관(본문 수정 잠금과 별개). */
  setImprovementVisibility: call<{ id: number; isPrivate: boolean }, { ok: true }>('setImprovementVisibility'),
  deleteImprovement: call<{ id: number }, { ok: true }>('deleteImprovement'),
  /** 코멘트·상태전이·완료결과를 **한 번에** 보낸다 — 나눠 보내면 알림이 두 번 나간다. */
  commentImprovement: call<
    { id: number; comment?: string; statusTo?: ImproveStatus | ''; resultNote?: string },
    { historyId: number }
  >('commentImprovement'),
  editImprovementComment: call<
    { historyId: number; comment: string; expectByEmail?: string },
    { ok: true }
  >('editImprovementComment'),
  deleteImprovementFile: call<{ fileId: number }, { ok: true }>('deleteImprovementFile'),
  // 링크 첨부(구글시트 등) — 바이트가 없어서 업로드와 달리 callable 이다.
  addImprovementLink: call<
    { id: number; kind: 'request' | 'comment' | 'result'; historyId?: number; url: string; title?: string },
    { improvementLinkId: number; url: string; title: string; provider: ImproveLinkProvider }
  >('addImprovementLink'),
  deleteImprovementLink: call<{ linkId: number }, { ok: true }>('deleteImprovementLink'),
  /** 삭제는 **행만 soft delete** — 버킷 객체는 남는다(행을 되살리면 그대로 복구된다). */
  deleteFileAsset: call<
    { fileAssetId: number },
    { fileAssetId: number; ownerType: FileOwnerType; ownerId: number; kind: string }
  >('deleteFileAsset'),

  // 영업정산 — 배정 한 건에 붙는 네 갈래 가감.
  listResultReq: call<
    { driveMon?: string; recvId?: number; disptId?: number; recvType?: string; q?: string; limit?: number; offset?: number; resultReqId?: number },
    ListResult<Record<string, unknown>> & {
      total: { n: string; cust_plus: string; cust_minus: string; driver_plus: string; driver_minus: string };
      finishedTypes: string[];
    }
  >('listResultReq'),
  listReqAccountNames: call<
    { kind: ReqAccountKind; q?: string },
    { kind: ReqAccountKind; rows: { account_name: string; sort: number | null }[] }
  >('listReqAccountNames'),
  saveResultReq: call<
    {
      resultReqId?: number; disptCarSchId?: number; disptId?: number; driverId?: number | null; carId?: number | null; driveMon: string;
      custPlus?: string; custMinus?: string; driverPlus?: string; driverMinus?: string;
      custPlusAccount?: string; custMinusAccount?: string;
      driverPlusAccount?: string; driverMinusAccount?: string; comment?: string;
      companyCost?: string; companyCostAccount?: string;
    },
    { resultReqId: number; created: boolean }
  >('saveResultReq'),
  /**
   * 영업정산 **일괄 등록**. 한 번에 **한 축·한 계정**이고 금액만 배정별로 다르다.
   * ⚠️ 새 항목을 만든다(덮어쓰기 아님) · 배정 하나라도 사라졌으면 **전부 롤백**한다.
   */
  saveResultReqBulk: call<
    {
      driveMon: string; kind: ReqAccountKind; accountName: string; comment?: string;
      items: { disptCarSchId?: number; disptId?: number; driverId?: number | null; carId?: number | null; amount: string }[];
    },
    { created: number; skipped: number; kind: ReqAccountKind; accountName: string }
  >('saveResultReqBulk'),
  /** 영업정산 대상 찾기 — 그 정산월에 운행한 배정·통근 달 줄(#80). */
  listReqTargets: call<{ driveMon: string; q?: string }, { rows: Record<string, unknown>[] }>('listReqTargets'),
  deleteResultReq: call<{ resultReqId: number }, { resultReqId: number; deleted: boolean }>('deleteResultReq'),

  // 회계 1단계 — 은행(#12·#13). 전부 `accounting`(+admin) 전용. 파싱은 서버가 한다 — 화면은 시트를 2차원 배열로 넘길 뿐.
  previewBankUpload: call<BankUploadArgs, BankUploadResult>('previewBankUpload'),
  commitBankUpload: call<BankUploadArgs, BankUploadResult & { bankUploadId: number; rowsNew: number; rowsDup: number }>('commitBankUpload'),
  listBankUploads: call<{ limit?: number }, { rows: BankUploadRow[] }>('listBankUploads'),
  revertBankUpload: call<{ id: number }, { ok: true; rows: number }>('revertBankUpload'),
  listAcctCorps: call<void, { rows: AcctCorp[] }>('listAcctCorps'),
  listBankAccounts: call<void, { rows: BankAccountRow[] }>('listBankAccounts'),
  saveBankAccount: call<
    { id?: number; corpId: number; bank: string; accountNo: string; product?: string; alias?: string; currency?: string; isActive?: boolean },
    { ok: true; id: number }
  >('saveBankAccount'),
  listBankTx: call<
    { dateFrom: string; dateTo: string; corpId?: number | null; accountId?: number | null; direction?: 'in' | 'out' | 'all'; q?: string; limit?: number; offset?: number; match?: 'all' | 'unmatched' | 'matched'; kind?: DepKind | 'none' | 'all'; sort?: string; dir?: 'asc' | 'desc' },
    { rows: Row[]; totals: { currency: string; n: number; amount_in: string; amount_out: string }[]; total: number }
  >('listBankTx'),
  /** 입금 주석 — 보낸 칸만 바꾼다. `kind`·`note` 에 null 이면 자동(연결에서 읽기)으로 되돌린다. */
  annotateBankTx: call<{ ids?: number[]; kind?: DepKind | null; note?: string | null; notes?: { id: number; note: string | null }[] }, { ok: true; n: number }>('annotateBankTx'),
  /** #156 입금 「재무추가」 → 기사 재무추가 지급 — 창 준비물·넣기·되돌리기. 스위치는 출납 공제와 같다. */
  getBankTxFin: call<{ id: number }, BankTxFinInfo>('getBankTxFin'),
  commitBankTxFin: call<{ id: number; carAllocId: number; accountName: string; driveMon: string; amount: string }, { resultDailyId: number; driveMon: string; amount: string }>('commitBankTxFin'),
  undoBankTxFin: call<{ id: number }, { removed: number }>('undoBankTxFin'),
  getBankBalances: call<{ date: string; corpId?: number | null; includeInactive?: boolean }, BankBalanceResult>('getBankBalances'),
  /** 일자별 잔액 법인별 확인 표시(#94) — 날짜마다 따로 남는다. */
  listBalanceChecks: call<{ date: string }, { date: string; rows: BalanceCheck[] }>('listBalanceChecks'),
  setBalanceCheck: call<{ date: string; corpId: number; checked: boolean }, { ok: true }>('setBalanceCheck'),

  // 회계 1단계 C — 출납일보·반복 출금(#19). `accounting`(+admin) 전용.
  listCashBook: call<
    { dateFrom: string; dateTo: string; corpId?: number | null; q?: string; deduct?: 'all' | 'deduct' | 'pending' },
    { rows: CashBookRow[]; totals: { corp_name: string | null; n: number; amount: string; deduct_amount: string; pending: string }[]; truncated: boolean }
  >('listCashBook'),
  saveCashBook: call<CashBookInput & { id?: number; afterId?: number; payDate: string }, { ok: true; id: number; deduct?: { state: 'none' | 'done' | 'pending'; reason?: string } }>('saveCashBook'),
  /** 시트·엑셀에서 복사한 여러 줄 — 전부 아니면 전무. */
  /** 표에서 칸 하나만 고치기(칸을 벗어나면 저장). 돌려받은 row 로 그 줄만 바꿔 끼운다. */
  patchCashBook: call<{ id: number; field: 'method' | 'cashAccount' | 'vendor' | 'summary' | 'memo' | 'evidence' | 'amount'; value: string | boolean | null }, { ok: true; row: CashBookRow }>('patchCashBook'),
  /** DYOPS 경조금 줄 «지급완료» — 그 줄의 일자·금액으로 DYOPS 대장 지급(paid false = 버스인웹이 지급한 것 취소). */
  setCondolencePaid: call<{ cashBookId: number; paid: boolean }, { ok: true; paid: boolean }>('setCondolencePaid'),
  pasteCashBook: call<{ corpId: number; rows: (Omit<CashBookInput, 'corpId'> & { payDate: string })[] }, { ok: true; added: number; amount: string }>('pasteCashBook'),
  setCashBookDeductDone: call<{ id: number; done: boolean }, { ok: true }>('setCashBookDeductDone'),
  /** #459 B/ 계정 재무추가 미공제(과태료 제외 · 사유 적은 줄 제외) · 사유 적기(빈 사유 = 지우기). */
  listCashBookBPending: call<{ corpId?: number | null }, { rows: CashBookRow[]; skipped: CashBookRow[]; from: string }>('listCashBookBPending'),
  setCashBookBSkip: call<{ ids: number[]; reason: string }, { ok: true; n: number }>('setCashBookBSkip'),
  deleteCashBook: call<{ id: number }, { ok: true; removed?: number }>('deleteCashBook'),
  deleteCashBooks: call<{ ids: number[] }, { ok: true; deleted: number; removed: number }>('deleteCashBooks'),
  shiftCashBookLine: call<{ id: number; dir: 'up' | 'down' }, { ok: true; moved: boolean }>('shiftCashBookLine'),
  /** #376 — 고른 줄(들)을 기준 줄 위/아래로 끌어 옮기기(같은 법인 · 다른 날이면 그 날로). */
  placeCashBookLines: call<{ ids: number[]; targetId: number; after: boolean }, { ok: true; n: number; dateMoved: number }>('placeCashBookLines'),
  moveCashBooks: call<{ ids: number[]; payDate: string; copy?: boolean }, { ok: true; n: number }>('moveCashBooks'),
  replaceCashBookSummary: call<{ ids: number[]; find: string; to: string }, { ok: true; n: number }>('replaceCashBookSummary'),
  // DYOPS 대중교통비·선지급 → 출납일보·영업정산 자동 반영(#90 · functions/src/opsTransitSync.ts)
  previewOpsTransit: call<void, OpsTransitRun>('previewOpsTransit'),
  runOpsTransitNow: call<void, OpsTransitRun>('runOpsTransitNow'),
  retryOpsTransit: call<{ docId: string }, OpsTransitRun>('retryOpsTransit'),
  listOpsTransit: call<{ ym?: string; corpId?: number | null; kind?: 'transit' | 'advance' | null }, { rows: OpsTransitRow[]; config: OpsTransitConfig | null }>('listOpsTransit'),
  opsTransitXref: call<{ ym?: string; cashBookId?: number | string; resultReqId?: number; dyNo?: string; all?: boolean }, { rows: OpsXrefRow[] }>('opsTransitXref'),
  setOpsTransitConfig: call<{ enabled?: boolean; fromYm?: string }, { enabled: boolean; fromYm: string }>('setOpsTransitConfig'),
  listCashBookHints: call<{ corpId?: number | null }, { methods: string[]; vendors: string[]; cashAccounts: string[]; deductAccounts: string[]; finishedMons: string[] }>('listCashBookHints'),
  /** 한 줄의 수정 이력(#38) — changes = {칸: [전, 후]}. first = 줄의 처음 입력(이력 표 이전 줄에도 있다). */
  listCashBookLog: call<{ id: number }, {
    first: { created_by: string | null; created_at: string; source: string } | null;
    logs: { log_id: string; action: 'insert' | 'update' | 'delete' | 'restore'; changes: Record<string, [unknown, unknown]> | null; changed_by: string | null; changed_at: string }[];
  }>('listCashBookLog'),
  // #19 3번 — 재무추가에 바로 공제 · 일자별 대조(스위치는 버스인웹 전환 뒤에 연다)
  commitCashBookDeduct: call<{ ids: number[]; clearManual?: boolean },{ rows: number; created: number; amount: string }>('commitCashBookDeduct'),
  undoCashBookDeduct: call<{ id: number }, { removed: number }>('undoCashBookDeduct'),
  // #460 한 번에 공제하기 — 창 채우기(추천 편성·계정) · 넣기(전부 아니면 전무)
  prepareCashBookBulkDeduct: call<{ ids: number[] }, { open: boolean; rows: CashBulkDeductRow[] }>('prepareCashBookBulkDeduct'),
  bulkCashBookDeduct: call<{ items: { id: number; carAllocId: number; deductAccount: string; deductMon: string | null; vatSeparate: boolean; deductAmount: string | null }[] },
    { rows: number; created: number; amount: string; saved: boolean; open: boolean }>('bulkCashBookDeduct'),
  cashBookDeductCheck: call<{ dateFrom: string; dateTo: string; corpId?: number | null }, CashDeductCheck>('cashBookDeductCheck'),
  setCashBookDirectDeduct: call<{ open: boolean }, { open: boolean }>('setCashBookDirectDeduct'),
  listCashRules: call<{ corpId?: number | null }, { rows: CashRuleRow[] }>('listCashRules'),
  saveCashRule: call<
    CashBookInput & { id?: number; dayOfMonth?: number; startMonth?: string; endMonth?: string | null; isActive?: boolean; remove?: boolean;
      cycle?: 'day' | 'weekday'; weekOfMonth?: number | null; weekday?: number | null; months?: number[] | null },
    { ok: true; id: number }
  >('saveCashRule'),
  /** #114 — 고른 규칙 여러 개 지우기(전부 아니면 전무). */
  deleteCashRules: call<{ ids: number[] }, { ok: true; deleted: number }>('deleteCashRules'),
  // 회계 「보험」(#135) — 공제조합 분납 → 출납일보
  previewInsurance: call<{ rows: InsRowInput[] }, { rows: InsPreviewRow[] }>('previewInsurance'),
  saveInsurance: call<{ rows: InsRowInput[] }, { ok: true; added: number; ids: number[] }>('saveInsurance'),
  listInsurance: call<{ month?: string | null; corpId?: number | null; q?: string | null; pending?: boolean }, { rows: InsPolicyRow[]; truncated: boolean }>('listInsurance'),
  reflectInsurance: call<{ installmentIds?: number[]; policyIds?: number[]; payDate?: string | null }, { ok: true; created: number; linked: number; skipped: number }>('reflectInsurance'),
  updateInsInstallment: call<{ id: number; amount?: string; periodTo?: string }, { ok: true; total: string | null }>('updateInsInstallment'),
  /** #135 2단계 — 경기공제조합 「분할납입 대상차량조회」 파일(첫 시트를 xlsx 로 읽은 표 그대로). preview 는 쓰지 않는다. */
  previewInsFile: call<{ sheet: unknown[][]; fallbackCorpId?: number | null }, InsFilePreview>('previewInsFile'),
  importInsFile: call<{ sheet: unknown[][]; fallbackCorpId?: number | null }, { ok: true; counts: InsFileCounts }>('importInsFile'),
  deleteInsPolicy: call<{ id: number }, { ok: true }>('deleteInsPolicy'),
  listInsAccounts: call<void, { rows: InsAccountRow[] }>('listInsAccounts'),
  saveInsAccount: call<{ corpId: number; unionName: string; account: string }, { ok: true; removed: boolean }>('saveInsAccount'),
  /** 반복 출금 엑셀 대량 등록(#39) — commit 없으면 미리보기. 한 줄이라도 틀리면 하나도 안 넣는다. */
  importCashRules: call<
    { rows: (string | number)[][]; commit?: boolean },
    { ok: boolean; committed: number; errors: string[];
      items: { line: number; corp: string; repeat: string; months: number[] | null; startMonth: string; endMonth: string | null;
        vendor: string | null; summary: string | null; amount: string }[] }
  >('importCashRules'),
  previewCashRules: call<{ month: string }, { month: string; items: CashRulePlanItem[]; create: number; skip: number }>('previewCashRules'),
  commitCashRules: call<{ month: string; summaries?: Record<string, string> }, { ok: true; month: string; created: number }>('commitCashRules'),

  previewCashBookImport: call<CashImportArgs, { errors: string[]; summary: CashImportSummary[]; ignored: string[] }>('previewCashBookImport'),
  commitCashBookImport: call<CashImportArgs, { ok: true; added: number; removed: number; summary: CashImportSummary[] }>('commitCashBookImport'),

  // 회계 2단계 E — 세금계산서 발행대장(#11). `accounting`(+admin) 전용.
  listInvoiceCandidates: call<{ monFrom?: string; monTo?: string; corpId?: number | null; allMethods?: boolean }, { from: string; to: string; rows: InvoiceCandidate[] }>('listInvoiceCandidates'),
  issueInvoices: call<{ items: { recvId: number; driveMon: string }[]; writtenDate?: string; series?: string; useReqDate?: boolean }, { ok: true; count: number; numbers: string[] }>('issueInvoices'),
  /** 계산서 처리 기록(요청·발행·취소/수정 요청·승인·수정(−) 줄 — 10-27 way). */
  getInvoiceTrail: call<{ id: number }, { rows: { at: string; kind: string; who: string | null; text: string; invoice_id: string | null }[]; recvId: number | null; driveMon: string | null }>('getInvoiceTrail'),
  listInvoices: call<
    { year?: number; driveMon?: string | null; corpId?: number | null; q?: string; status?: 'all' | 'unissued' | 'billed' | 'unpaid' | 'paid'; kind?: 'company' | 'driver' | 'all'; requestOnly?: boolean; requestScope?: 'active' | 'done' | 'all' },
    { year: number; driveMon?: string | null; months?: string[]; rows: InvoiceRow[]; totals: { corp_name: string | null; n: number; total: string; billed: string; paid: string; unpaid: string }[]; truncated: boolean; driverOpen: boolean | null; driverSync: DriverSheetSync | null }
  >('listInvoices'),
  /** 기사 앱 세금계산서 신청 열기·닫기(전환일) — plan/driver-orders.md ③. */
  setDriverOrderOpen: call<{ open: boolean; note?: string }, { ok: true; open: boolean }>('setDriverOrderOpen'),
  /** 기사 거래처(기사 계산서 신청 목록) — 회계팀 등록·수정·지우기(#132). */
  listDriverCustomers: call<{ q?: string; limit?: number; offset?: number }, { rows: DriverCustomerRow[]; total: number }>('listDriverCustomers'),
  saveDriverCustomer: call<{ id?: number; custName: string; bizNumber?: string; email?: string }, { ok: true; id: number; sameBiz: string[]; sheetError?: string | null }>('saveDriverCustomer'),
  deleteDriverCustomer: call<{ id: number }, { ok: true; sheetError?: string | null }>('deleteDriverCustomer'),
  /** #206 기사 매출 대리 처리 — 회계팀이 기사 대신 신청(기사 앱과 같은 길) · 매월 고정발행. */
  searchAcctDrivers: call<{ q: string }, { rows: ProxyDriver[] }>('searchAcctDrivers'),
  getAcctDriverForm: call<{ driverId: number }, { corps: { corp_id: number; short_name: string }[]; requester: string;
    recent: { cust_name: string; biz_number: string | null; email: string | null; phone: string | null }[] }>('getAcctDriverForm'),
  searchAcctDriverCustomers: call<{ q: string }, { rows: { cust_name: string; biz_number: string | null; email: string | null }[] }>('searchAcctDriverCustomers'),
  acctCreateDriverInvoice: call<ProxyCommon & { supply: string; vat?: string | null; dists?: { driverId: number; amount: string }[] },
    { ok: true; invoiceId: number; invoiceNo: string; newCustomer: boolean }>('acctCreateDriverInvoice'),
  acctCreateDriverOrder: call<ProxyCommon & { kind: DriverOrderKind; cashId?: string; supply?: string; vat?: string | null;
    dists?: ({ driverId: number; amount: string } | { type: '외부배분-개인' | '외부배분-법인'; name: string; amount: string })[] },
    { ok: true; orderId: number; orderNo: string }>('acctCreateDriverOrder'),
  listDriverInvoiceFixed: call<void, { rows: FixedRuleRow[] }>('listDriverInvoiceFixed'),
  getDriverInvoiceFixedDraft: call<{ invoiceId: number }, { invoiceId: number; requester: string | null; candidates: { driverId: number; label: string }[];
    existing: number[]; rule: FixedRuleDraft }>('getDriverInvoiceFixedDraft'),
  saveDriverInvoiceFixed: call<Partial<FixedRuleDraft> & { id?: number; sourceInvoiceId?: number | null }, { ok: true; id: number }>('saveDriverInvoiceFixed'),
  deleteDriverInvoiceFixed: call<{ id: number }, { ok: true }>('deleteDriverInvoiceFixed'),
  previewDriverInvoiceFixed: call<{ month: string }, FixedRun>('previewDriverInvoiceFixed'),
  commitDriverInvoiceFixed: call<{ month: string }, FixedRun>('commitDriverInvoiceFixed'),
  /** 기사개인오더 ④ 페이앱·카드단말기·현금영수증·다수배분(plan/driver-orders.md ④). */
  listDriverOrders: call<{ year: number; kind?: DriverOrderKind | null; corpId?: number | null; status?: DriverOrderStatus; q?: string },
    { year: number; rows: DriverOrderRow[]; totals: { kind: DriverOrderKind | null; n: number; amount: string; unpaid: string }[]; truncated: boolean;
      driverOpen: boolean; driverSync: DriverSheetSync | null }>('listDriverOrders'),
  saveDriverOrder: call<{ id: number; custName?: string; bizNumber?: string; cashId?: string; email?: string; phone?: string; requestDate?: string | null;
    supply?: string; vat?: string; memo?: string; issuer?: string; paidDate?: string | null; settleMon?: string | null; depositAmount?: string | null;
    attach?: { path: string; type: string; name: string } | null }, { ok: true; fin: DriverOrderFinResult }>('saveDriverOrder'),
  cancelDriverOrder: call<{ id: number; undo?: boolean; lineNo?: number }, { ok: true; cancelState: string | null; fin: DriverOrderFinResult | null }>('cancelDriverOrder'),
  syncDriverOrders: call<{ commit?: boolean; writeBack?: boolean }, { commit: boolean; source: string; tabs: DriverOrderImportTab[];
    writeBack: { asked: number; updated: number; missing: string[] } | null }>('syncDriverOrders'),
  /** 기사개인오더 ⑤ 발행 알림톡(계산서·페이앱 이미지 → 신청 기사). 이미지는 `uploadDriverNoticeImage` 로 먼저 올린다. */
  previewDriverNotice: call<{ target: DriverNoticeTarget; id: number }, DriverNoticePreview>('previewDriverNotice'),
  sendDriverNotice: call<{ target: DriverNoticeTarget; id: number; phone: string; image: { path: string; type: string; name: string } },
    { ok: true; notifyId: number; templateCode: string }>('sendDriverNotice'),
  saveInvoice: call<Record<string, unknown>, { ok: true; id: number; invoiceNo?: string }>('saveInvoice'),
  exportSmartbill: call<{ ids: number[]; kind?: 'tax' | 'statement' }, {
    files: { corpName: string; rows: (string | number)[][] }[]; skipped: { invoiceNo: string; reason: string }[]; missing: number;
  }>('exportSmartbill'),
  /** 기사개인오더(#12 5번·#14 3번) — 기사접수SYS 엑셀 맞추기. commit 없으면 미리보기(되돌린다). */
  importDriverInvoices: call<CashImportArgs & { commit?: boolean }, { commit: boolean; tabs: DriverImportTab[] }>('importDriverInvoices'),
  /** 전환기 — dytaxdriver 시트에서 **새 신청·기사 취소만** 들여오고, 웹 발행자·입금일자를 시트에 맞춘다(웹이 정본). */
  syncDriverInvoices: call<{ commit?: boolean; writeBack?: boolean }, { commit: boolean; fileName: string | null; tabs: DriverImportTab[];
    writeBack: { asked: number; updated: number; missing: string[] } | null }>('syncDriverInvoices'),

  // 회계 G 과태료·통행료(#21, plan/accounting.md 5-I)
  importFineSheet: call<{ fileName: string; rows: BankCell[][]; commit?: boolean }, FineImportResult>('importFineSheet'),
  listFines: call<FineListArgs, { rows: FineRow[]; total: number; amount: string; badge: { check: number; todeduct: number; unpaid: number } }>('listFines'),
  saveFine: call<Record<string, unknown>, { fineId: number; created: boolean; match: FineMatchCounts | null; deduct?: { created: number; pending: string[]; redone?: boolean } }>('saveFine'),
  deleteFine: call<{ fineId: number }, { fineId: number }>('deleteFine'),
  rematchFines: call<{ fineIds?: number[] }, { targets: number; match: FineMatchCounts }>('rematchFines'),
  getFineCandidates: call<{ fineId: number }, { rows: FineCandidate[] }>('getFineCandidates'),
  searchFineAllocs: call<{ fineId: number; q?: string }, { rows: FineAllocHit[]; violationDate: string }>('searchFineAllocs'),
  lookupFineOwner: call<{ carNo: string; date?: string }, { owners: string[] }>('lookupFineOwner'),
  confirmFineDriver: call<{ fineId: number; carAllocId?: number; skip?: boolean; note?: string }, { fineId: number; state: string }>('confirmFineDriver'),
  listMyFineRequests: call<Record<string, never>, { rows: FineRow[] }>('listMyFineRequests'),

  // 세금계산서 발행 요청(영업 → 회계, 구글챗 대체 · plan/accounting.md 5-J)
  invoiceRequestMeta: call<Record<string, never>, { corps: { corp_id: number; short_name: string }[]; isAcct: boolean; canAcctWrite: boolean; today: string }>('invoiceRequestMeta'),
  lookupInvoiceRequestRecv: call<{ recvId: number; driveMon?: string | null }, InvReqLookup>('lookupInvoiceRequestRecv'),
  saveInvoiceRequest: call<InvReqSaveArgs, { ok: boolean; id: number; bizWarn?: string | null }>('saveInvoiceRequest'),
  cancelInvoiceRequest: call<{ id: number; reason?: string }, { ok: boolean }>('cancelInvoiceRequest'),
  /** #306 세금계산서 창 「반려」 — 발행 전 계산서를 내리고 요청을 보완 요청으로. */
  /** #307 요청 창 거래처·사업자번호 찾기(발행 이력 + 기준정보 거래처). */
  searchInvoiceCust: call<{ q: string }, { rows: { cust_name: string; biz_number: string; email: string | null; issued: number; last_date: string | null; in_master: boolean }[] }>('searchInvoiceCust'),
  rejectInvoiceToRequest: call<{ invoiceId: number; reason: string }, { ok: boolean; requestId: number }>('rejectInvoiceToRequest'),
  listInvoiceRequests: call<{ scope?: 'mine' | 'all'; state?: 'active' | 'done' | 'all' | 'norecv'; q?: string }, { rows: InvReqRow[] }>('listInvoiceRequests'),
  getInvoiceRequest: call<{ id: number }, { row: InvReqRow; msgs: InvReqMsg[]; files: InvReqFile[]; mine: boolean; acct: boolean;
    settle: { supply: string; vat: string; total: string } | null; settleDiff: boolean }>('getInvoiceRequest'),
  deleteInvoiceRequestFile: call<{ fileId: number }, { ok: boolean }>('deleteInvoiceRequestFile'),
  replyInvoiceRequest: call<{ id: number; body: string; fix?: boolean }, { ok: boolean }>('replyInvoiceRequest'),
  issueFromInvoiceRequest: call<{ id: number; series?: string }, { ok: boolean; no: string; invoiceId: number }>('issueFromInvoiceRequest'),
  linkInvoiceRequest: call<{ id: number; invoiceNo: string }, { ok: boolean; done: boolean }>('linkInvoiceRequest'),
  /** 접수번호 없이 올라온 요청에 회계가 접수를 잇는다(발행대장 계산서 접수가 비어 있으면 거기도). */
  setInvoiceRequestRecv: call<{ id: number; recvId: number; driveMon?: string | null }, { ok: boolean; invoiceToo: boolean }>('setInvoiceRequestRecv'),
  completeInvoiceRequest: call<{ id: number; message?: string }, { ok: boolean }>('completeInvoiceRequest'),
  ackInvoiceRequests: call<{ ids: number[] }, { ok: boolean }>('ackInvoiceRequests'),
  /** 세금계산서 발행현황(개선요청 #54) — 정산월 청구액 ↔ 발행대장, 정산 범위. */
  listInvoiceStatus: call<{ driveMon: string }, { driveMon: string; rows: InvStatusRow[]; truncated: boolean }>('listInvoiceStatus'),
  // 차인지급액 조정(탑라인) — plan/topline.md · functions/src/topline.ts
  getTopline: call<{ driveMon: string }, Topline>('getTopline'),
  saveToplineDriver: call<{ driverId: number; payGrpId?: number | null; incomeKind?: '4대보험' | '3.3%' | null }, { ok: boolean }>('saveToplineDriver'),
  saveToplineOwner: call<{ ownerId: number; payDay25: boolean; bizIncome: boolean }, { ok: boolean }>('saveToplineOwner'),
  saveToplineAdjust: call<{ driveMon: string; id?: number; payeeKind?: 'owner' | 'driver'; payeeId?: number; amount: string | number; reason?: string }, { ok: boolean; id?: number }>('saveToplineAdjust'),
  /** 확정(= 정산부족분 재무추가 반영) · reopen = 확정 풀기(반영 줄 내림) */
  closeTopline: call<{ driveMon: string; reopen?: boolean }, { ok: boolean; posted?: number; failed?: number; removed?: number }>('closeTopline'),
  /** 재확정 — 바뀐 묶음 하나만 지금 숫자로 다시 굳힘 */
  reconfirmTopline: call<{ driveMon: string; group: string }, { ok: boolean; result: string }>('reconfirmTopline'),
  exportFineDeduct: call<{ deductMon: string }, FineDeductExport>('exportFineDeduct'),
  exportFineWehago: call<{ from: string; to?: string; corpId?: number | null }, { rows: { fineId: number; paidDate: string; amount: string; kind: string; summary: string }[] }>('exportFineWehago'),
  setFineDeductState: call<{ fineIds: number[]; state: 'done' | 'skip' | 'none' }, { requested: number; updated: number; removedResultDaily: number }>('setFineDeductState'),
  listFineLinkCandidates: call<{ fineId: number }, { rows: FineLinkCandidate[] }>('listFineLinkCandidates'),
  linkFineDeduct: call<{ fineId: number; resultDailyId: number }, { ok: true }>('linkFineDeduct'),
  setFinePaidDate: call<{ fineIds: number[]; paidDate: string }, { requested: number; updated: number; skipped: number; deduct: { created: number; pending: string[] } }>('setFinePaidDate'),
  /** ③ 버스인웹 재무추가에 바로 공제 — 스위치(cutover 뒤 관리자가 연다)가 켜져 있을 때만. 화면이 본 줄만 넣는다. */
  commitFineDeduct: call<{ deductMon: string; fineIds: number[] }, { deductMon: string; created: number; amount: string }>('commitFineDeduct'),
  setFineDirectDeduct: call<{ open: boolean }, { open: boolean }>('setFineDirectDeduct'),
  /** ②단계 — 경찰청·세외수입·통행료 붙여넣기(미리보기 = 넣고 되돌림). */
  importFinePaste: call<{ text: string; format?: FinePasteFormat; commit?: boolean }, FinePasteResult>('importFinePaste'),
  /** 과태료 스캔함(10-01) — 드라이브 폴더 → AI 판독 → 자동 등록·사진. 「지금 가져오기」는 한 번에 10장까지. */
  runFineScan: call<{ max?: number; since?: string }, FineScanRun>('runFineScan'),
  listFineScans: call<{ state?: 'todo' | 'all'; limit?: number }, FineScanList>('listFineScans'),
  setFineScanAuto: call<{ on: boolean }, { on: boolean }>('setFineScanAuto'),
  resolveFineScan: call<{ scanId: number; action: 'register' | 'link' | 'skip' | 'retry'; fields?: FineScanFields; fineId?: number }, { state: string; fineId?: number | null; added?: number; attached?: number }>('resolveFineScan'),
  previewFineNotify: call<{ fineIds: number[] }, FineNotifyPreview>('previewFineNotify'),
  previewFineAutoNotify: call<Record<string, never>, {
    enabled: boolean; from: string; lastAt: string | null; lastNote: string | null; by: string | null;
    willSend: number; waiting: number; max: number; skipped: Record<string, number>;
    rows: Array<{ fineId: number; carNo: string; violationDate: string; amount: string; to: string; phone: string }>;
    templateReady: boolean; templateStatus: string;
  }>('previewFineAutoNotify'),
  setFineAutoNotify: call<{ enabled: boolean }, { enabled: boolean }>('setFineAutoNotify'),
  // 통근 월정산 — 운행일수·기사 등록(#51·#52) · `functions/src/commuteMonth.ts`
  commuteMonthOverview: call<{ driveMon: string }, CommuteOverview>('commuteMonthOverview'),
  getCommuteMonth: call<{ recvId: number; driveMon: string }, CommuteMonth>('getCommuteMonth'),
  createCommuteMonth: call<
    { driveMon: string; recvId?: number; disptIds?: number[]; dryRun?: boolean },
    { driveMon: string; dryRun: boolean; routes: number; lines: number; held: CommuteHeld[] }
  >('createCommuteMonth'),
  saveCommuteLines: call<{ items: { id: number; cnt: number | null; costDriver: string | null }[] }, { saved: number }>('saveCommuteLines'),
  addCommuteLine: call<{ disptId: number; driveMon: string; disptCarSchId?: number; carAllocId?: number }, { id: number }>('addCommuteLine'),
  removeCommuteLine: call<{ id: number }, { id: number }>('removeCommuteLine'),
  /** 배차에서 다시 불러오기(09-30 재설계) — apply 없으면 차이만. */
  reloadCommuteRecv: call<
    { recvId: number; driveMon: string; apply?: { addRoutes: number[]; addLines: number[]; removeLines: number[]; removeRoutes: number[] } },
    CommuteReloadDiff
  >('reloadCommuteRecv'),
  /** 다른 달 그대로 가져오기 — recvId 없으면 범위 안 통근 전부 · dryRun 이면 건수만. */
  copyCommuteMonth: call<
    { driveMon: string; fromMon: string; recvId?: number; dryRun?: boolean },
    { recvs: number; from: { routes: number; lines: number }; cur: { routes: number; lines: number }; held?: CommuteHeld[] }
  >('copyCommuteMonth'),
  /** 통근 새 노선 승인(10-01 미팅 #30) — `functions/src/commuteRoute.ts`. 지난달에 없던 노선은 DYOPS 결재 뒤에 정산 줄. */
  requestCommuteRoutes: call<
    { recvId: number; driveMon: string; disptIds: number[]; reason: string },
    { requestId: number; docId: string; routes: number; approver1: string; approver2: string }
  >('requestCommuteRoutes'),
  checkCommuteRouteRequests: call<{ recvId?: number; driveMon?: string; requestId?: number }, { checked: number; changed: number; errors: string[] }>(
    'checkCommuteRouteRequests'),
  cancelCommuteRouteRequest: call<{ requestId: number }, { requestId: number; draftDocId: string | null }>('cancelCommuteRouteRequest'),
  getCommuteRouteGate: call<Record<string, never>, { on: boolean; updatedBy: string | null; updatedAt: string | null; open: number }>('getCommuteRouteGate'),
  setCommuteRouteGate: call<{ on: boolean }, { on: boolean }>('setCommuteRouteGate'),
  saveCommuteRoute: call<
    { disptId: number; driveMon: string; costCust?: string; costCustCnt?: string; remove?: boolean; vatIncl?: boolean },
    { removed: boolean }
  >('saveCommuteRoute'),
  // 차량검사 만료 알림톡(#62) — 차주에게 60일 전·당일 · 스위치/지금 보내기는 관리자만
  previewCarExpire: call<void, CarExpirePreview>('previewCarExpire'),
  setCarExpireEnabled: call<{ enabled: boolean }, { enabled: boolean }>('setCarExpireEnabled'),
  runCarExpireNow: call<void, { day: string; sent: number; failed: number; skipped: number; blocked: string | null }>('runCarExpireNow'),
  // #449 할부금 만기 구글챗 — `functions/src/finInstExpire.ts`
  previewFinInstExpire: call<void, FinInstExpirePreview>('previewFinInstExpire'),
  setFinInstExpireEnabled: call<{ enabled: boolean }, { enabled: boolean }>('setFinInstExpireEnabled'),
  runFinInstExpireNow: call<void, { day: string; sent: number; failed: number; skipped: number; blocked: string | null }>('runFinInstExpireNow'),
  sendFineNotify: call<{ fineIds: number[]; resend?: boolean }, { requested: number; sent: number; failed: number; skipped: string[]; errors: string[] }>('sendFineNotify'),

  // A↔E 입금 ↔ 계산서 연결(#12 6번)
  getDepositMatch: call<{ bankTxId: number; q?: string }, DepositMatchResult>('getDepositMatch'),
  matchDeposit: call<{ bankTxId: number; invoiceId: number; amount?: string }, { ok: true; matchId: number; amount: string }>('matchDeposit'),
  unmatchDeposit: call<{ matchId: number }, { ok: true }>('unmatchDeposit'),
  // 계산서 → 버스인웹 미수 반영(5-G 미수 해소) — 스위치는 버스인웹 전환 뒤에 연다
  previewMisuSync: call<Record<string, never>, MisuSyncPreview>('previewMisuSync'),
  commitMisuSync: call<{ bill: number; paid: number }, { bill: number; paid: number; written: number }>('commitMisuSync'),
  setMisuSync: call<{ open: boolean }, { open: boolean }>('setMisuSync'),
  autoMatchDeposits: call<{ dateFrom: string; dateTo: string; corpId?: number | null; commit?: boolean }, AutoMatchResult>('autoMatchDeposits'),
  setInvoiceStatus: call<{ id: number; issuer?: string | null; issueDate?: string | null; paidDate?: string | null }, { ok: true }>('setInvoiceStatus'),
  setInvoiceIssuerBulk: call<{ ids: number[]; issuer?: string; issueDate?: string | null; self?: boolean },{ ok: true; updated: number; skipped: number }>('setInvoiceIssuerBulk'),
  amendInvoice: call<{ id: number; writtenDate?: string },{ ok: true; id: number }>('amendInvoice'),
  deleteInvoice: call<{ id: number }, { ok: true }>('deleteInvoice'),
  acctCancelDriverInvoice: call<{ id: number; reason?: string }, { ok: true }>('acctCancelDriverInvoice'),
  previewInvoiceImport: call<CashImportArgs, { errors: string[]; summary: InvoiceImportSummary[] }>('previewInvoiceImport'),
  commitInvoiceImport: call<CashImportArgs, { ok: true; added: number; removed: number; updated: number; conflicts: string[]; conflictCount: number }>('commitInvoiceImport'),

  // 회계 1단계 D — 유동성 시재(#20). `accounting`(+admin) 전용.
  getLiquidity: call<{ month: string; corpId?: number | null }, LiquidityResult>('getLiquidity'),
  saveCashCount: call<{ corpId: number; date: string; amount: string | null; memo?: string; kind?: 'cash' | 'bank' }, { ok: true; removed: boolean }>('saveCashCount'),
  listBankInPlans: call<{ corpId?: number | null; accountId?: number | null; from?: string; to?: string }, { rows: BankInPlanRow[] }>('listBankInPlans'),
  saveBankInPlan: call<{ id?: number | null; corpId: number; accountId: number | null; date: string; amount: string; memo?: string }, { id: number }>('saveBankInPlan'),
  deleteBankInPlan: call<{ id: number }, { ok: true }>('deleteBankInPlan'),
  setBillForecastDate: call<{ misuId: number; date: string | null; memo?: string; autoDate?: string | null }, { ok: true; cleared: boolean }>('setBillForecastDate'),
  listBillForecastAdjust: call<{ misuId: number }, { rows: BillForecastAdjustRow[] }>('listBillForecastAdjust'),
  listCashCounts: call<{ corpId?: number | null }, { rows: { cash_count_id: number; corp_name: string; count_date: string; amount: string; kind: 'cash' | 'bank'; memo: string | null; by: string | null }[] }>('listCashCounts'),

  // 경영 분석 — 월 수익·추세(10-24 · 시트 주요정산계정추세분석·수익TOOL 이관). 서버 `functions/src/profit.ts`.
  listProfitTrend: call<Record<string, never>, { rows: ProfitMonthRow[]; targets: { id: number; yr: number; amt: string }[]; latestSalaryMon: string | null }>('listProfitTrend'),
  getProfitMonth: call<{ mon: string }, ProfitMonthResult>('getProfitMonth'),
  saveProfitManual: call<{ mon: string; values: Partial<Record<ProfitManualItem, number>> }, { ok: true }>('saveProfitManual'),
  saveProfitMonth: call<{ mon: string; note?: string }, { mon: string; cutAt: string; metrics: Record<string, number> }>('saveProfitMonth'),
  saveProfitTarget: call<{ yr?: number; amt?: number; deleteId?: number }, { ok: true }>('saveProfitTarget'),
  getProfitCust: call<{ mon: string }, { mon: string; prevMon: string; cur: ProfitCustRow[]; prev: ProfitCustRow[] }>('getProfitCust'),
  listProfitMisu: call<Record<string, never>, { rows: ProfitMisuRow[] }>('listProfitMisu'),
  listProfitIdle: call<{ mon: string }, { mon: string; rows: ProfitIdleRow[]; checked: number }>('listProfitIdle'),
  listProfitRoute: call<{ mon: string }, { mon: string; rows: ProfitRouteRow[] }>('listProfitRoute'),
  listProfitInst: call<{ mon: string }, { mon: string; payMon: string; rows: ProfitInstRow[] }>('listProfitInst'),
  listProfitMinus: call<{ mon: string }, { mon: string; rows: ProfitMinusRow[] }>('listProfitMinus'),
  getProfitDirect: call<{ mon: string }, { mon: string; saved: boolean; source: 'sheet' | 'web' | 'live'; cutAt: string | null; rows: ProfitDirectRow[] }>('getProfitDirect'),
  listProfitRepair: call<{ yr: number }, { yr: number; rows: { mon: string; car_no: string; amt: string }[]; months: { mon: string; source: 'sheet' | 'web' | 'live' }[] }>('listProfitRepair'),
  // 거래처 추세(10-27) — 전 직원. 서버 `functions/src/profitCustTrend.ts`(매일 05:30 다시 센 보관 표를 읽는다).
  listProfitCustTrend: call<Record<string, never>, { months: string[]; cur: string; computedAt: string | null; firstMon: string | null; lastMon: string | null; groups: CustTrendGroup[] }>('listProfitCustTrend'),
  getProfitCustTrend: call<{ cust: string }, { cust: string; cur: string; rows: CustTrendRow[] }>('getProfitCustTrend'),
  // 거래처 묶음 고치기(10-28) — 목록은 전 직원 · 저장은 화면 권한 profit 입력
  listProfitCustNames: call<Record<string, never>, { rows: { raw_name: string; grp: string | null; last_mon: string }[] }>('listProfitCustNames'),
  saveProfitCustGroup: call<{ rawName: string; group: string }, { ok: true; group: string }>('saveProfitCustGroup'),
  // 회계 — 가불금·블랙리스트(10-23 · 시트 가불금_블랙리스트 이관). 서버 `functions/src/gabul.ts`.
  listGabulMonths: call<Record<string, never>, { rows: GabulMonthRow[]; latestSalaryMon: string | null }>('listGabulMonths'),
  getGabulMonth: call<{ mon: string }, GabulMonthResult>('getGabulMonth'),
  saveGabulMonth: call<{ mon: string; note?: string }, { mon: string; cutAt: string; total: number; owners: number; listed: number; count: Record<string, number> }>('saveGabulMonth'),
  listGabulSms: call<{ mon: string }, { mon: string; saved: boolean; rows: GabulSmsRow[] }>('listGabulSms'),
  reserveGabulSms: call<{ mon: string; sendAt: string; ownerIds: number[] }, { mon: string; sendAt: string; reserved: number; skipped: { ownerId: number; ownerName: string | null; reason: string }[] }>('reserveGabulSms'),
  listGabulSmsJobs: call<{ mon: string }, { mon: string; rows: GabulSmsJob[] }>('listGabulSmsJobs'),
  cancelGabulSms: call<{ ids: number[] }, { cancelled: number }>('cancelGabulSms'),
  listGabulPrices: call<{ baseMon?: string }, {
    bases: { base_mon: string; n: number }[]; base: string | null;
    rows: { id: number; car_name: string; car_year: number; price: string }[];
    numbers: { grp_name: string; price: string; updated_by: string | null; updated_at: string | null }[];
    missing: { car_name: string | null; n: number }[];
  }>('listGabulPrices'),
  saveGabulPrice: call<{ baseMon: string; carName: string; carYear: number; price: string }, { id: number; updated: boolean }>('saveGabulPrice'),
  deleteGabulPrice: call<{ id: number }, { ok: true }>('deleteGabulPrice'),
  copyGabulPrices: call<{ from: string; to: string }, { copied: number }>('copyGabulPrices'),
  saveGabulNumberPrice: call<{ grpName: string; price: string }, { ok: true }>('saveGabulNumberPrice'),
};

export interface GabulMonthRow {
  mon: string; total: string | null; owner_cnt: number | null;
  cnt_a: number | null; cnt_b: number | null; cnt_c: number | null; cnt_d: number | null; cnt_up: number | null;
  source: 'sheet' | 'web'; cut_at: string | null; note: string | null; created_by: string | null;
}
/** 블랙리스트 한 줄 — 굳힌 달(gabul_month_bl, 금액 문자열)과 계산 미리보기(GabulLine, 숫자)를 화면이 같은 모양으로 다룬다. */
export interface GabulBlRow {
  grade: string; owner_name: string | null; car_no: string | null; grp_name: string | null; car_name: string | null; car_year: number | null;
  residual: number; car_amt: number; owner_amt: number; inst_remain: number; est_price: number; number_value: number; memo?: string | null;
}
export interface GabulSmsRow { ownerId: number | null; ownerName: string | null; amt: number; net: number | null; phone: string | null; message: string | null; bytes: number }
export interface GabulSmsJob {
  id: number; owner_id: number; owner_name: string | null; phone: string; message: string; send_at: string;
  status: '대기' | '보내는중' | '보냄' | '실패' | '취소'; sent_at: string | null; error: string | null; created_by: string | null; cancelled_by: string | null;
}
export type GabulMonthResult =
  | { mon: string; saved: true; source: 'sheet' | 'web'; cutAt: string | null; total: number; savedBy: string | null;
      bl: (Omit<GabulBlRow, 'residual' | 'car_amt' | 'owner_amt' | 'inst_remain' | 'est_price' | 'number_value'> & Record<'residual' | 'car_amt' | 'owner_amt' | 'inst_remain' | 'est_price' | 'number_value', string | null>)[];
      owners: { owner_id: number | null; owner_name: string | null; amt: string; net: string | null }[] }
  | { mon: string; saved: false; source: 'live'; cutAt: string; total: number; count: Record<string, number>;
      lines: { ownerName: string | null; carNo: string | null; grpName: string | null; carName: string | null; carYear: number | null;
        carAmt: number; ownerAmt: number; instRemain: number; estPrice: number; numberValue: number; residual: number; grade: string | null; listed: boolean }[];
      owners: { ownerId: number; ownerName: string | null; amt: number; net: number | null }[] };

type Money = string | null;
export interface LiquidityDay {
  date: string;
  bankOpen: Money; bankIn: Money; bankOut: Money; bankClose: Money; bankActual: Money; bankDiff: Money; bankOutRef: Money; bankInTransfer: Money; bankInPlan: Money; bankInForecast: Money;
  cashOpen: Money; cashIn: Money; cashOut: Money; cashClose: Money; cashCount: Money;
}
/** 수기 입금(추정, #351). counted = 유동성 시재에 들어가는가(그 법인 은행 거래내역 마지막 날 뒤). */
export interface BankInPlanRow {
  bank_in_plan_id: number; corp_id: number; corp_name: string; plan_date: string; amount: string; memo: string | null; by: string | null;
  /** 입금될 계좌(#472) — 옛 줄은 null. */
  bank_account_id: number | null; bank: string | null; account_no: string | null; account_alias: string | null;
  last_tx_date: string | null; counted: boolean;
}
/** 입금 예측(청구) 한 건 = 청구(result_misu) 한 줄(#435). date = 조정 > 자동. */
export interface BillForecastItem {
  misuId: number; recvId: number; driveMon: string; corpId: number; cust: string; reqDate: string; amt: string;
  auto: string | null; date: string; adj: { date: string; by: string | null; at: string | null; memo: string | null } | null;
}
export interface BillForecastAdjustRow {
  adjust_id: number; plan_date: string; auto_date: string | null; memo: string | null; is_deleted: boolean;
  created_by: string | null; created_at: string; updated_by: string | null; updated_at: string | null;
}
export interface LiquidityResult {
  month: string; corpId: number | null; lastDataDate: string | null; firstDataDate: string | null; openBank: Money; openCash: Money;
  days: LiquidityDay[];
  /** 입금 예측(청구 · #435) 날짜별 거래처·금액 — 「(참고) 입금 예측」 칸 풍선 도움말. 옛 서버면 없다. */
  forecast?: Record<string, BillForecastItem[]>;
  /** 예측일이 오늘 전인데 입금 안 된 청구 — 앞날 입금엔 안 넣고 따로 보인다(일자조정으로 다시 날짜를 줄 수 있다). */
  forecastOverdue?: BillForecastItem[];
  accounts: { name: string; cash: boolean; excluded: boolean; total: Money; byDay: Record<string, Money> }[];
}

export interface InvoiceCandidate {
  recvId: number; driveMon: string; reqStatus: string; reqDate: string | null; recvKind: string | null; requester: string | null;
  custId: number | null; custName: string; bizNumber: string | null; email: string | null; method: string | null;
  corpId: number | null; corpName: string | null; grpName: string | null; problem: string | null; supply: string; vat: string; total: string;
}
export interface InvoiceRow {
  invoice_id: string; corp_id: number; corp_name: string; inv_year: number; series: string; seq: number; invoice_no: string; amend_of: string | null;
  recv_id: number | null; drive_mon: string | null; recv_kind: string | null; requester: string | null; cust_id: number | null; cust_name: string;
  biz_number: string | null; email: string | null; written_date: string | null; issue_date: string | null;
  supply: string; vat: string; total: string; item: string | null; memo: string | null; issuer: string | null; paid_date: string | null;
  corp_remark: string | null; remark1?: string | null; smartbill_at: string | null; paid_by_match?: boolean;
  /** #232 접수의 청구방법 · #234 이어진 발행 요청의 발행방식. */
  recv_method?: string | null; issue_way?: string | null;
  /** #134 — 품목 비고(스마트빌 30. 비고) · 참조자 2(5·6번). */
  item_note?: string | null; cc_name?: string | null; cc_email?: string | null;
  source: 'busin' | 'manual' | 'sheet' | 'driver'; by: string | null;
  from_app?: boolean; attach_name?: string | null; phone?: string | null; dists?: string | null;
  /** #404 기사 앱 신청분의 접수일(신청한 날 · created_at). 시트에서 옮겨 온 건·회사오더는 없음. */
  received_date?: string | null;
  /** 시트로 신청한 기사 계산서의 사업자등록증 구글드라이브 링크(#127). */
  attach_url?: string | null;
  notified_at?: string | null; // ⑤ 발행 알림톡 마지막 성공 시각
  /** 이 계산서로 이어진 발행 요청 번호(#133 「발행요청건」 표시) — 없으면 null. */
  request_id?: number | null;
  /** 요청일자 — 원 줄 = 발행 요청 올린 날 · 수정(−) 줄 = 영업 취소·수정 요청 올린 날(10-27 way). */
  request_date?: string | null;
  /** #206 회계팀이 기사 대신 신청한 계산서 · 매월 고정발행으로 만든 계산서(규칙 번호·달). */
  by_acct?: boolean; fixed_id?: number | null; fixed_month?: string | null;
  /** 다른 기사가 대신 신청(#332 · #258 차주 대행) — 넣은 기사 이름. 회계 「대리」(by_acct)와 다르다. */
  by_driver?: string | null;
}
export interface InvoiceImportSummary { tab: string; corpName: string; rows: number; add: number; keep: number; remove: number; skipped: number; total: string; lastNo: string }
export interface DriverImportTab {
  tab: string; corpName: string; numbers: number; added: number; updated: number; unchanged: number;
  amended: number; removed: number; dropped: number; skipped: string[]; conflicts: string[];
}
export interface CashImportArgs { fileName?: string; tabs: { name: string; rows: BankCell[][] }[] }
export interface CashImportSummary {
  tab: string; corpName: string; from: string | null; to: string | null; rows: number;
  add: number; keep: number; remove: number; skipped: number; truncated: number; amount: string;
}
export interface CashBookInput {
  corpId: number; method?: string; cashAccount?: string; vendor?: string; summary?: string; amount: string; evidence?: boolean | null; memo?: string;
  deduct?: boolean; vatSeparate?: boolean; deductAmount?: string | null; deductPlan?: { month: string; amount: string }[] | null; deductDone?: boolean;
  carAllocId?: number | null; deductAccount?: string | null;
  /** 공제 정산적용월(#57) — null = 자동(지급일 6일~5일) · '' = 정산월 없음 · YYYY-MM. */
  deductMon?: string | null;
}
export interface CashBookRow {
  cash_book_id: number; corp_id: number; corp_name: string; pay_date: string; method: string | null; cash_account: string | null;
  vendor: string | null; summary: string | null; amount: string; evidence: boolean | null; memo: string | null;
  deduct: boolean; vat_separate: boolean; deduct_amount: string | null; deduct_plan: { month: string; amount: string }[] | null; deduct_done: boolean;
  rule_id: number | null; rule_month: string | null; source: 'web' | 'sheet' | 'ops'; updated_by: string | null; updated_at: string;
  /** DYOPS 직영수리비 자동 줄(source ops · source_key repair:…) — 「DY」 대신 「수리비 자동」 */
  repair_auto?: boolean;
  /** 손입력 줄에 연결만 된 직영수리비 기안 번호(#312). */
  repair_linked?: string | null;
  /** DYOPS 경조금 줄(10-22) — created = 자동으로 넣은 줄 · linked = 회계 손입력 줄에 연결만 · paid = DYOPS 대장 지급 사본. */
  condolence?: { id: string; mode: 'created' | 'linked'; paid: boolean; paidDate: string | null; via: string | null } | null;
  /** 재무추가 바로 공제 — 편성·계정 · 넣은 정산월 「2026-10 100000.00, …」(없으면 null) */
  car_alloc_id: number | null; deduct_account: string | null; alloc_label: string | null; deducted: string | null; deducted_ids: string | null;
  deduct_mon: string | null;
  /** B/ 미공제 사유(#459) — 적은 줄은 미공제 창에 안 뜨고 목록 공제 칸에 「미공제 사유」로 보인다. */
  b_skip_reason?: string | null; b_skip_by?: string | null; b_skip_at?: string | null;
}
/** 출납일보 공제 ↔ 재무추가 일자별 대조(functions/src/cashbookDeduct.ts). */
export interface CashDeductCheck {
  open: boolean;
  days: { pay_date: string; corp_id: number; corp_name: string; n: number; cash: string; linked: string; pending_n: number; pending: string;
    manual_n: number; broken_n: number; direct_n: number; direct: string }[];
  problems: { cash_book_id: string; pay_date: string; corp_name: string; vendor: string | null; summary: string | null; deduct_amount: string;
    kind: 'pending' | 'deleted' | 'changed'; drive_mon: string | null; result_daily_id: number | null; ready: boolean }[];
}
export interface CashRuleRow {
  rule_id: number; corp_id: number; corp_name: string; day_of_month: number | null; method: string | null; cash_account: string | null;
  /** #39 — 'day' 매월 며칠 · 'weekday' 매월 N째(5 = 마지막) 요일(0 = 일). months = 나가는 달, null = 매월. */
  cycle: 'day' | 'weekday'; week_of_month: number | null; weekday: number | null; months: number[] | null;
  vendor: string | null; summary: string | null; amount: string; evidence: boolean | null; memo: string | null;
  deduct: boolean; vat_separate: boolean; start_month: string; end_month: string | null; is_active: boolean; last_month: string | null;
  /** #447 — 적요가 적은 그대로 들어가는 달(그 뒤 달은 「N월」을 민다). 서버 RULE_BASE_SQL. */
  summary_base?: string | null;
}
export interface CashRulePlanItem {
  rule_id: number; corp_name: string; pay_date: string; vendor: string | null; summary: string | null; amount: string; exists: boolean;
  /** 규칙대로 센 날 — 주말·공휴일이면 pay_date 가 다음 영업일(#159). */
  base_date?: string;
}

type Row = Record<string, unknown>;
export type BankCell = string | number | boolean | null;
export interface BankUploadArgs { fileName?: string; sheet: BankCell[][] }
export interface BankUploadResult {
  kind: 'account' | 'tx' | 'balance';
  errors: string[]; errorCount: number; warnings: string[];
  summary: { total: number; new: number; dup?: number; same?: number; changed?: number; overwrite?: number; dateFrom?: string | null; dateTo?: string | null };
  sample?: { corpName: string; bank: string; accountNo: string; product: string; alias: string; status: 'new' | 'changed' }[];
}
export interface BankUploadRow {
  bank_upload_id: number; kind: 'account' | 'tx' | 'balance'; file_name: string | null; rows_total: number; rows_new: number; rows_dup: number;
  date_from: string | null; date_to: string | null; warnings: string | null; is_deleted: boolean; created_by: string | null; created_at: string;
}
/** 기사접수 시트 새 신청 자동 가져오기(10분)의 마지막 결과 — 두 화면이 보여 준다(#164). */
export interface DriverSheetSync { at: string | null; ok: boolean | null; note: string | null }
export type DriverOrderKind = '페이앱' | '카드단말기' | '현금영수증' | '다수배분';
/** #206 회계 대리 신청 — 기사 고르기 결과(지금 운행 중 편성 · 그 기사의 법인). */
export interface ProxyDriver { driverId: number; label: string; grpName: string | null; corps: { corp_id: number; short_name: string }[] }
export interface ProxyCommon {
  driverId: number; corpId: number; custName?: string; bizNumber?: string; email?: string; phone?: string; requestDate?: string; memo?: string;
  attach?: { path: string; type: string; name: string } | null;
}
/** #206 매월 고정발행 규칙(칸 = 계산서 한 장 스냅숏). 금액은 문자열(numeric). */
export interface FixedRuleDraft {
  driverId: number | null; corpId: number; custName: string; bizNumber: string; email: string; phone: string; supply: string; vat: string; memo: string;
  dists: { driverId: number; amount: string; label?: string }[]; issueDay: number; startMonth: string; endMonth: string | null;
}
export interface FixedRuleRow {
  fixed_id: number; driver_id: number; driver_name: string | null; requester: string | null; corp_id: number; corp_name: string;
  cust_name: string; biz_number: string; email: string; phone: string | null; supply: string; vat: string; memo: string | null;
  dists: { driverId: number; amount: string }[]; issue_day: number; start_month: string; end_month: string | null; source_invoice_id: number | null;
  last_month: string | null; made_n: number; created_by: string | null; created_at: string; updated_by: string | null; updated_at: string | null;
}
export interface FixedRun {
  month: string; commit: boolean; create: number; skip: number; fail: number;
  items: { fixedId: number; driverId: number; requester: string | null; corpName: string; custName: string; supply: string; vat: string; writtenDate: string;
    action: 'create' | 'skip' | 'fail'; reason: string | null; invoiceNo: string | null }[];
}
export type DriverOrderStatus = 'active' | 'pending' | 'done' | 'paid' | 'cancelled' | 'all';
export interface DriverOrderLine { line_no: number; dist_type: string; dist_name: string | null; amount: string; exec_date: string | null; issuer: string | null; paid_date: string | null; cancelled: boolean; attach_url: string | null }
/** #407 카드단말기 재무추가 지급 줄 맞추기 결과(null = 할 일 없음). */
export type DriverOrderFinResult = { action: 'added' | 'moved' | 'removed' | 'kept'; driveMon?: string; amount?: string; resultDailyId?: number; note?: string; summary?: string } | null;
export interface DriverOrderRow {
  order_id: string; kind: DriverOrderKind; corp_id: number; corp_name: string; order_no: string; requester: string | null; cust_name: string | null;
  biz_number: string | null; cash_id: string | null; received_date: string | null; request_date: string | null; supply: string; vat: string; amount: string;
  email: string | null; phone: string | null; attach_url: string | null; attach_name: string | null; memo: string | null; issuer: string | null;
  paid_date: string | null; settle_mon: string | null; cancel_state: '신청취소' | '취소' | null; source: 'sheet' | 'app'; from_app: boolean; by: string | null;
  lines: DriverOrderLine[]; notified_at?: string | null;
  /** #407 카드단말기 지급금액 → 재무추가 지급 줄(있으면). */
  fin?: { result_daily_id: number; drive_mon: string; amount: string } | null;
  /** #516 다수배분 → 재무추가 공제·지급 줄 수(없으면 null). */
  dasu_fin?: { drive_mon: string; deduct: number; pay: number } | null;
  /** #208 실입금금액(수수료 뗀 뒤) — 비면 수수료 빈칸. */
  deposit_amount?: string | null;
  /** #206 회계팀이 기사 대신 등록. */
  by_acct?: boolean;
  by_driver?: string | null;
}
export type DriverNoticeTarget = 'invoice' | 'order';
export interface DriverNoticePreview {
  no: string; kind: string; requester: string | null; cancelled: boolean; amend: boolean; inHours: boolean; templateCode: string; preview: string;
  /** 지금 못 보내는 사유(현금영수증 = 새 문구 검수 전 · #370). */
  blockedWhy?: string | null;
  defaultPhone: string | null;
  candidates: { driver_id: number; driver_name: string | null; car_no: string | null; phone: string | null; car_match: boolean }[];
  history: { notify_id: string; created_at: string; created_by: string | null; phone: string; template_code: string; status: 'sent' | 'failed'; error: string | null;
    image_name: string | null; view_url: string | null }[];
}
export interface DriverOrderImportTab { tab: string; kind: DriverOrderKind; corpName: string; numbers: number; added: number; cancelled: number; unchanged: number; skipped: string[]; conflicts: string[] }
/** 보험(#135) 입력 한 줄 — 금액은 쉼표 없는 글자. */
export interface InsRowInput {
  corpId: number | null; unionName: string; carNo: string; driverName: string; kind: '갱신' | '신규'; startDate: string;
  totalAmount: string; firstAmount: string; ownDamage: string; memo?: string;
}
export interface InsSlotPreview {
  seq: number; dueDate: string; periodFrom: string; periodTo: string; amount: string; payDate: string; method: string; summary: string;
}
export interface InsPreviewRow {
  suggest: { unionName: string | null; corpId: number | null; driverName: string | null; allocLabel: string | null };
  schedule: InsSlotPreview[] | null; error: string | null; duplicate: boolean;
}
export interface InsInstallmentRow {
  id: number; policy_id: number; seq: number; due_date: string; period_from: string; period_to: string; amount: string; pay_date: string;
  cash_book_id: number | null; cash_pay_date: string | null; reflected: boolean; cash_removed: boolean;
  /** 출납일보에 이미 있던 줄(시트·손입력)에 연결만 한 회차. */
  cash_linked: boolean;
}
export interface InsPolicyRow {
  id: number; corp_id: number; corp_name: string; union_name: string; car_no: string; driver_name: string; kind: string;
  /** 분납자료로 만든 계약은 시작일·총액·1회차를 모른다(null). */
  start_date: string | null; total_amount: string | null; first_amount: string | null;
  own_damage: string | null; memo: string | null; created_by: string | null; created_at: string; contract_no: string | null;
  installments: InsInstallmentRow[];
}
export interface InsFileCounts { rows: number; newPolicies: number; newInstallments: number; matched: number; same: number; inCash: number; warnings: number; errors: number }
export interface InsFileItem {
  line: number; contractNo: string | null; carNo: string; seq: number; amount: string; periodFrom: string; dueDate: string;
  action: 'new' | 'add' | 'update' | 'same' | 'warn' | 'error'; message: string | null;
  corpName: string | null; unionName: string; driverName: string | null; cashMatchId: string | null; cashPayDate: string | null; reflected: boolean;
  before: { amount: string; periodFrom: string; dueDate: string } | null;
}
export interface InsFilePreview { items: InsFileItem[]; counts: InsFileCounts | null; errors: string[]; skipped: number }
export interface InsAccountRow { id: number; corp_id: number; corp_name: string; union_name: string; account: string; updated_by: string | null; updated_at: string }

export interface AcctCorp { corp_id: number; biz_number: string; name: string; short_name: string; invoice_code: number | null; invoice_remark?: string | null; parent_corp_id: number | null; parent_name: string | null }
export interface BankAccountRow {
  bank_account_id: number; corp_id: number; corp_name: string; bank: string; account_no: string; product: string | null; alias: string | null;
  currency: string; is_active: boolean; last_tx_date: string | null; last_bal_date: string | null;
  /** 기사매출 계좌(법인 기사 계산서 비고1 에 이 번호가 있다). */
  is_driver_acct?: boolean;
}
export interface BankBalanceRow {
  bankAccountId: number; corpId: number; corpName: string; parentName: string | null; bank: string; accountNo: string; alias: string | null;
  currency: string; isActive: boolean; amountIn: string | null; amountOut: string | null; balance: string | null; asOf: string | null;
  source: 'tx' | 'balance' | null; carried: boolean; mismatch: boolean; txBalance: string | null;
}
export interface BalanceCheck { corpId: number; corpName: string; checked: boolean; by: string | null; at: string | null }

export interface BankBalanceResult {
  date: string; rows: BankBalanceRow[];
  totals: { corpName: string | null; currency: string; n: number; amountIn: string; amountOut: string; balance: string }[];
}

/** 계정 마스터 축 — 지급 계정을 공제 칸에 넣으면 명세가 뒤집힌다. 수익비용 = 기사에게 안 가고 거래처 수익에서만 빠짐(company_cost). */
export type ReqAccountKind = '기사지급' | '기사공제' | '거래처청구' | '거래처공제' | '수익비용';

export type FileOwnerType = 'driver' | 'car' | 'cust' | 'grp';

export interface FileAssetItem {
  fileAssetId: number;
  kind: string;
  label: string;
  contentType: string;
  byteSize: number;
  createdAt: string;
}

/**
 * 서류 업로드·교체. 다운로드와 같은 이유로 callable 이 아니다 — JSON 이면 base64 로 1.33배 부푼다.
 *
 * ⚠️ 형식은 **서버가 파일 앞머리로 판정**한다. 확장자를 바꿔도 통과하지 않고, PNG/JPEG 만 받는다.
 */
export async function uploadFileAsset(
  ownerType: FileOwnerType, ownerId: number, kind: string, file: File,
): Promise<{ fileAssetId: number; label: string; byteSize: number; replaced: boolean }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const qs = new URLSearchParams({ ownerType, ownerId: String(ownerId), kind });
  const res = await fetch(`${base}/uploadFileAsset?${qs}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '서류를 등록하지 못했습니다.');
  return res.json();
}

/**
 * 서류 파일 바이트를 받아 화면에서 쓸 수 있는 blob URL 로.
 *
 * `<img src>` 로 직접 못 건다 — 이 엔드포인트는 **Authorization 헤더**를 요구한다.
 * 서명 URL 이었다면 헤더가 없어도 됐겠지만, 링크가 새면 만료까지 아무나 보게 된다.
 * 여기 있는 건 면허증·주민등록등본·통장 사본이라 그 편의를 택하지 않았다.
 *
 * 돌려준 URL 은 쓰고 나면 `URL.revokeObjectURL` 로 반드시 해제할 것(안 하면 탭이 메모리를 문다).
 */
export async function fetchFileAssetUrl(fileAssetId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadFileAsset?id=${fileAssetId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.text()) || '서류를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}

// ─────────────────────────────────────────────────────────────────────────────
// 개선요청 게시판
// ─────────────────────────────────────────────────────────────────────────────

export type ImproveStatus = 'requested' | 'reviewing' | 'in_progress' | 'done' | 'rejected';

/** 상태 라벨·색. 다른 프로젝트 게시판 넷과 **같은 다섯 값**을 쓴다(처리 도구가 같은 말을 쓴다). */
export const IMPROVE_STATUS: Record<ImproveStatus, { label: string; chip: string }> = {
  requested: { label: '요청', chip: 'bg-brand-50 text-brand-700 border-brand-100' },
  reviewing: { label: '검토', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: '반영중', chip: 'bg-brand-100 text-brand-700 border-brand-100' },
  done: { label: '완료', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: '반려', chip: 'bg-red-50 text-red-700 border-red-200' },
};

/** 아직 손이 필요한 상태 — 목록 기본값·메뉴 배지가 이 셋을 센다. */
export const IMPROVE_OPEN: ImproveStatus[] = ['requested', 'reviewing', 'in_progress'];

export interface ImproveListArgs {
  status?: ImproveStatus | '';
  mine?: boolean;
  menu?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ImproveRow {
  improvement_id: number;
  title: string;
  menu: string | null;
  status: ImproveStatus;
  visibility: 'all' | 'private';
  requester_email: string;
  requester_name: string | null;
  created_at: string;
  last_reply_at: string | null;
  last_other_at: string | null;
  history_count: number;
  /** 「새 답변」 — 서버가 계정 단위로 판정(09-29). */
  unread: boolean;
  file_count: number;
  link_count: number;
}

export interface ImproveHistory {
  improvement_history_id: number;
  status_to: ImproveStatus | null;
  comment: string | null;
  by_email: string;
  by_name: string | null;
  edited_at: string | null;
  edited_by: string | null;
  created_at: string;
}

export interface ImproveFile {
  improvement_file_id: number;
  kind: 'request' | 'comment' | 'result';
  history_id: number | null;
  file_name: string;
  content_type: string;
  byte_size: string;
  created_at: string;
  created_by: string | null;
}

export type ImproveLinkProvider = 'sheet' | 'doc' | 'slide' | 'form' | 'drive' | 'link';

/** 링크 첨부. 첨부파일과 세 축(`kind`·`history_id`)은 같고, 바이트 대신 주소를 쥔다. */
export interface ImproveLink {
  improvement_link_id: number;
  kind: 'request' | 'comment' | 'result';
  history_id: number | null;
  link_url: string;
  link_title: string;
  provider: ImproveLinkProvider;
  created_at: string;
  created_by: string | null;
}

export interface ImproveMe {
  email: string;
  name: string | null;
  isAdmin: boolean;
}

export interface ImproveListResult {
  rows: ImproveRow[];
  counts: Partial<Record<ImproveStatus, number>>;
  me: ImproveMe;
}

export interface ImproveDetail {
  row:
    | (ImproveRow & { content: string; result_note: string | null; updated_at: string | null })
    | null;
  history: ImproveHistory[];
  files: ImproveFile[];
  links: ImproveLink[];
  me?: ImproveMe;
}

/**
 * 게시판 첨부 업로드. 서류(`uploadFileAsset`)와 같은 이유로 callable 이 아니다.
 *
 * ⚠️ 형식은 **서버가 앞머리 바이트로** 본다. 이미지·PDF 가 아니면 열람 때 내려받기로 처리된다
 *    (올린 HTML 이 우리 도메인에서 실행되지 않게).
 */
export async function uploadImprovementFile(
  improvementId: number,
  kind: 'request' | 'comment' | 'result',
  file: File,
  historyId?: number,
): Promise<{ improvementFileId: number; fileName: string; byteSize: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const qs = new URLSearchParams({ id: String(improvementId), kind, name: file.name });
  if (historyId) qs.set('historyId', String(historyId));
  const res = await fetch(`${base}/uploadImprovementFile?${qs}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 올리지 못했습니다.');
  return res.json();
}

/** 첨부 열람용 blob URL. 쓰고 나면 `URL.revokeObjectURL` 로 해제할 것. */
/** 기사 앱 신청 계산서의 첨부(사업자등록증) — blob URL. 쓰고 나면 revoke. */
export async function fetchDriverInvoiceFileUrl(invoiceId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadDriverInvoiceFile?id=${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}

/** 공지 본문 사진 — 올리면 본문 `<img src>` 에 넣을 상대 주소(`/notice-img?k=…`)를 준다. */
export async function uploadNoticeImage(file: Blob): Promise<{ url: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/uploadNoticeImage`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '사진을 올리지 못했습니다.');
  return res.json();
}

/** ⑤ 발행 알림톡에 붙일 계산서 이미지(JPG·PNG·PDF)를 올린다 → 보낼 때 그 경로를 싣는다. */
/** #408 스마트빌 여러 장 계산서 PDF → 쪽마다 관리번호로 기사 계산서와 짝짓기(ready 줄엔 그 쪽만 떼어 올린 파일). */
export interface DriverInvoicePdfRow {
  page: number; no: string | null; approval: string | null; buyerBiz: string | null; total: number | null; writtenDate: string | null;
  state: 'ready' | 'skip'; reason: string | null; invoiceId: number | null; custName: string | null; requester: string | null;
  listTotal: string | null; phone: string | null; image: { path: string; type: string; name: string } | null;
}
export async function uploadDriverInvoicePdf(file: File): Promise<{ rows: DriverInvoicePdfRow[]; inHours: boolean }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/uploadDriverInvoicePdf`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '파일을 올리지 못했습니다.');
  return res.json();
}

export async function uploadDriverNoticeImage(file: File): Promise<{ path: string; type: string; name: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/uploadDriverNoticeImage?${new URLSearchParams({ name: file.name })}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '이미지를 올리지 못했습니다.');
  return res.json();
}

/** #206 회계 화면에서 기사 대신 신청할 때의 첨부 — 그 기사 칸에 올린다(돌려준 값을 신청에 싣는다). */
/** 회계 화면에서 기사 신청 첨부 올리기 — 대신 등록은 기사 번호, 이미 들어온 신청(#490)은 `{ order }` 로 그 신청 기사 칸에. */
export async function uploadAcctDriverFile(target: number | { order: number }, file: File): Promise<{ path: string; type: string; name: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/uploadAcctDriverFile?${new URLSearchParams({ ...(typeof target === 'number' ? { driverId: String(target) } : { order: String(target.order) }), name: file.name })}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '파일을 올리지 못했습니다.');
  return res.json();
}

/** 기사 앱 4종 신청(④)의 첨부 — blob URL. 쓰고 나면 revoke. */
export async function fetchDriverOrderFileUrl(orderId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadDriverInvoiceFile?order=${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}

export async function fetchImprovementFileUrl(fileId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadImprovementFile?fileId=${fileId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}

/** 입금 ↔ 계산서 연결(#12 6번). 금액은 numeric 문자열. */
export interface DepositMatchInvoice {
  invoice_id: string; corp_id: number; corp_name: string; invoice_no: string; cust_name: string; biz_number: string | null;
  drive_mon: string | null; requester: string | null; written_date: string | null; issuer: string | null;
  net: string; applied: string; remaining: string; score: number; is_driver: boolean;
  /** 손으로 적은 입금일자(이 입금일 근처라 연결할 수 있는 것 · #154). */
  paid_date: string | null; paid_by_match: boolean;
}
export interface DepositMatchResult {
  deposit: { bank_tx_id: string; tx_date: string; memo: string | null; summary: string | null; amount_in: string; currency: string;
    corp_name: string; bank: string; account_no: string; matched: string; rest: string; is_driver_acct: boolean };
  links: { match_id: string; invoice_id: string; amount: string; auto: boolean; invoice_no: string; cust_name: string; created_at: string; created_by: string | null; inv_rest: string }[];
  candidates: DepositMatchInvoice[];
}
export interface AutoMatchResult {
  deposits: number; committed?: number;
  pairs: { bankTxId: number; invoiceId: number; amount: string; txDate: string; memo: string | null; corpName: string; invoiceNo: string; custName: string; writtenDate: string | null }[];
}

/** 입금 구분(입금시트 「입금체크」 색) — 서버 `bank.ts` DEP_KINDS 와 같다. */
export interface BankTxFinInfo {
  open: boolean;
  /** 넣을 수 없는 이유(지급 줄·구분이 재무추가 아님 등) — 이미 넣은 줄이면 null. */
  blockedReason: string | null;
  tx: { tx_date: string; amount_in: string; memo: string | null; slip_note: string | null } | null;
  defaultMon: string | null; comment: string | null; plate: string | null;
  accounts: string[]; finishedMons: string[];
  link: {
    link_id: string; drive_mon: string; amount: string; result_daily_id: number; created_by: string | null; created_at: string;
    rd_deleted: boolean | null; rd_amount: string | null; car_alloc_id: number | null; account_name: string | null;
    car_no: string | null; driver_name: string | null; owner_name: string | null;
  } | null;
}
export type DepKind = 'company' | 'driver' | 'finance' | 'vehicle' | 'purchase' | 'check' | 'other';

// ── 과태료·통행료(#21) — 서버 `fine.ts` ──────────────────────────────────────
export type FineState = 'all' | 'check' | 'unpaid' | 'todeduct' | 'deducted' | 'skip';
/** 과태료 기간 기준(#129) — 위반일 · 납부기한 · 납부일자. */
export type FineDateBy = 'violation' | 'due' | 'paid' | 'created';
export interface FineListArgs {
  from?: string; to?: string; dateBy?: FineDateBy; q?: string; state?: FineState; kind?: string; deductMon?: string; corpId?: number | null;
  limit?: number; offset?: number; sort?: string; dir?: 'asc' | 'desc';
  /** 칸별 거르기(#191) — 칸 key → 검색어 */
  filters?: Record<string, string>;
}
/** 과태료 편성 찾기(#191) — 그 차의 편성 전부 또는 검색 결과. `on_date` = 위반일에 걸친 편성 · `acct` = 회계·차량계정 편성. */
export interface FineAllocHit {
  car_alloc_id: number; car_no: string | null; driver_name: string | null; driver_kind: string | null; owner_name: string | null;
  grp_name: string | null; startdt: string | null; enddt: string | null; is_use: boolean | null; on_date: boolean; acct: boolean;
}
/** 「이미 공제함」 후보 — 같은 차·기사·금액의 재무추가 공제 줄(#221). */
export interface FineLinkCandidate {
  result_daily_id: number; drive_mon: string; account_name: string | null; amount: string; driver_name: string | null; car_no: string | null;
  create_method: string | null; comment: string | null; created_at: string | null;
}

export interface FineRow {
  fine_id: number; corp_name: string | null; kind: string; issuer: string | null; car_no: string; violation_date: string;
  violation_time: string | null; place: string | null; content: string | null; giro_no: string | null; notice_no: string | null;
  due_date: string | null; amount: string; photo_url: string | null; memo: string | null; paid_date: string | null;
  deduct_mon: string | null; eff_deduct_mon: string | null; deduct_state: 'none' | 'done' | 'skip'; deduct_at: string | null; deduct_by: string | null; result_daily_id: number | null;
  /** 출납일보·재무추가 줄에 「이미 공제함」으로 연결만 한 고지서(#221) — 되돌려도 그 줄은 안 지운다. */
  rd_linked: boolean | null;
  match_state: 'ok' | 'check' | 'confirmed'; check_reason: string | null; match_source: 'db' | 'sheet' | 'live' | 'manual' | null;
  car_alloc_id: number | null; driver_id: number | null; owner_id: number | null; driver_name: string | null; owner_name: string | null;
  grp_name: string | null; driver_kind: string | null; sales_app_user_id: number | null; sales_name: string | null; sales_email: string | null;
  sales_note: string | null; confirmed_by: string | null; confirmed_at: string | null; source: 'web' | 'sheet'; sms_sent_date: string | null;
  created_at: string; updated_by: string | null;
  has_photo: boolean; photo_name: string | null;
  last_notify: { status: 'sent' | 'failed'; at: string; to: string | null; error: string | null } | null;
  /** 회계 목록만(#169) — 같은 고지서가 또 있음 · 차주 이월잔액(음수 = 마이너스 기사) */
  dup?: boolean; owner_remain?: string | null;
}
export interface FineMatchCounts { ok: number; check: number; a: number; live: 'skipped' | 'used' | 'failed' }
export interface FineImportResult {
  commit: boolean; fileName: string | null; total: number; added: number; filled: number; same: number; deletedKept: number;
  skipped: string[]; skippedCount: number; match: FineMatchCounts; dupCount?: number; dups?: string[];
}
export type FinePasteFormat = '경찰청' | '세외수입' | '통행료';
export interface FinePasteResult extends FineImportResult {
  format: FinePasteFormat | null;
  sample: { kind: string; carNo: string; violationDate: string; violationTime: string | null; place: string | null; content: string | null;
    amount: string; noticeNo: string | null; dueDate: string | null; issuer: string | null }[];
}
/** 통근 월정산(#51·#52) — `functions/src/commuteMonth.ts`. 금액은 문자열(numeric). */
export interface CommuteOverview {
  driveMon: string; prevMon: string; closed: string[];
  /** 새 노선 승인 스위치(미팅 #30). */
  gate: boolean;
  rows: { recv_id: number; cust_name: string | null; made_cnt: number; prev_missing: number; cover_missing: number;
    line_cnt: number; filled_cnt: number; driver_amt: string;
    /** 지난달 노선 수 · 이 달 노선 중 지난달에 없던 것 · 승인 대기(요청·승인, 아직 줄 없음) 노선 수. */
    prev_cnt: number; new_cnt: number; pending_cnt: number }[];
}
/** 승인 대기로 빠진 새 노선(만들지 않았다). */
export interface CommuteHeld { dispt_id: number; dispt_name: string | null }
export interface CommuteRouteRequest {
  request_id: number; dispt_ids: number[]; dispt_names: string | null; reason: string;
  status: '요청' | '승인' | '반려' | '취소' | '반영'; draft_doc_id: string | null; approver1: string | null; approver2: string | null;
  decided_note: string | null; check_error: string | null; applied_routes: number | null; applied_lines: number | null;
  created_by: string; created_at: string; decided_at: string | null; checked_at: string | null;
}
export interface CommuteRoute {
  dispt_id: number; dispt_name: string | null; start_time: string | null; is_shuttle: boolean | null; car_cnt: number | null;
  result_dispt_id: number | null; cost_cust: string | null; cost_cust_cnt: number | null; route_cost_driver: string | null;
  /** 청구 부가세 포함 단가(#247) — 비면 포함가 없이 공급가로만 청구. */
  cost_cust_add_tax: string | null;
  made: boolean; made_prev: boolean; cover_cnt: number;
  /** 이 달에 만들었다가 뺀 노선(자동 반영이 다시 안 넣는다) · 노선 이름 날짜가 다른 달(자동에서 뺐다). */
  removed: boolean; name_other: boolean;
  /** 새 노선 승인이 필요(스위치 켜짐 + 지난달에 없음) · 이 노선의 마지막 승인 요청 상태. */
  need_ok: boolean; req_status: CommuteRouteRequest['status'] | null;
}
export interface CommuteReloadDiff {
  closed: string[];
  addRoutes: { dispt_id: number; dispt_name: string | null; cover_cnt: number; made_prev: boolean; removed: boolean; name_other: boolean }[];
  addLines: { dispt_car_sch_id: number; dispt_id: number; dispt_name: string | null; driver_name: string | null; car_no: string | null;
    start_day: string | null; end_day: string | null; removed: boolean; route_new: boolean }[];
  removeLines: { id: number; dispt_id: number; dispt_name: string | null; driver_name: string | null; car_no: string | null; reason: string }[];
  removeRoutes: { dispt_id: number; dispt_name: string | null; reason: string; line_cnt: number }[];
  /** apply 결과 — 승인 대기로 빠진 새 노선. */
  held?: CommuteHeld[];
}
export interface CommuteLine {
  id: number; dispt_id: number; dispt_car_sch_id: number | null; car_alloc_id: number | null; driver_id: number | null;
  driver_name: string | null; driver_kind: string | null; car_no: string | null; owner_name: string | null; grp_name: string | null;
  cost_driver: string | null; cost_driver_cnt: number | null; amount: string;
}
export interface CommuteMonth {
  recvId: number; custName: string | null; driveMon: string; closed: string[];
  routes: CommuteRoute[]; lines: CommuteLine[];
  candidates: { dispt_car_sch_id: number; dispt_id: number; driver_name: string | null; car_no: string | null; owner_name: string | null;
    start_day: string | null; end_day: string | null }[];
  limits: { cntMax: number | null; costMin: number | null };
  /** 이 달이 자동 반영 대상(2026-10~)인가. */
  auto: boolean;
  /** 새 노선 승인 스위치 · 이 접수·달의 승인 요청(최근 순). */
  gate: boolean; requests: CommuteRouteRequest[];
}
/** 할부금 만기 구글챗 알림 미리보기(#449) — `functions/src/finInstExpire.ts previewFinInstExpire`. */
export interface FinInstExpirePreview {
  day: string; days: number; enabled: boolean; switchedBy: string | null; switchedAt: string | null; webhookReady: boolean;
  rows: { id: number; label: string; holder: string | null; endDate: string; daysLeft: number; amount: string; sent: boolean }[];
  sample: string | null;
  ahead: { day: string; cards: number }[];
  recent: { id: string; day: string; days_left: number; status: 'sent' | 'fail'; error: string | null; created_by: string; at: string; label: string }[];
}
/** 차량검사 만료 알림톡 미리보기(#62) — `functions/src/carExpire.ts previewCarExpire`. */
export interface CarExpirePreview {
  day: string; enabled: boolean; switchedBy: string | null; switchedAt: string | null;
  template: { code: string; ready: boolean; status: string };
  sample: string;
  rows: { carId: number; carNo: string; testExpire: string; stage: 'd60' | 'd0'; daysLeft: number; ownerName: string | null;
    phone: string | null; sent: boolean; reason: string | null }[];
  ahead: { day: string; d60: number; d0: number }[];
  recent: { id: string; car_no: string; test_expire: string; stage: 'd60' | 'd0'; owner_name: string | null; status: 'sent' | 'fail';
    error: string | null; created_by: string; at: string }[];
}
export interface FineNotifyPreview {
  rows: { fineId: number; carNo: string; violationDate: string; amount: string; driverName: string | null;
    to: { phone: string; name: string; role: 'driver' | 'owner' } | null; reason: string | null; sentBefore: number; lastSent: string | null }[];
  templateCode: string; templateReady: boolean; templateStatus: string; inHours: boolean; sample: string | null;
}

/** 과태료 고지서 사진(JPG·PNG·PDF)을 올린다 — 올리면 그 고지서에 바로 붙는다. */
export async function uploadFineImage(fineId: number, file: File): Promise<{ fineId: number; name: string; type: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/uploadFineImage?${new URLSearchParams({ fineId: String(fineId), name: file.name })}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '사진을 올리지 못했습니다.');
  return res.json();
}

export interface FineScanFields {
  kind?: string; carNo?: string; dueDate?: string; issuer?: string; noticeNo?: string;
  lines?: { violationDate?: string; violationTime?: string; place?: string; content?: string; amount?: string }[];
}
export interface FineScanLine { violationDate: string | null; violationTime: string | null; place: string | null; content: string | null; amount: number | null }
export interface FineScanRow {
  scan_id: number; file_name: string; drive_created: string | null; state: 'new' | 'added' | 'attached' | 'ready' | 'check' | 'done' | 'skip' | 'error';
  fine_id: number | null; note: string | null; tries: number; moved: boolean; has_photo: boolean; created_at: string; updated_at: string | null; updated_by: string | null;
  fine_car_no: string | null; fine_violation_date: string | null; fine_amount: string | null;
  extracted: { kind?: string; carNo?: string | null; issuer?: string | null; noticeNo?: string | null; dueDate?: string | null; lines?: FineScanLine[]; unclear?: string[]; note?: string | null; fineIds?: number[] } | null;
}
export interface FineScanList {
  rows: FineScanRow[]; todo: number;
  config: { auto_on: boolean; folder_id: string; done_folder_id: string | null; last_run_at: string | null; last_result: string | null } | null;
}
export interface FineScanRun {
  seen: number; added: number; attached: number; ready: number; check: number; error: number; summary: string; more: boolean;
  /** 이번 호출 뒤 남은 장 수(새 파일 + 다시 시도할 오류) · 이번에 읽은 장 수 · 장마다 결과 */
  remaining: number; processed: number; items: { file: string; state: FineScanRow['state']; note: string | null }[];
}
/** 스캔함 사진(확인 필요 줄 — 아직 고지서가 없다) — blob URL. 쓰고 나면 revoke. */
export async function fetchFineScanImageUrl(scanId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadFineScanImage?scanId=${scanId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '사진을 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}
/** 올린 고지서 사진 — blob URL. 쓰고 나면 revoke. */
export async function fetchFineImageUrl(fineId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadFineImage?fineId=${fineId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '사진을 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}
export interface FineCandidate {
  car_alloc_id: number | null; driver_name: string | null; driver_kind: string | null; cust_name: string | null; dispt_name: string | null;
  start_at: string | null; end_at: string | null;
  /** 그 차의 회계계정 편성(배차가 아니다) — 통행료·주차 과태료를 차량 회계로 공제할 때(#102) */
  acct: boolean;
}
/** 차인지급액 조정(탑라인) — 서버 `functions/src/toplineCalc.ts` 의 결과 그대로. 금액은 원 단위 정수. */
export type ToplineBucket = '지입기본' | '사업소득' | '2대이상' | '직영' | '예비기사' | '25일';
export interface ToplineLine {
  ownerId: number; ownerName: string | null; carId: number | null; carNo: string | null;
  driverId: number | null; driverName: string | null; driverKind: string | null;
  grpId: number | null; grpName: string | null; grpKind: string | null; carAllocId: number | null;
  net: number; reserve: number; carry: number; carryUsed: number; payGrpId: number | null;
  excluded: string | null; tags: string[];
}
export interface ToplineAdjust { id: number; payeeKind: 'owner' | 'driver'; payeeId: number; amount: number; reason: string }
export interface ToplinePayee {
  key: string; kind: 'owner' | 'driver'; id: number; name: string; ownerId: number; ownerName: string | null;
  bucket: ToplineBucket; payGrpId: number | null; cars: string[];
  net: number; carry: number; adjust: number; gross: number; tax: number; pay: number; newCarry: number;
  tags: string[]; bankName: string | null; bankOwner: string | null; bankNumber: string | null; adjusts: ToplineAdjust[];
}
export interface ToplinePosting {
  ownerId: number; ownerName: string | null; carId: number | null; carNo: string | null; carAllocId: number | null;
  driverName: string | null; before: number; after: number; delta: number;
}
export interface Topline {
  driveMon: string;
  /** 확정으로 굳힌 것 · posted = 재무추가에 넣은 정산부족분 줄 수(null = 아직 안 넣음) · note = 못 넣은 줄·재확정 사유 */
  closed: { at: string; by: string | null; cutoff: string; posted: number | null; postedAt: string | null; note: string | null } | null;
  /** 확정 뒤 숫자가 바뀐 묶음(차주 `o:id` · 관리기사A `d:id`) — 「재확정」 대상. 이체액·정산부족분 반영 합 전후. */
  changes: { group: string; name: string; before: number; after: number; postingBefore: number; postingAfter: number }[];
  statement: { lines: number; last: string | null };
  /** 명세가 없어 생성 문장으로 미리 센 것 — 대상 = targetMon 의 정산현황 체크. 확정은 막힌다. */
  preview: { targetMon: string | null } | null;
  grps: { grp_id: number; grp_name: string }[];
  lines: ToplineLine[]; payees: ToplinePayee[]; postings: ToplinePosting[];
}
export interface FineDeductExport {
  deductMon: string;
  /** 켜져 있으면 버스인웹 재무추가에 바로 공제(cutover 뒤). 꺼져 있으면 버스인 업로드 엑셀. */
  directDeduct: boolean;
  rows: { fineId: number; carAllocId: number; io: 0; account: string; amount: string; comment: string; carNo: string; driverName: string | null; ownerName: string | null }[];
  held: { fineId: number; carNo: string; violationDate: string; amount: string; reason: string }[];
}

/** 계산서 → 버스인웹 미수 반영 미리보기(functions/src/misuSync.ts). */
export interface MisuSyncPreview {
  open: boolean; units: number; noMisu: number; misuPaidInvoiceNot: number; bill: number; paid: number;
  sample: { recvId: number; driveMon: string; misuId: number; bill: string | null; paid: string | null }[];
}

// ── 공지 관리(09-27) ─────────────────────────────────────────────
export interface NoticeAdminRow {
  notice_id: number;
  category: string | null;
  subject: string | null;
  is_top: boolean | null;
  expire: string | null;
  grp_id: number | null;
  grp_name: string | null;
  /** 대상 소속들(10-19 여러 개) · null = 전체 기사. */
  grp_ids: number[] | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  is_active: boolean;
  is_new: boolean;
  driver_read_cnt: number;
  last_push_at: string | null;
  last_push_success: number | null;
  last_push_tokens: number | null;
}
export interface NoticeAdminDetail {
  notice_id: number;
  subject: string | null;
  comment: string | null;
  is_top: boolean | null;
  expire: string | null;
  grp_id: number | null;
  grp_name: string | null;
  grp_ids: number[] | null;
  created_at: string | null;
  created_by: string | null;
}
export interface NoticeSaveArgs {
  noticeId?: number | null;
  subject: string;
  comment: string;
  /** 대상 소속들 — 비면 전체 기사. */
  grpIds?: number[];
  isTop?: boolean;
  expire?: string | null;
}
export interface NoticePushPreview {
  target: string;
  tokenCnt: number;
  driverCnt: number;
  allDriverCnt: number;
  lastSentAt: string | null;
  lastSuccess: number | null;
}

/** 담당자별 정산 보기 미리보기 한 줄 — 최근 3개월 접수 기준(`admin.ts previewSettleScope`). */
export interface SettleScopeRow {
  app_user_id: number;
  name: string | null;
  email: string;
  role: string;
  team: string | null;
  view_scope: string | null;
  /** 실제로 적용될 범위(회계·관리자 = all · 팀 없는 team = own). */
  eff: 'own' | 'team' | 'all';
  total: number;
  unassigned: number;
  visible: number;
  mine: number;
}
export interface SettleScopePreview {
  on: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  rows: SettleScopeRow[];
}

/** 화면 권한 표(`admin.ts listScreenPerms`). 단계 null = 예외 없음(위 단계를 따른다). */
/** 경영 월 수익 한 달(굳힌 값 · 금액은 문자열). `functions/src/profit.ts` METRICS 와 같은 칸. */
export interface ProfitMonthRow {
  mon: string; rev: string | null; exp: string | null; profit: string | null; real_sales: string | null;
  commute_sales: string | null; general_sales: string | null; commute_profit: string | null; general_profit: string | null;
  personal: string | null; fin_add: string | null; direct_cost: string | null;
  source: 'sheet' | 'web'; cut_at: string | null; note: string | null;
}
export type ProfitManualItem = 'rent' | 'mgmt' | 'land' | 'card_staff' | 'card_etc';
/** 거래처별 수익 한 줄(접수구분 × 거래처 묶음 · 금액 문자열) — `functions/src/profit.ts` CUST_SQL. */
export interface ProfitCustRow { recv_type: string; cust_name: string; manager: string; n: number; members: number; rev: string; exp: string; dalloc: string }
/** 거래처 추세 목록 한 줄(묶음) — 최근 12개월(이번 달 제외) 수입·수익 합 · 달별 수익(spark, 없는 달 null). */
export interface CustTrendGroup { cust: string; types: string[]; managers: string[]; members: number; last_mon: string; rev12: string; profit12: string; spark: (string | null)[] }
/** 거래처 추세 상세 한 줄(달 × 구분 × 묶음 안 거래처). 수익 = rev − exp − dalloc. */
export interface CustTrendRow { mon: string; recv_type: string; raw_name: string; manager: string; n: number; rev: string; exp: string; dalloc: string; computed_at: string }
export interface ProfitLine { side: 'in' | 'out'; group: string; account: string; amt: number }
/** 경영 분석 미수 한 줄(`functions/src/profitOps.ts` MISU_SQL). */
export interface ProfitMisuRow {
  drive_mon: string; recv_id: number; recv_type: string; cust_name: string; manager: string; manager_active: boolean;
  req_status: string; req_date: string | null; amt: string; comment: string; days: number; avg_days: string;
}
/** 경영 분석 노선별 한 줄(ROUTE_SQL — 일반 = 노선 · 통근 = 접수). */
export interface ProfitRouteRow {
  kind: '일반' | '통근'; recv_id: number; dispt_id: number | null; name: string | null; cust_name: string; manager: string;
  grp_name: string | null; n: number; rev: string; exp: string; dalloc: string;
}
/** 경영 분석 할부금 한 줄(INST_SQL — 차량별 공제 ↔ 다음 달 회사 납부). */
export interface ProfitInstRow {
  car_no: string; owner_name: string | null; holder_name: string | null; company: string | null; corp_label: string | null;
  cnt: number; unlinked: boolean; direct: boolean; ded: string | null; pay: string | null;
  /** 기사 공제 중 회사할부 실납부에 연결된 공제 계약 몫(참고 — 공제 합에 들어 있다). */
  loan: string | null;
}
/** 경영 분석 관리기사A 차량 한 줄(`getProfitDirect` — 시트 직영차량분석RAW 한 줄). 금액은 문자열. */
export interface ProfitDirectRow {
  car_no: string; car_id: number | null; driver_name: string | null; corp: string | null; car_year: number | null;
  sales: string; commute_sales: string; general_sales: string; extra_pay: string; base_pay: string; drv_deduct: string;
  ins_company: string; fin_pay: string; fin_deduct: string; inst: string; dep: string; profit_inst: string; profit_dep: string; custs: string;
}
/** 경영 분석 차인 마이너스 한 줄(`listProfitMinus`). */
export interface ProfitMinusRow {
  axis: 'owner' | 'direct'; id: number; name: string; driver_kind: string | null; sales_total: number; fin_total: number; grand_total: number; is_salary: boolean;
}
/** 경영 분석 일 없는 기사 한 줄(`listProfitIdle`). */
export interface ProfitIdleRow {
  owner_id: number; name: string; grand_total: number; fin_total: number; sales_total: number;
  grp_name: string | null; car_no: string | null; full_cnt: number | null; car_year: string | null;
}
/** 자동으로 가져온 손입력 칸(`functions/src/profitAuto.ts`). */
export interface ProfitAutoVal { ok: boolean; amt: number; source: string; note: string; blank?: boolean }
export interface ProfitLive {
  mon: string; cutAt: string; metrics: Record<string, number>; lines: ProfitLine[];
  manual: Record<ProfitManualItem, number>; directAlloc: number; prevDetail: boolean;
  vatOffset: number; auto: { card_staff: ProfitAutoVal; card_etc: ProfitAutoVal } | null;
}
export interface ProfitMonthResult {
  mon: string; saved: boolean; source: 'sheet' | 'web' | 'live';
  row: (ProfitMonthRow & { detail?: { lines?: ProfitLine[]; manual?: Record<ProfitManualItem, number>; directAlloc?: number } | null; created_by?: string | null }) | null;
  live?: ProfitLive;
}
export type ScreenKey = 'master' | 'dispatch' | 'settle' | 'result-daily' | 'result-mo' | 'result-req' | 'fin-inst' | 'accounting' | 'notices' | 'profit';
export type ScreenLv = 'none' | 'read' | 'write';
/** 기준정보 수정 소속(09-28, 서버 shared.ts masterGrpScope). */
export type GrpScope = 'all' | 'internal' | 'external';
export interface ScreenPermTable {
  screens: ScreenKey[];
  teams: { team: string; levels: Record<ScreenKey, ScreenLv | null>; grpScope: GrpScope | null }[];
  users: {
    app_user_id: number; email: string; name: string | null; role: Role; team: string | null;
    cells: Record<ScreenKey, { role: ScreenLv; team: ScreenLv | null; own: ScreenLv | null; eff: ScreenLv }>;
    grpScope: { team: GrpScope | null; own: GrpScope | null; eff: GrpScope };
  }[];
}
/** 화면 칸 또는 「master-grp」(기준정보 수정 소속) 칸 하나의 변경. */
export type ScreenPermChange =
  | { team?: string | null; appUserId?: number | null; screen: ScreenKey; level: ScreenLv | 'inherit' }
  | { team?: string | null; appUserId?: number | null; screen: 'master-grp'; level: GrpScope | 'inherit' };

// ─── 세금계산서 발행 요청(영업 → 회계, 서버 invoiceRequest.ts) ────────────────────
/** new=새 요청(아무도 안 봄) · seen=확인중 · numbered=발행대장 번호 매김 · fix=보완 요청 · done=발행 완료 · cancel=취소 */
export type InvReqStatus = 'new' | 'seen' | 'numbered' | 'fix' | 'done' | 'cancel';
export interface InvReqRow {
  id: number; corp_id: number; corp_name: string | null; recv_id: number | null; drive_mon: string | null; recv_kind: string | null; no_recv_reason: string | null;
  cust_name: string; biz_number: string | null; email: string | null; written_date: string; supply: string; vat: string; total: string;
  vat_included: boolean; item: string | null; note: string | null; requester_app_user_id: number; requester_email: string;
  item_note: string | null; cc_name: string | null; cc_email: string | null; remark1: string | null;
  issue_way: string | null; recv_method: string | null;
  requester_name: string | null; state: string; status: InvReqStatus; seen_at: string | null; seen_by: string | null;
  invoice_id: number | null; invoice_no: string | null; invoice_issuer: string | null; invoice_issue_date: string | null;
  done_at: string | null; done_by: string | null; created_at: string; updated_at: string;
  unread_for_sales: boolean; unread_for_acct: boolean; last_msg: string | null; age_min: number;
}
export interface InvReqMsg { id: number; side: 'sales' | 'acct' | 'system'; author_name: string | null; body: string; created_at: string }
/** 요청 첨부(사업자등록증 등) — msg_id = 올리면서 남긴 대화 줄(그 말풍선 안에 그린다). */
/** 발행현황 한 줄 = 접수 하나. 금액은 문자열(원값), 차이는 10원 이상일 때만. */
export interface InvStatusRow {
  recvId: number; custName: string | null; recvKind: string | null; owner: string | null; method: string | null;
  billTotal: string | null; invTotal: string | null; invNos: string | null; invCount: number;
  writtenDate: string | null; issueDate: string | null; paidDate: string | null;
  state: 'none' | 'requested' | 'pending' | 'issued' | 'paid'; diff: string | null; noBill: boolean;
}

export interface InvReqFile {
  id: number; msg_id: number | null; side: 'sales' | 'acct'; file_name: string; content_type: string; byte_size: string;
  created_by: string | null; created_at: string;
}
/** 요청 첨부 올리기 — 개선요청 첨부와 같은 이유로 callable 이 아니다(바이트 그대로). 형식은 서버가 앞머리 바이트로 본다. */
export async function uploadInvoiceRequestFile(requestId: number, file: File): Promise<{ fileId: number; fileName: string; byteSize: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/uploadInvoiceRequestFile?${new URLSearchParams({ id: String(requestId), name: file.name })}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '파일을 올리지 못했습니다.');
  return res.json();
}
/**
 * 편성 변경 첨부 올리기(#189) — `changeId` 없으면 등록 전 대기(등록할 때 `fileIds` 로 붙인다), 있으면 그 기록에 바로.
 * 바이트 그대로 보내는 이유는 발행 요청 첨부와 같다(callable 크기 한도).
 */
export async function uploadAllocChangeFile(file: File, changeId?: number): Promise<{ fileId: number; fileName: string; byteSize: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const qs = new URLSearchParams({ name: file.name, ...(changeId ? { id: String(changeId) } : {}) });
  const res = await fetch(`${base}/uploadAllocChangeFile?${qs}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '파일을 올리지 못했습니다.');
  return res.json();
}
/** 할부 계약 상환스케줄표(#230) — 서버 `finInstFile.FinInstFile`. */
export interface FinInstFile { id: number; file_name: string; content_type: string; byte_size: string; created_by: string | null; created_at: string }
/**
 * 할부 상환스케줄표 올리기(#230) — `finInstId` 없으면 등록 전 대기(「등록」이 `fileIds` 로 붙인다), 있으면 그 계약에 바로.
 * 바이트 그대로 보내는 이유는 편성 변경 첨부와 같다(callable 크기 한도).
 */
export async function uploadFinInstFile(file: File, finInstId?: number): Promise<{ fileId: number; fileName: string; byteSize: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const qs = new URLSearchParams({ name: file.name, ...(finInstId ? { id: String(finInstId) } : {}) });
  const res = await fetch(`${base}/uploadFinInstFile?${qs}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '파일을 올리지 못했습니다.');
  return res.json();
}
/** 할부 상환스케줄표 열람용 blob URL. 쓰고 나면 revoke. */
export async function fetchFinInstFileUrl(fileId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadFinInstFile?fileId=${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}
/** 편성 변경 첨부 열람용 blob URL. 쓰고 나면 revoke. */
export async function fetchAllocChangeFileUrl(fileId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadAllocChangeFile?fileId=${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}
/** 요청 첨부 열람용 blob URL. 쓰고 나면 revoke. */
export async function fetchInvoiceRequestFileUrl(fileId: number): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const res = await fetch(`${base}/downloadInvoiceRequestFile?fileId=${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error((await res.text()) || '첨부를 불러오지 못했습니다.');
  return URL.createObjectURL(await res.blob());
}
export interface InvReqSaveArgs {
  id?: number; corpId: number; recvId?: number | null; driveMon?: string | null; recvKind?: string; custName: string; bizNumber?: string;
  email?: string; writtenDate: string; amount: string; vatIncluded: boolean; item?: string; note?: string; force?: boolean; noRecvReason?: string;
  itemNote?: string; ccName?: string; ccEmail?: string; remark1?: string; issueWay?: string | null;
}
export type InvReqLookup =
  | { found: false; outOfScope?: boolean }
  | { found: true; recvKind: string | null; custName: string | null; bizNumber: string | null; email: string | null; corpId: number | null;
      amount: { supply: string; vat: string; total: string } | null; invoices: { invoice_no: string; issuer: string | null }[];
      requests: { id: number; status: InvReqStatus; drive_mon: string | null; mine?: boolean }[];
      /** 거래처에 등록된 담당자 중 이메일이 있는 사람 — 이메일·참조자 칸에서 고른다 */
      contacts?: CustContact[];
      /** #473 같은 접수로 앞 정산월에 발행한 계산서 이메일(가장 최근 달) */
      prevMail?: { email: string; drive_mon: string; invoice_no: string } | null };
/** 차종 설정 한 줄(#171). seats = 허용 정원(빈 배열 = 제한 없음). */
export interface CarKind { car_kind_option_id: number; name: string; seats: number[]; sort: number; is_use: boolean }
/** dept = 담당 부서(10-17). 저장값 kind 는 레거시 그대로 두고 화면 이름만 `CONTACT_KIND_LABEL` 로 보인다. */
export interface CustContact { id: number | null; kind: '일반담당자' | '정산담당자'; dept?: string; name: string; phone: string; email: string }
/** 담당 종류 화면 이름(10-17 way 「명칭을 명확하게」) — 일반담당자 = 배차·운행 연락, 정산담당자 = 청구·계산서. */
export const CONTACT_KIND_LABEL: Record<CustContact['kind'], string> = { 일반담당자: '거래처담당자', 정산담당자: '거래처정산담당자' };
export const CONTACT_KIND_HINT: Record<CustContact['kind'], string> = { 일반담당자: '배차·운행 연락', 정산담당자: '청구·세금계산서' };
/** 사업자번호·이름 중복 미리 보기 한 줄 */
export interface CustDupHit { cust_id: number; cust_name: string | null; biz_number: string | null }
export interface CustDupMember {
  cust_id: number; cust_name: string | null; biz_number: string | null;
  manager: string | null; manager_phone: string | null; cust_fin_manager: string | null;
  n_recv: number; last_recv: string | null; n_contacts: number;
}
export interface CustDupGroup { biz: string; members: CustDupMember[]; n_recv: number }
export interface OwnerDupMember {
  owner_id: number; owner_name: string | null; is_private: boolean | null; biz_number: string | null; bank_name: string | null;
  mobile_phone: string | null; email: string | null; n_live_alloc: number; n_car: number; n_sch: number; last_salary: string | null;
}
export interface OwnerDupGroup { key: string; kind: '관광사' | '개인'; members: OwnerDupMember[]; n_live_alloc: number; n_sch: number }
export interface OwnerMergeLog {
  id: string; from_owner_id: number; from_name: string | null; to_owner_id: number; to_name: string | null;
  kept: Record<string, number>; created_by: string | null; created_at: string; is_undone: boolean;
}
export interface CustMergeLog {
  id: string; from_cust_id: number; from_name: string | null; to_cust_id: number; to_name: string | null;
  reason: string | null; created_by: string | null; created_at: string; is_undone: boolean;
}
export type MisuCancelKind = '취소' | '수정';
/** 청구 취소·수정 요청 한 줄(서버 `misuCancel.LIST_COLS`). 금액은 문자열(numeric). */
export interface MisuCancelRow {
  request_id: number | string; recv_id: number; drive_mon: string; kind: MisuCancelKind; status: '대기' | '승인' | '반려' | '취소';
  status_at_request: string; bank_at_request: string | null; reason: string;
  fix_amount: string | null; fix_vat_included: boolean | null; fix_supply: string | null; fix_vat: string | null; fix_total: string | null; fix_note: string | null;
  /** 영업이 넣은 세금계산서 발행일자(10-27 · 옛 요청은 null). */
  fix_written_date?: string | null;
  old_total: string | null; requested_by: string; requested_name: string | null; requested_at: string;
  decided_by: string | null; decided_name: string | null; decided_at: string | null; decide_note: string | null;
  seen_name: string | null; seen_at: string | null; reopen_request_id: number | null; amend_ids: string[] | null;
  cust_name: string | null; corp_id: number | null; corp_name: string | null;
  cur_req_status: string | null; cur_bank_status: string | null; cur_deposit_date: string | null;
  invoices: { id: number; no: string; issuer: string | null; issue_date: string | null; total: string; amended: boolean; paid: boolean; deposit_linked: boolean }[];
  last_request: { id: number; state: string; total: string; issue_way: string | null } | null;
  issued?: boolean; depositLinked?: boolean;
}
/** 맥박에 실리는 취소·수정 요청(회계 = 대기 · 영업 = 내 결과). */
export interface MisuCancelPulse {
  id: number; kind: MisuCancelKind; recv_id: number; drive_mon: string; cust_name: string | null; reason: string;
  fix_total: string | null; old_total: string | null; fix_note: string | null; requested_name: string | null; requested_at: string;
  seen_name: string | null; seen_at: string | null; status: string; decided_name: string | null; decided_at: string | null; decide_note: string | null;
  corp_name?: string | null; reopen_request_id?: number | null;
}
export interface BillEvent {
  at: string; step: string; title: string; who: string | null; detail: string | null;
  tone: 'req' | 'acct' | 'issue' | 'cancel' | 'fix' | 'paid' | 'muted' | 'reject';
}
export interface BillTimeline {
  custName: string | null; billTotal: string | null; stage: '청구 필요' | '청구 요청' | '발행' | '입금';
  pending: { kind: MisuCancelKind; requestedAt: string } | null;
  misu: { req_status: string | null; bank_status: string | null; req_date: string | null; req_call_date: string | null; deposit_date: string | null } | null;
  events: BillEvent[];
}
export interface InvReqPulse {
  mine: { id: number; cust_name: string; status: InvReqStatus; invoice_no: string | null; total: string; last_msg: string | null }[];
  myActive: number;
  /** 내 취소·수정 요청 결과(승인·반려) 중 「확인」 전 — 옛 서버 응답엔 없다. */
  cancelMine?: MisuCancelPulse[];
  acct: null | {
    /** 회계 입력 권한(처리하는 사람). false = 조회만 — 팝업·소리·빨간 띠 없이 메뉴 배지만. 옛 서버 응답엔 없다(undefined = 예전처럼 처리자로). */
    handler?: boolean;
    fresh: { id: number; corp_name: string | null; cust_name: string; total: string; requester_name: string | null; created_at: string; age_min: number; seen_name: string | null }[];
    replied: { id: number; cust_name: string; requester_name: string | null; changed_at?: string | null }[];
    /** 대기 중인 취소·수정 요청 — 처리될 때까지 팝업(10-19). 옛 서버 응답엔 없다. */
    cancels?: MisuCancelPulse[];
    active: number; overdue: number; overdueMin: number;
  };
}
/**
 * 알림 맥박 — 모든 화면이 45초마다 부른다. `call()` 을 거치지 않는다:
 * 로딩 카드를 띄우면 안 되고(사람이 기다리는 조회가 아니다), 쓰기로 읽혀 조회 캐시를 버리게 해서도 안 된다.
 */
export function invoiceRequestPulse(): Promise<InvReqPulse> {
  return httpsCallable<Record<string, never>, InvReqPulse>(functions, 'invoiceRequestPulse')({}).then((r) => r.data);
}
